import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import {
  getInvoices, getInvoiceSummary, getInvoice, getReceivables, exportInvoices,
  exportSalesRegister, exportCreditNoteRegister,
  createInvoice, updateInvoice, recordPayment, getInvoicePayments, getAllPayments,
  deleteInvoicePayment, updateInvoiceTds,
  cancelInvoice, deleteInvoice, markInvoiceUnpaid,
  getTasks, getTask, getBillingEntities, getClients, getStaff,
  getAllCertificates, getClientGstins,
  getTdsCertificates, createTdsCertificate, updateTdsCertificate,
  deleteTdsCertificate, getTdsReconciliation,
  getCreditNotes, createCreditNote, updateCreditNote,
  cancelCreditNote, deleteCreditNote,
  uploadsUrl,
  getOpeningBalances, createOpeningBalance, updateOpeningBalance,
  deleteOpeningBalance, recordOpeningBalancePayment,
  importOpeningBalances, downloadOpeningBalanceTemplate,
  getInvoiceEmailSuggestions, getInvoiceEmailHistory, sendInvoiceEmail, sendInvoiceBatchEmail,
  sendReceiptEmail, sendClientReminder,
  getAdjustments, createAdjustment, reverseAdjustment,
  getLatestFollowUps, getFollowUps, addFollowUp, deleteFollowUp,
} from '../api';
import ProfitCentreAccess from './ProfitCentreAccess';
import Reconciliation from './Reconciliation';
import Retainers from './Retainers';
import MonthlyInvoiceWizard from '../components/MonthlyInvoiceWizard';
import RichTextEditor from '../components/RichTextEditor';
import RegisterDownload from '../components/RegisterDownload';
import { DEFAULT_INVOICE_EMAIL_BODY, buildBatchInvoiceEmailBody } from '../utils/invoiceEmailTemplate';
import { useAuth } from '../contexts/AuthContext';
import { useCanSeeBilling, useIsPartnerHROrAdmin } from '../utils/billingAccess';
import { useOrgSettings, useFirmName, getLatestOrgSettings } from '../contexts/OrgSettingsContext';
import DateRangeFilter, { inDateRange } from '../components/DateRangeFilter';
import { showToast } from '../utils/toast';

// ── Types ─────────────────────────────────────────────────────────────────────

export type InvoiceStatus = 'DRAFT' | 'SENT' | 'PAID' | 'PARTIALLY_PAID' | 'OVERDUE' | 'CANCELLED';
type TaxType = 'CGST_SGST' | 'IGST' | 'NONE';
type GSTType = 'REGISTERED' | 'B2C' | 'EXPORT' | 'SEZ';
type PaymentMode = 'CASH' | 'BANK' | 'CHEQUE' | 'UPI' | 'OTHER';

interface ClientInfo { id: number; clientCode: string; clientName: string; legalName?: string; }
interface TaskInfo { id: number; taskId: string; taskName: string; status: string; billedAmount?: number; clientId?: number; client?: ClientInfo; category?: { id?: number; name: string; hsnSacCode?: string }; partner?: { staffName: string }; manager?: { staffName: string }; }

interface ClientGstin {
  id: number; label?: string; gstin?: string;
  gstType: GSTType; address?: string; city?: string;
  state?: string; stateCode?: string; isPrimary?: boolean;
}

interface Signatory {
  id: number; name: string; designation?: string; signatureFile?: string; isDefault?: boolean;
}

interface BillingEntityFull {
  id: number; name: string;
  gstin?: string; pan?: string; address?: string; city?: string;
  state?: string; stateCode?: string; email?: string; phone?: string;
  bankName?: string; bankAccount?: string; bankIfsc?: string; bankBranch?: string;
  signatories?: Signatory[];
}

interface LineItem {
  id?: number; slNo: number; description: string; hsnSac?: string;
  quantity: number; rate: number; unit?: string; amount: number;
  taskId?: number; certificateId?: number;
  // Optional — set on lines that represent reimbursable out-of-pocket
  // expenses. When 2+ such lines exist on an invoice, the PDF renderer
  // collapses them into a single "Out-of-pocket expenses" row in the main
  // table and lists the breakdown in an annexure at the bottom.
  isOpe?: boolean; expenseId?: number;
}

interface ProfitCentreInfo { id: number; name: string; }

interface Payment {
  id: number; invoiceId: number;
  paymentDate: string; amount: number;
  tdsDeducted?: number;
  mode: PaymentMode; reference?: string; notes?: string;
  createdBy?: { id: number; staffName: string };
  createdAt: string;
}

interface TdsCertificate {
  id: number; clientId: number;
  client?: ClientInfo;
  certificateNo: string; section: string;
  fiscalYear: string; period?: string;
  amountAsPer26AS: number;
  receivedDate?: string; notes?: string;
  createdAt: string; updatedAt: string;
}

interface TdsReconRow {
  clientId: number; clientName: string;
  section: string; invoiceTotal: number;
  certTotal: number; variance: number;
}

export interface Invoice {
  id: number; invoiceNumber: string; status: InvoiceStatus;
  amount: number; taxType: TaxType;
  cgstRate?: number; sgstRate?: number; igstRate?: number;
  cgstAmount?: number; sgstAmount?: number; igstAmount?: number;
  totalAmount?: number;
  tdsSection?: string; tdsRate?: number; tdsAmount?: number;
  paymentAmount?: number; creditedAmount?: number; writtenOffAmount?: number; paymentDate?: string; paymentNotes?: string;
  payments?: Payment[];
  invoiceDate: string; dueDate?: string; notes?: string; hsnSacCode?: string;
  template: number;
  rcmApplicable?: boolean;
  task: TaskInfo; client?: ClientInfo;
  // The client the work was done for, when the invoice is raised on another party.
  serviceClient?: { id: number; clientCode: string; clientName: string } | null;
  billingEntity?: BillingEntityFull;
  clientGstin?: ClientGstin; clientGstinId?: number;
  lineItems?: LineItem[];
  profitCentre?: ProfitCentreInfo;
  signatory?: Signatory | null;
  createdAt: string;
}

interface Receivable {
  id: number; invoiceNumber: string; amount: number; totalAmount?: number;
  tdsAmount?: number; paymentAmount?: number; creditedAmount?: number; writtenOffAmount?: number;
  invoiceDate: string; dueDate?: string; client?: ClientInfo; task?: TaskInfo;
  daysOverdue: number; ageingBucket: string;
}

interface InvoiceSettings { id: number; billingEntityId?: number | null; billingEntity?: { id: number; name: string } | null; prefix: string; suffix: string; startNumber: number; }

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const fmtD = (d?: string) => (d ? format(new Date(d), 'dd-MMM-yy') : '—');
const today = () => format(new Date(), 'yyyy-MM-dd');

// Outstanding = total − TDS − cash received − credit notes issued against it
// − write-offs (short & excess / bad debts). Credit notes are a billing
// reversal (same effect as a payment) and must always be subtracted —
// several call sites here used to drop this term and overstate outstanding
// for CN-settled invoices.
export const outstandingOf = (inv: { totalAmount?: any; amount: any; tdsAmount?: any; paymentAmount?: any; creditedAmount?: any; writtenOffAmount?: any }) =>
  Math.max(0, Number(inv.totalAmount ?? inv.amount) - Number(inv.tdsAmount ?? 0) - Number(inv.paymentAmount ?? 0) - Number(inv.creditedAmount ?? 0) - Number(inv.writtenOffAmount ?? 0));

// GST-return sale-type classification (GSTR-1 nomenclature), derived from the
// recipient GSTIN's type + whether tax was actually charged. EXPORT/SEZ each
// split into "with payment" (IGST charged, claimable) vs "without payment"
// (LUT/bond, taxType NONE) since GST filing treats those very differently.
const saleTypeLabel = (gstType: GSTType | undefined, taxType: TaxType): string => {
  if (!gstType) return '';
  switch (gstType) {
    case 'REGISTERED': return 'B2B';
    case 'B2C': return 'B2C';
    case 'EXPORT': return taxType === 'NONE' ? 'EXPWOP' : 'EXPWP';
    case 'SEZ': return taxType === 'NONE' ? 'SEZWOP' : 'SEZWP';
    default: return '';
  }
};

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  DRAFT: 'Draft', SENT: 'Sent', PAID: 'Paid',
  PARTIALLY_PAID: 'Partially Paid', OVERDUE: 'Overdue', CANCELLED: 'Cancelled',
};
const STATUS_CLASS: Record<InvoiceStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-600', SENT: 'bg-blue-100 text-blue-700',
  PAID: 'bg-green-100 text-green-700', PARTIALLY_PAID: 'bg-yellow-100 text-yellow-700',
  OVERDUE: 'bg-red-100 text-red-700', CANCELLED: 'bg-gray-100 text-gray-400 line-through',
};

const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
  'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function numToWords(n: number): string {
  if (n === 0) return 'Zero';
  const chunks = (v: number): string => {
    if (v === 0) return '';
    if (v < 20) return ONES[v] + ' ';
    if (v < 100) return TENS[Math.floor(v / 10)] + (v % 10 ? ' ' + ONES[v % 10] : '') + ' ';
    return ONES[Math.floor(v / 100)] + ' Hundred ' + chunks(v % 100);
  };
  const inr = Math.floor(n);
  const paise = Math.round((n - inr) * 100);
  let words = '';
  if (inr >= 10000000) { words += chunks(Math.floor(inr / 10000000)) + 'Crore '; }
  if (inr >= 100000) { words += chunks(Math.floor((inr % 10000000) / 100000)) + 'Lakh '; }
  if (inr >= 1000) { words += chunks(Math.floor((inr % 100000) / 1000)) + 'Thousand '; }
  words += chunks(inr % 1000);
  words = words.trim();
  if (paise > 0) words += ' and ' + chunks(paise).trim() + ' Paise';
  return 'INR ' + words + ' Only';
}

function autoTaxType(be?: BillingEntityFull, cg?: ClientGstin): TaxType {
  if (!cg || !be) return 'NONE';
  // Exports — zero-rated; tax type stays NONE until user opts in to IGST
  // (the "with payment of tax" variant they'd then claim a refund on).
  if (cg.gstType === 'EXPORT') return 'NONE';
  // SEZ supplies are also zero-rated. Default to IGST (with payment of tax)
  // — that's the more common pattern for CA-firm work since most SEZ
  // recipients then claim ITC. Flip the dropdown to NONE for an LUT/bond
  // supply (without payment).
  if (cg.gstType === 'SEZ') return 'IGST';
  if (!cg.stateCode || !be.stateCode) return 'NONE';
  return cg.stateCode === be.stateCode ? 'CGST_SGST' : 'IGST';
}

/**
 * Render a GST rate for a document, without inventing one.
 *
 * These labels used to fall back to `?? 9` (or 18 for IGST). That is fine when
 * the rate is genuinely 9 — which it is on every healthy invoice, so the
 * fallback never fired — but on a row whose tax fields are NULL it printed a
 * confident "SGST / UTGST @ 9%" against an amount of 0.00. Invoice 2026-27/M117
 * went to a client that way: a real-looking rate, no tax charged, and a Grand
 * Total short by the SGST. Missing data must look missing, so it gets caught
 * before it is sent rather than after.
 */
const gstRate = (rate: unknown): string =>
  rate === null || rate === undefined || rate === '' ? '—' : `${Number(rate)}%`;

// ── Summary Card ──────────────────────────────────────────────────────────────

const SummaryCard: React.FC<{ label: string; value: string; sub?: string; color?: string }> = ({
  label, value, sub, color = 'text-gray-800',
}) => (
  <div className="card p-4">
    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </div>
);

// ── Document-type filter (Invoices tab) ───────────────────────────────────────
// Replaces the old "All | Invoices | Credit Notes" sub-tab row. Each of the
// three views renders this in its own filter row; the parent owns the state so
// switching views and the "Create CN from invoice" jump keep working.
type InvoiceDocType = 'all' | 'invoices' | 'credit-notes';

const DocTypeSelect: React.FC<{ value: InvoiceDocType; onChange: (v: InvoiceDocType) => void }> = ({ value, onChange }) => {
  // "All" mixes in credit notes and cancelled documents firm-wide, and credit
  // notes are a billing-manager function — staff only ever see invoices, so
  // the dropdown would have a single option and is hidden for them.
  const canSeeBilling = useCanSeeBilling();
  if (!canSeeBilling) return null;
  return (
    <select className="input-field w-40" value={value} onChange={e => onChange(e.target.value as InvoiceDocType)}>
      <option value="all">All Documents</option>
      <option value="invoices">Invoices</option>
      <option value="credit-notes">Credit Notes</option>
    </select>
  );
};

/** Clickable table header that toggles sort direction/column for a pivot table. */
const SortHeader: React.FC<{
  label: string; k: string; sortKey: string; sortDir: 'asc' | 'desc';
  onSort: (k: any) => void; className?: string;
}> = ({ label, k, sortKey, sortDir, onSort, className = '' }) => (
  <th className={`table-header cursor-pointer select-none whitespace-nowrap ${className}`} onClick={() => onSort(k)}>
    {label}{sortKey === k && <span className="text-gray-400">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>}
  </th>
);

export const StatusBadge: React.FC<{ status: InvoiceStatus }> = ({ status }) => (
  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_CLASS[status]}`}>
    {STATUS_LABEL[status]}
  </span>
);

// ── Image loader for PDF signatures ──────────────────────────────────────────

/** Fetches a remote image and returns it as a base64 data URL for jsPDF.
 *  Returns null if the fetch fails (network error, CORS, etc.) */
async function fetchImageDataUrl(url: string): Promise<string | null> {
  try {
    const resp = await fetch(url, { mode: 'cors' });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ── Text-based PDF Generator (single standard template) ─────────────────────

type RGB = [number, number, number];

// ICAI-aligned palette — deep navy (CA brand) header, soft alt rows, maroon
// accent for important highlights. The standard jsPDF helvetica font does not
// include the ₹ glyph (U+20B9) — it renders as a superscript "¹". We use
// "Rs." instead so amounts display correctly in every PDF reader.
const HBG: RGB = [12, 35, 64];      // deep navy (ICAI-style) — fallback default
const HTXT: RGB = [255, 255, 255];
const ALT_ROW: RGB = [240, 244, 250];
const ACCENT: RGB = [139, 26, 26];  // maroon — used for RCM / important notes; fallback default

const hexToRgb = (hex: string, fallback: RGB): RGB => {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex || '');
  if (!m) return fallback;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

export async function generateInvoicePDF(invoice: Invoice, _template?: number, returnBase64?: boolean): Promise<string | void> {
  // Branding from Settings → Documents & Branding — shadows the module
  // defaults above for the rest of this function.
  const { documents } = getLatestOrgSettings();
  const HBG: RGB = hexToRgb(documents.pdfHeaderColor, [12, 35, 64]);
  const ACCENT: RGB = hexToRgb(documents.pdfAccentColor, [139, 26, 26]);
  const invoiceFooterText = documents.invoiceFooterText || 'This is a Computer Generated Invoice';

  // Load signature image before building PDF (async)
  const sig = invoice.signatory;
  let sigImgDataUrl: string | null = null;
  if (sig?.signatureFile) {
    sigImgDataUrl = await fetchImageDataUrl(uploadsUrl(sig.signatureFile));
  }

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const be  = invoice.billingEntity;
  const cg  = invoice.clientGstin;
  const M   = 14;   // margin
  const PW  = 210;  // page width mm
  const PH  = 297;  // page height mm
  const CW  = PW - 2 * M; // content width 182mm
  const rcm = !!(invoice as any).rcmApplicable;

  const taxable = Number(invoice.amount);
  const total   = Number(invoice.totalAmount ?? invoice.amount);
  const legalName = (invoice.client as any)?.legalName || invoice.client?.clientName || '';
  const lines: LineItem[] = invoice.lineItems?.length
    ? invoice.lineItems
    : [{ slNo: 1, description: invoice.task?.taskName ?? '', hsnSac: invoice.hsnSacCode, quantity: 1, rate: taxable, amount: taxable }];

  // Use "Rs." instead of ₹ because helvetica lacks the rupee glyph in jsPDF
  const R  = (n: number) => 'Rs. ' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const Dt = (s?: string) => s ? format(new Date(s), 'dd-MMM-yy') : '';

  let y = 0;

  // ── HEADER BAR ─────────────────────────────────────────────────────────────
  doc.setFillColor(HBG[0], HBG[1], HBG[2]);
  doc.rect(0, 0, PW, 28, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(HTXT[0], HTXT[1], HTXT[2]);
  doc.text(be?.name ?? '', M, 12);

  if (be?.pan) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`PAN: ${be.pan}`, M, 20);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('TAX INVOICE', PW - M, 12, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`# ${invoice.invoiceNumber}`, PW - M, 20, { align: 'right' });

  y = 32;

  // ── SUPPLIER INFO + INVOICE META ───────────────────────────────────────────
  const split = M + CW * 0.55;

  // Both columns are collected and measured BEFORE the box is drawn. The meta
  // column is variable-length (Due Date is optional, SEZ adds a row), and a
  // hard-coded box height pushed the last row onto — or past — the bottom
  // border as soon as the optional rows appeared.
  const leftLines: Array<{ text: string; bold?: boolean }> = [];
  if (be?.address) leftLines.push({ text: be.address });
  const cityLine = [be?.city, be?.state].filter(Boolean).join(', ');
  if (cityLine) leftLines.push({ text: cityLine + (be?.stateCode ? `  |  Code: ${be.stateCode}` : '') });
  if (be?.gstin) leftLines.push({ text: `GSTIN/UIN: ${be.gstin}`, bold: true });
  if (be?.email) leftLines.push({ text: `E: ${be.email}` });
  if (be?.phone) leftLines.push({ text: `Ph: ${be.phone}` });

  const metaRows: Array<{ label: string; value: string; bold?: boolean; accent?: boolean }> = [
    { label: 'Invoice No.', value: invoice.invoiceNumber, bold: true },
    { label: 'Date', value: Dt(invoice.invoiceDate) },
  ];
  if (invoice.dueDate) metaRows.push({ label: 'Due Date', value: Dt(invoice.dueDate) });

  // Place of Supply (POS) — per GST law, for B2B services this is the
  // RECIPIENT'S location, not the supplier's. For exports it's outside India.
  //
  // The SEZ declaration is a full sentence, several times wider than the meta
  // column. As a right-aligned value it ran back across the divider and over
  // the supplier block, so it is rendered as its own full-width line beneath
  // the box instead (see below).
  let sezDecl = '';
  if (cg?.gstType === 'EXPORT') {
    metaRows.push({ label: 'Place of Supply', value: 'Outside India (Export)' });
  } else if (cg?.gstType === 'SEZ') {
    // SEZ supplies are zero-rated; mark POS as the SEZ unit's state with a
    // SEZ tag so the recipient and any auditor immediately see the intent.
    metaRows.push({
      label: 'Place of Supply',
      value: cg?.state && cg?.stateCode
        ? `${cg.state} (${cg.stateCode}) — SEZ`
        : (cg?.stateCode ? `${cg.stateCode} — SEZ` : 'SEZ unit'),
    });
    sezDecl = invoice.taxType === 'IGST'
      ? 'Declaration: Supply to SEZ unit — with payment of integrated tax (refundable)'
      : 'Declaration: Supply to SEZ unit — without payment of integrated tax (under LUT/bond)';
  } else if (cg?.state && cg?.stateCode) {
    metaRows.push({ label: 'Place of Supply', value: `${cg.state} (${cg.stateCode})` });
  } else if (cg?.stateCode) {
    metaRows.push({ label: 'Place of Supply', value: cg.stateCode });
  }
  // Always show RCM status so the recipient can see Yes/No at a glance
  metaRows.push({ label: 'Reverse Charge', value: rcm ? 'Yes' : 'No', bold: rcm, accent: rcm });

  // Measure each value against the width actually left over after its label,
  // so anything unexpectedly long wraps inside the column rather than running
  // back over the divider.
  const VAL_LH = 4.5;
  const metaMeasured = metaRows.map((r) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const labelW = doc.getTextWidth(r.label);
    doc.setFont('helvetica', r.bold ? 'bold' : 'normal');
    doc.setFontSize(9.5);
    const valMaxW = Math.max(20, (PW - M - 4) - (split + 4 + labelW + 3));
    return { ...r, lines: doc.splitTextToSize(r.value, valMaxW) as string[] };
  });

  const leftH = leftLines.length * 5 + 6;
  const metaH = metaMeasured.reduce((h, r) => h + 6.5 + (r.lines.length - 1) * VAL_LH, 0) + 4.5;
  const infoH = Math.max(40, leftH, metaH);

  doc.setFillColor(250, 252, 255);
  doc.setDrawColor(210, 218, 230);
  doc.setLineWidth(0.3);
  doc.rect(M, y, CW, infoH, 'FD');
  doc.line(split, y, split, y + infoH);

  // Left: entity address / GSTIN
  doc.setFontSize(9);
  doc.setTextColor(55, 65, 81);
  let ly = y + 6.5;
  for (const l of leftLines) {
    doc.setFont('helvetica', l.bold ? 'bold' : 'normal');
    doc.text(l.text, M + 4, ly);
    ly += 5;
  }

  // Right: invoice key-value meta
  let ry = y + 7;
  for (const r of metaMeasured) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(120, 130, 145);
    doc.text(r.label, split + 4, ry);

    doc.setFont('helvetica', r.bold ? 'bold' : 'normal');
    doc.setFontSize(9.5);
    if (r.accent) doc.setTextColor(ACCENT[0], ACCENT[1], ACCENT[2]);
    else doc.setTextColor(25, 35, 55);
    doc.text(r.lines, PW - M - 4, ry, { align: 'right' });

    ry += 6.5 + (r.lines.length - 1) * VAL_LH;
  }

  y += infoH + 4;

  // SEZ declaration — full width, wrapped, so the auditor sees the
  // "with / without payment of integrated tax" intent explicitly without the
  // sentence colliding with anything in the meta box.
  if (sezDecl) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(90, 100, 120);
    const declLines = doc.splitTextToSize(sezDecl, CW - 8) as string[];
    doc.text(declLines, M + 4, y + 3);
    y += declLines.length * 3.8 + 3;
  }

  // ── BILL TO ────────────────────────────────────────────────────────────────
  doc.setFillColor(HBG[0], HBG[1], HBG[2]);
  doc.rect(M, y, CW, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(HTXT[0], HTXT[1], HTXT[2]);
  doc.text('BILL TO', M + 4, y + 5);
  y += 7;

  // Compute address lines first so the box can grow to fit (long client
  // addresses previously caused the GSTIN line to overprint the wrapped
  // second line of the address).
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const addrParts: string[] = [];
  if (cg?.address) addrParts.push(cg.address);
  const cgCity = [cg?.city, cg?.state].filter(Boolean).join(', ');
  if (cgCity) addrParts.push(cgCity + (cg?.stateCode ? ` — Code: ${cg.stateCode}` : ''));
  const addrLines: string[] = addrParts.length
    ? (doc.splitTextToSize(addrParts.join('   |   '), CW - 10) as string[])
    : [];
  const ADDR_LH = 4.5;                          // line height for fontSize 9
  const billH = Math.max(30, 16 + addrLines.length * ADDR_LH + 6);

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(210, 218, 230);
  doc.rect(M, y, CW, billH, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(20, 30, 55);
  doc.text(legalName, M + 5, y + 9);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(60, 70, 80);
  let by = y + 16;
  if (addrLines.length) {
    doc.text(addrLines, M + 5, by);
    by += addrLines.length * ADDR_LH + 1;
  }
  if (cg?.gstin) {
    doc.setFont('helvetica', 'bold');
    doc.text(`GSTIN: ${cg.gstin}`, M + 5, by);
    doc.setFont('helvetica', 'normal');
  } else if (cg?.gstType) {
    doc.text(`GST Type: ${cg.gstType}`, M + 5, by);
  }

  y += billH + 5;

  // ── OPE collapse: when 2+ OPE line items exist, show them as ONE combined
  // "Out-of-pocket expenses (see Annexure A)" line in the main table and
  // list the breakdown in an annexure rendered after the totals block.
  const opeLines = lines.filter((li: any) => li.isOpe);
  const nonOpeLines = lines.filter((li: any) => !li.isOpe);
  const hasOpeAnnex = opeLines.length >= 2;
  const opeTotal = opeLines.reduce((s, li) => s + Number(li.amount), 0);
  const displayLines: LineItem[] = hasOpeAnnex
    ? [
        ...nonOpeLines,
        {
          slNo: nonOpeLines.length + 1,
          description: 'Out-of-pocket expenses (see Annexure A)',
          hsnSac: nonOpeLines[0]?.hsnSac ?? invoice.hsnSacCode ?? '',
          quantity: 1,
          rate: opeTotal,
          amount: opeTotal,
        },
      ]
    : lines;

  // ── LINE ITEMS TABLE (no Unit column) ─────────────────────────────────────
  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M },
    head: [['#', 'Particulars', 'HSN/SAC', 'Qty', 'Rate', 'Amount']],
    body: displayLines.map(li => [
      String(li.slNo),
      li.description,
      li.hsnSac ?? invoice.hsnSacCode ?? '',
      String(li.quantity),
      R(Number(li.rate)),
      R(Number(li.amount)),
    ]),
    styles: {
      font: 'helvetica', fontSize: 10,
      cellPadding: { top: 4, bottom: 4, left: 3.5, right: 3.5 },
      textColor: [30, 40, 55] as RGB,
      lineColor: [210, 218, 230] as RGB, lineWidth: 0.25,
      minCellHeight: 9,
    },
    headStyles: {
      fillColor: HBG, textColor: HTXT,
      fontStyle: 'bold', fontSize: 9.5, halign: 'center',
      cellPadding: { top: 4, bottom: 4, left: 3.5, right: 3.5 },
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: 9 },
      1: { cellWidth: 'auto' },
      2: { halign: 'center', cellWidth: 22 },
      3: { halign: 'center', cellWidth: 16 },
      4: { halign: 'right', cellWidth: 34 },
      5: { halign: 'right', cellWidth: 40, fontStyle: 'bold' },
    },
    alternateRowStyles: { fillColor: ALT_ROW },
    theme: 'grid',
  });

  y = (doc as any).lastAutoTable.finalY + 6;

  // ── TOTALS BLOCK + AMOUNT IN WORDS (side-by-side) ─────────────────────────
  const totW = 100;
  const totX = M + CW - totW;
  const wordsW = CW - totW - 4;
  const totRows: Array<{ label: string; amount: number; bold: boolean }> = [
    { label: 'Taxable Amount', amount: taxable, bold: false },
  ];
  if (invoice.taxType === 'CGST_SGST') {
    totRows.push({ label: `CGST @ ${gstRate(invoice.cgstRate)}`,        amount: Number(invoice.cgstAmount ?? 0), bold: false });
    totRows.push({ label: `SGST / UTGST @ ${gstRate(invoice.sgstRate)}`, amount: Number(invoice.sgstAmount ?? 0), bold: false });
  } else if (invoice.taxType === 'IGST') {
    totRows.push({ label: `IGST @ ${gstRate(invoice.igstRate)}`, amount: Number(invoice.igstAmount ?? 0), bold: false });
  }
  totRows.push({ label: 'Grand Total', amount: total, bold: true });

  const totalsStartY = y;
  let ty = y;
  totRows.forEach(({ label, amount, bold }) => {
    const rh = bold ? 9.5 : 7;
    if (bold) {
      doc.setFillColor(HBG[0], HBG[1], HBG[2]);
      doc.setTextColor(HTXT[0], HTXT[1], HTXT[2]);
    } else {
      doc.setFillColor(248, 250, 254);
      doc.setTextColor(40, 50, 65);
    }
    doc.rect(totX, ty, totW, rh, 'F');
    doc.setDrawColor(210, 218, 230);
    doc.setLineWidth(0.25);
    doc.rect(totX, ty, totW, rh, 'S');
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 11 : 9.5);
    const midY = ty + rh / 2 + (bold ? 1.8 : 1.4);
    doc.text(label, totX + 4, midY);
    doc.text(R(amount), totX + totW - 4, midY, { align: 'right' });
    ty += rh;
  });

  // Amount in words to the LEFT of totals (instead of below) — more page real estate used
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(210, 218, 230);
  doc.setLineWidth(0.25);
  const wordsH = ty - totalsStartY;
  doc.rect(M, totalsStartY, wordsW, wordsH, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(40, 50, 65);
  doc.text('Amount in Words', M + 4, totalsStartY + 6);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(10);
  doc.setTextColor(25, 35, 55);
  doc.text(numToWords(total), M + 4, totalsStartY + 13, { maxWidth: wordsW - 8 });

  if (rcm) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(ACCENT[0], ACCENT[1], ACCENT[2]);
    doc.text('* Tax payable under Reverse Charge Mechanism', M + 4, totalsStartY + wordsH - 4);
  }

  y = ty + 6;

  // ── TAX SUMMARY ────────────────────────────────────────────────────────────
  if (invoice.taxType !== 'NONE') {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(55, 65, 81);
    doc.text('Tax Summary', M, y + 4);
    y += 7;

    const isCGST = invoice.taxType === 'CGST_SGST';
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M },
      head: [isCGST
        ? ['HSN/SAC', 'Taxable Value', 'CGST %', 'CGST Amt', 'SGST %', 'SGST Amt', 'Total Tax']
        : ['HSN/SAC', 'Taxable Value', 'IGST %', 'IGST Amt', 'Total Tax']],
      body: [isCGST ? [
        lines[0]?.hsnSac ?? invoice.hsnSacCode ?? '',
        R(taxable),
        `${gstRate(invoice.cgstRate)}`,
        R(Number(invoice.cgstAmount ?? 0)),
        `${gstRate(invoice.sgstRate)}`,
        R(Number(invoice.sgstAmount ?? 0)),
        R(Number(invoice.cgstAmount ?? 0) + Number(invoice.sgstAmount ?? 0)),
      ] : [
        lines[0]?.hsnSac ?? invoice.hsnSacCode ?? '',
        R(taxable),
        `${gstRate(invoice.igstRate)}`,
        R(Number(invoice.igstAmount ?? 0)),
        R(Number(invoice.igstAmount ?? 0)),
      ]],
      styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 2.5, lineColor: [210, 218, 230] as RGB, lineWidth: 0.25, minCellHeight: 8 },
      headStyles: { fillColor: HBG, textColor: HTXT, fontStyle: 'bold', fontSize: 8.5, halign: 'center' },
      bodyStyles: { halign: 'center' },
      columnStyles: isCGST ? {
        0: { cellWidth: 22 },
        1: { cellWidth: 30, halign: 'right' },
        2: { cellWidth: 18 },
        3: { cellWidth: 28, halign: 'right' },
        4: { cellWidth: 18 },
        5: { cellWidth: 28, halign: 'right' },
        6: { cellWidth: 38, halign: 'right', fontStyle: 'bold' },
      } : {
        0: { cellWidth: 28 },
        1: { cellWidth: 42, halign: 'right' },
        2: { cellWidth: 22 },
        3: { cellWidth: 42, halign: 'right' },
        4: { cellWidth: 48, halign: 'right', fontStyle: 'bold' },
      },
      theme: 'grid',
    });

    y = (doc as any).lastAutoTable.finalY + 6;
  }

  // ── ANNEXURE A: Out-of-Pocket Expenses (only when 2+ OPE lines exist) ───
  if (hasOpeAnnex) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(55, 65, 81);
    doc.text('Annexure A — Out-of-Pocket Expenses', M, y + 4);
    y += 7;

    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M },
      head: [['#', 'Date', 'Particulars', 'Category', 'Amount']],
      body: opeLines.map((li, idx) => {
        const expDate = (li as any).expense?.date;
        const expCat = (li as any).expense?.category;
        return [
          String(idx + 1),
          expDate ? Dt(expDate) : '',
          li.description || 'Out-of-pocket expense',
          expCat && expCat !== 'OTHER' ? expCat : '',
          R(Number(li.amount)),
        ];
      }),
      foot: [['', '', 'Total', '', R(opeTotal)]],
      styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 2.5, lineColor: [210, 218, 230] as RGB, lineWidth: 0.25, minCellHeight: 8 },
      headStyles: { fillColor: HBG, textColor: HTXT, fontStyle: 'bold', fontSize: 8.5, halign: 'center' },
      footStyles: { fillColor: ALT_ROW, textColor: [30, 40, 55] as RGB, fontStyle: 'bold', halign: 'right' },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { cellWidth: 28 },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 30 },
        4: { halign: 'right', cellWidth: 40, fontStyle: 'bold' },
      },
      theme: 'grid',
    });
    y = (doc as any).lastAutoTable.finalY + 6;
  }

  // ── BANK DETAILS + SIGNATORY (anchored near bottom of page) ───────────────
  const footH  = 38;
  // Anchor to bottom of page so the invoice fills the full page nicely. If the
  // anchored position would run off the page, start a fresh page and
  // recompute the anchor there — otherwise `y = footerY` below would
  // overwrite the fresh page's y=M with the old off-page value.
  let footerY = Math.max(y, PH - M - footH - 12);
  if (footerY + footH + 10 > PH) {
    doc.addPage();
    y = M;
    footerY = Math.max(y, PH - M - footH - 12);
  }
  y = footerY;

  const footMid = M + CW * 0.6;
  doc.setFillColor(250, 252, 255);
  doc.setDrawColor(210, 218, 230);
  doc.setLineWidth(0.3);
  doc.rect(M, y, CW, footH, 'FD');
  doc.line(footMid, y, footMid, y + footH);

  let bankY = y + 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(40, 50, 65);
  if (be?.bankName) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text("Company's Bank Details", M + 4, bankY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    bankY += 5.5;
    doc.text(`Bank: ${be.bankName}`,    M + 4, bankY); bankY += 5;
    doc.text(`A/c No.: ${be.bankAccount ?? ''}`, M + 4, bankY); bankY += 5;
    doc.text(`IFSC: ${be.bankIfsc ?? ''}${be.bankBranch ? '  /  ' + be.bankBranch : ''}`, M + 4, bankY);
  }
  if (invoice.notes) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(110, 120, 135);
    // jsPDF's helvetica has no rupee glyph — it renders as a superscript "1",
    // which is why every amount above goes through R(). Notes are free text
    // typed by staff, so normalise here too rather than trusting the input.
    const noteText = invoice.notes.replace(/₹\s*/g, 'Rs. ');
    const NOTE_LH = 3.4;
    // The bank block above ends around y+27, and the box bottom is y+footH.
    // Two lines is what genuinely fits; anything longer is an internal note
    // that does not belong on a client-facing invoice, so it is clipped
    // rather than allowed to run over the border and the signatory block.
    const MAX_NOTE_LINES = 2;
    let noteLines = doc.splitTextToSize(noteText, footMid - M - 8) as string[];
    if (noteLines.length > MAX_NOTE_LINES) {
      noteLines = noteLines.slice(0, MAX_NOTE_LINES);
      noteLines[MAX_NOTE_LINES - 1] = noteLines[MAX_NOTE_LINES - 1].replace(/\s*\S*$/, '') + '…';
    }
    // Grow upward from the bottom of the box: jsPDF lays wrapped lines out
    // downward from the anchor, so a fixed bottom anchor pushed line 2 past
    // the border.
    doc.text(noteLines, M + 4, y + footH - 4.5 - (noteLines.length - 1) * NOTE_LH);
  }

  // Signatory box
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(55, 65, 81);
  doc.text(`for ${be?.name ?? ''}`, footMid + 4, y + 6);
  // Render signature image if available
  if (sigImgDataUrl) {
    try { doc.addImage(sigImgDataUrl, 'PNG', footMid + 4, y + 8, 38, 14, undefined, 'FAST'); } catch { /* skip if image fails */ }
  }
  const sigLabelY = y + footH - 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(40, 50, 65);
  doc.text('Authorised Signatory', footMid + 4, sigLabelY);
  if (sig?.name) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(80, 90, 105);
    doc.text(sig.name + (sig.designation ? ` | ${sig.designation}` : ''), footMid + 4, sigLabelY + 4.5);
  }

  y += footH + 5;

  // ── FOOTER TEXT ────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(170, 175, 185);
  doc.text(invoiceFooterText, PW / 2, Math.min(y, PH - 6), { align: 'center' });

  if (returnBase64) {
    // jsPDF's "datauristring" emits something like
    // "data:application/pdf;filename=generated.pdf;base64,XXXX". Newer jsPDF
    // builds inject the filename= segment, so the previous hard-coded prefix
    // strip stopped working and the full data URI was forwarded to Microsoft
    // Graph, which rejected it with:
    //   "Cannot convert the literal 'data:application/pdf;...' to the
    //    expected type 'Edm.Binary'"
    // Splitting on the canonical ";base64," delimiter is parameter-order-
    // agnostic and yields just the base64 body.
    const dataUri = doc.output('datauristring');
    const idx = dataUri.indexOf(';base64,');
    return idx >= 0 ? dataUri.slice(idx + ';base64,'.length) : dataUri;
  }
  doc.save(`Invoice_${invoice.invoiceNumber}.pdf`);
}

// ── Credit Note PDF Generator ─────────────────────────────────────────────────

async function generateCreditNotePDF(cn: CreditNote, template: number = 1): Promise<void> {
  // Load signature image before building PDF (async)
  const cnSig = cn.signatory;
  let cnSigImgDataUrl: string | null = null;
  if (cnSig?.signatureFile) {
    cnSigImgDataUrl = await fetchImageDataUrl(uploadsUrl(cnSig.signatureFile));
  }

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const be  = cn.billingEntity;
  const cg  = cn.clientGstin;
  const M   = 14;
  const PW  = 210;
  const CW  = PW - 2 * M;

  const palettes: Record<number, { hBg: RGB; hTxt: RGB; altRow: RGB }> = {
    1: { hBg: [30, 58, 95],  hTxt: [255, 255, 255], altRow: [240, 246, 255] },
    2: { hBg: [12, 20, 40],  hTxt: [255, 255, 255], altRow: [237, 242, 249] },
    3: { hBg: [55, 65, 81],  hTxt: [255, 255, 255], altRow: [245, 246, 247] },
  };
  const { hBg, hTxt, altRow } = palettes[template] ?? palettes[1];

  const taxable   = Number(cn.amount);
  const total     = Number(cn.totalAmount ?? cn.amount);
  const legalName = (cn.client as any)?.legalName || cn.client?.clientName || '';
  const lines     = cn.lineItems ?? [];

  const R  = (n: number) => 'Rs. ' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const Dt = (s?: string) => s ? format(new Date(s), 'dd-MMM-yy') : '';

  const CN_REASON: Record<string, string> = {
    SALES_RETURN: 'Sales Return', PRICE_ADJUSTMENT: 'Price Adjustment',
    POST_SUPPLY_DISCOUNT: 'Post-Supply Discount', DEFICIENCY_IN_SERVICE: 'Deficiency in Service',
    INVOICE_ERROR: 'Invoice Error', OTHER: 'Other',
  };

  let y = 0;

  // ── HEADER ─────────────────────────────────────────────────────────────────
  doc.setFillColor(hBg[0], hBg[1], hBg[2]);
  doc.rect(0, 0, PW, 24, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(hTxt[0], hTxt[1], hTxt[2]);
  doc.text(be?.name ?? '', M, 11);

  if (be?.gstin) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`GSTIN: ${be.gstin}`, M, 18);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('CREDIT NOTE', PW - M, 10, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(cn.creditNoteNumber, PW - M, 17, { align: 'right' });

  y = 24;

  // ── META BOX ───────────────────────────────────────────────────────────────
  const split = M + CW * 0.5;
  const metaH = 28;
  doc.setFillColor(250, 252, 255);
  doc.setDrawColor(210, 218, 230);
  doc.setLineWidth(0.25);
  doc.rect(M, y, CW, metaH, 'FD');
  doc.line(split, y, split, y + metaH);

  const kvL = (label: string, row: number) => {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(120, 130, 145);
    doc.text(label, M + 3, row);
  };
  const kvV = (val: string, row: number, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(8.5); doc.setTextColor(25, 35, 55);
    doc.text(val, split - 3, row, { align: 'right' });
  };
  const kvL2 = (label: string, row: number) => {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(120, 130, 145);
    doc.text(label, split + 3, row);
  };
  const kvV2 = (val: string, row: number, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(8.5); doc.setTextColor(25, 35, 55);
    doc.text(val, PW - M - 3, row, { align: 'right' });
  };

  let ry = y + 6;
  kvL('Credit Note No.', ry); kvV(cn.creditNoteNumber, ry, true);
  kvL2('Date', ry);           kvV2(Dt(cn.creditNoteDate), ry);    ry += 5.5;
  kvL('Orig. Invoice No.', ry); kvV(cn.originalInvoiceNumber, ry);
  kvL2('Orig. Invoice Date', ry); kvV2(Dt(cn.originalInvoiceDate), ry); ry += 5.5;
  kvL('Reason', ry); kvV(CN_REASON[cn.reason] ?? cn.reason, ry);

  y += metaH;

  // ── BILL TO ────────────────────────────────────────────────────────────────
  doc.setFillColor(hBg[0], hBg[1], hBg[2]);
  doc.rect(M, y, CW, 6, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(7);
  doc.setTextColor(hTxt[0], hTxt[1], hTxt[2]);
  doc.text('BILL TO', M + 3, y + 4.2);
  y += 6;

  // Pre-compute wrapped address lines and grow the box to fit (prevents
  // GSTIN overprinting the wrapped second line for long client addresses).
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
  const addrParts: string[] = [];
  if (cg?.address) addrParts.push(cg.address);
  const cgCity = [cg?.city, cg?.state].filter(Boolean).join(', ');
  if (cgCity) addrParts.push(cgCity + (cg?.stateCode ? ` — Code: ${cg.stateCode}` : ''));
  const addrLines: string[] = addrParts.length
    ? (doc.splitTextToSize(addrParts.join('   |   '), CW - 8) as string[])
    : [];
  const ADDR_LH = 4;                            // line height for fontSize 8
  const billH = Math.max(25, 13.5 + addrLines.length * ADDR_LH + 5);

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(210, 218, 230);
  doc.rect(M, y, CW, billH, 'FD');

  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(20, 30, 55);
  doc.text(legalName, M + 4, y + 8);

  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(60, 70, 80);
  let by = y + 13.5;
  if (addrLines.length) {
    doc.text(addrLines, M + 4, by);
    by += addrLines.length * ADDR_LH + 0.5;
  }
  if (cg?.gstin) {
    doc.setFont('helvetica', 'bold');
    doc.text(`GSTIN: ${cg.gstin}`, M + 4, by);
    doc.setFont('helvetica', 'normal');
  } else if (cg?.gstType) {
    doc.text(`GST Type: ${cg.gstType}`, M + 4, by);
  }

  y += billH + 3;

  // ── LINE ITEMS TABLE ───────────────────────────────────────────────────────
  if (lines.length > 0) {
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M },
      head: [['#', 'Particulars', 'HSN/SAC', 'Qty', 'Rate', 'Amount']],
      body: lines.map(li => [
        String(li.slNo),
        li.description,
        li.hsnSac ?? cn.hsnSacCode ?? '',
        String(li.quantity),
        R(Number(li.rate)),
        R(Number(li.amount)),
      ]),
      styles: { font: 'helvetica', fontSize: 9.5, cellPadding: 3.5, textColor: [30, 40, 55] as RGB, lineColor: [210, 218, 230] as RGB, lineWidth: 0.25, minCellHeight: 9 },
      headStyles: { fillColor: hBg, textColor: hTxt, fontStyle: 'bold', fontSize: 9, halign: 'center' },
      columnStyles: {
        0: { halign: 'center', cellWidth: 9 },
        1: { cellWidth: 'auto' },
        2: { halign: 'center', cellWidth: 22 },
        3: { halign: 'center', cellWidth: 16 },
        4: { halign: 'right', cellWidth: 34 },
        5: { halign: 'right', cellWidth: 40, fontStyle: 'bold' },
      },
      alternateRowStyles: { fillColor: altRow },
      theme: 'grid',
    });
    y = (doc as any).lastAutoTable.finalY + 2;
  }

  // ── TOTALS ─────────────────────────────────────────────────────────────────
  const totW = 100;
  const totX = M + CW - totW;
  const totRows: Array<{ label: string; amount: number; bold: boolean }> = [
    { label: 'Taxable Amount', amount: taxable, bold: false },
  ];
  if (cn.taxType === 'CGST_SGST') {
    totRows.push({ label: `CGST @ ${gstRate(cn.cgstRate)}`,         amount: Number(cn.cgstAmount ?? 0), bold: false });
    totRows.push({ label: `SGST / UTGST @ ${gstRate(cn.sgstRate)}`, amount: Number(cn.sgstAmount ?? 0), bold: false });
  } else if (cn.taxType === 'IGST') {
    totRows.push({ label: `IGST @ ${gstRate(cn.igstRate)}`, amount: Number(cn.igstAmount ?? 0), bold: false });
  }
  totRows.push({ label: 'Total Credit Amount', amount: total, bold: true });

  let ty = y;
  totRows.forEach(({ label, amount, bold }) => {
    const rh = bold ? 7.5 : 5.5;
    if (bold) {
      doc.setFillColor(hBg[0], hBg[1], hBg[2]); doc.setTextColor(hTxt[0], hTxt[1], hTxt[2]);
    } else {
      doc.setFillColor(248, 250, 254); doc.setTextColor(40, 50, 65);
    }
    doc.rect(totX, ty, totW, rh, 'F');
    doc.setDrawColor(210, 218, 230); doc.setLineWidth(0.2);
    doc.rect(totX, ty, totW, rh, 'S');
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 9 : 8.5);
    const midY = ty + rh / 2 + (bold ? 1.5 : 1.2);
    doc.text(label, totX + 3, midY);
    doc.text(R(amount), totX + totW - 3, midY, { align: 'right' });
    ty += rh;
  });

  y = ty + 3;

  // ── AMOUNT IN WORDS ────────────────────────────────────────────────────────
  doc.setFillColor(248, 250, 252); doc.setDrawColor(210, 218, 230); doc.setLineWidth(0.2);
  doc.rect(M, y, CW, 9.5, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(40, 50, 65);
  doc.text('Amount in Words:', M + 3, y + 5.8);
  doc.setFont('helvetica', 'italic'); doc.setFontSize(8);
  doc.text(numToWords(total), M + 35, y + 5.8, { maxWidth: CW - 38 });

  y += 12;

  // ── NOTES + SIGNATORY ──────────────────────────────────────────────────────
  if (cn.notes) {
    doc.setFont('helvetica', 'italic'); doc.setFontSize(7.5); doc.setTextColor(100, 110, 125);
    doc.text(`Note: ${cn.notes}`, M, y + 4, { maxWidth: CW * 0.6 });
    y += 8;
  }

  const sigH = cnSig?.name ? 30 : 24;
  const sigX = M + CW * 0.63;
  const sigW = CW - (sigX - M);
  if (y + sigH + 10 > 297) { doc.addPage(); y = M; }
  doc.setFillColor(250, 252, 255); doc.setDrawColor(210, 218, 230); doc.setLineWidth(0.25);
  doc.rect(sigX, y, sigW, sigH, 'FD');
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(55, 65, 81);
  doc.text(`for ${be?.name ?? ''}`, sigX + 3, y + 6);
  // Render signature image if available
  if (cnSigImgDataUrl) {
    try { doc.addImage(cnSigImgDataUrl, 'PNG', sigX + 3, y + 8, 34, 12, undefined, 'FAST'); } catch { /* skip */ }
  }
  const cnSigLabelY = y + sigH - (cnSig?.name ? 8 : 4);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(40, 50, 65);
  doc.text('Authorised Signatory', sigX + 3, cnSigLabelY);
  if (cnSig?.name) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(80, 90, 105);
    doc.text(cnSig.name + (cnSig.designation ? ` | ${cnSig.designation}` : ''), sigX + 3, cnSigLabelY + 4);
  }

  y += sigH + 4;

  // ── FOOTER ─────────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'italic'); doc.setFontSize(7); doc.setTextColor(170, 175, 185);
  doc.text('This is a Computer Generated Credit Note', PW / 2, y, { align: 'center' });

  doc.save(`CreditNote_${cn.creditNoteNumber}.pdf`);
}

// ── GST Print Templates ───────────────────────────────────────────────────────

// Print/PDF amounts always carry 2 decimals (per CLAUDE.md) — `fmt` above is
// the UI's 0-decimal rounded formatter and must not be used inside a printed
// document's line items or tax summary.
const fmtPrint = (n: number) =>
  '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function buildGSTHTML(inv: Invoice, template: number): string {
  const invoiceFooterText = getLatestOrgSettings().documents.invoiceFooterText || 'This is a Computer Generated Invoice';
  const be = inv.billingEntity;
  const cg = inv.clientGstin;
  const taxable = Number(inv.amount);
  const total = Number(inv.totalAmount ?? inv.amount);
  const taxLabel = inv.taxType === 'CGST_SGST' ? 'CGST + SGST' : inv.taxType === 'IGST' ? 'IGST' : 'No Tax';

  // Use line items if present, else a single line from task
  const lines: LineItem[] = inv.lineItems?.length
    ? inv.lineItems
    : [{ slNo: 1, description: inv.task?.taskName ?? '', hsnSac: inv.hsnSacCode, quantity: 1, rate: taxable, amount: taxable }];

  const lineRows = lines.map(li => `
    <tr>
      <td class="td center">${li.slNo}</td>
      <td class="td">${li.description}</td>
      <td class="td center">${li.hsnSac ?? inv.hsnSacCode ?? ''}</td>
      <td class="td center">${li.quantity}</td>
      <td class="td right">${fmtPrint(li.rate)}</td>
      <td class="td right bold">${fmtPrint(li.amount)}</td>
    </tr>`).join('');

  const taxRows = inv.taxType === 'CGST_SGST' ? `
    <tr>
      <td colspan="5" class="td right">Output CGST @ ${gstRate(inv.cgstRate)}</td>
      <td class="td right bold">${fmtPrint(Number(inv.cgstAmount ?? 0))}</td>
    </tr>
    <tr>
      <td colspan="5" class="td right">Output SGST/UTGST @ ${gstRate(inv.sgstRate)}</td>
      <td class="td right bold">${fmtPrint(Number(inv.sgstAmount ?? 0))}</td>
    </tr>` : inv.taxType === 'IGST' ? `
    <tr>
      <td colspan="5" class="td right">Output IGST @ ${gstRate(inv.igstRate)}</td>
      <td class="td right bold">${fmtPrint(Number(inv.igstAmount ?? 0))}</td>
    </tr>` : '';

  const taxSummaryRows = inv.taxType === 'CGST_SGST' ? `
    <tr>
      <td class="td">${lines[0]?.hsnSac ?? inv.hsnSacCode ?? ''}</td>
      <td class="td right">${fmtPrint(taxable)}</td>
      <td class="td center">${gstRate(inv.cgstRate)}</td>
      <td class="td right">${fmtPrint(Number(inv.cgstAmount ?? 0))}</td>
      <td class="td center">${gstRate(inv.sgstRate)}</td>
      <td class="td right">${fmtPrint(Number(inv.sgstAmount ?? 0))}</td>
      <td class="td right">${fmtPrint(Number(inv.cgstAmount ?? 0) + Number(inv.sgstAmount ?? 0))}</td>
    </tr>` : inv.taxType === 'IGST' ? `
    <tr>
      <td class="td">${lines[0]?.hsnSac ?? inv.hsnSacCode ?? ''}</td>
      <td class="td right">${fmtPrint(taxable)}</td>
      <td class="td center" colspan="2">—</td>
      <td class="td center">${gstRate(inv.igstRate)}</td>
      <td class="td right">${fmtPrint(Number(inv.igstAmount ?? 0))}</td>
      <td class="td right">${fmtPrint(Number(inv.igstAmount ?? 0))}</td>
    </tr>` : '';

  const taxSummaryTotal = inv.taxType === 'CGST_SGST' ? `
    <tr class="total-row">
      <td class="td bold">Total</td>
      <td class="td right bold">${fmtPrint(taxable)}</td>
      <td class="td"></td>
      <td class="td right bold">${fmtPrint(Number(inv.cgstAmount ?? 0))}</td>
      <td class="td"></td>
      <td class="td right bold">${fmtPrint(Number(inv.sgstAmount ?? 0))}</td>
      <td class="td right bold">${fmtPrint(Number(inv.cgstAmount ?? 0) + Number(inv.sgstAmount ?? 0))}</td>
    </tr>` : inv.taxType === 'IGST' ? `
    <tr class="total-row">
      <td class="td bold">Total</td>
      <td class="td right bold">${fmtPrint(taxable)}</td>
      <td class="td" colspan="2"></td>
      <td class="td"></td>
      <td class="td right bold">${fmtPrint(Number(inv.igstAmount ?? 0))}</td>
      <td class="td right bold">${fmtPrint(Number(inv.igstAmount ?? 0))}</td>
    </tr>` : '';

  const taxAmt = total - taxable;
  const taxAmtWords = taxAmt > 0 ? `<p class="small"><strong>Tax Amount (in words):</strong> ${numToWords(taxAmt)}</p>` : '';

  // Template color schemes
  const palette = template === 2
    ? { headerBg: '#1e3a5f', headerText: '#fff', accentBorder: '#1e3a5f', accentBg: '#eef2f7' }
    : template === 3
    ? { headerBg: '#f5f5f5', headerText: '#222', accentBorder: '#888', accentBg: '#fafafa' }
    : { headerBg: '#fff', headerText: '#000', accentBorder: '#000', accentBg: '#fff' };

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Tax Invoice ${inv.invoiceNumber}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 11px; color: #111; padding: 20px; }
  h1 { text-align: center; font-size: 16px; font-weight: bold; margin-bottom: 8px; letter-spacing: 1px; }
  table { width: 100%; border-collapse: collapse; }
  .outer { border: 1px solid #555; }
  .td { border: 1px solid #ccc; padding: 4px 6px; vertical-align: top; white-space: pre-line; }
  .th { border: 1px solid #888; padding: 4px 6px; background: #f0f0f0; font-weight: bold; text-align: center; }
  .bold { font-weight: bold; }
  .center { text-align: center; }
  .right { text-align: right; }
  .header-top { background: ${palette.headerBg}; color: ${palette.headerText}; }
  .total-row td { font-weight: bold; background: #f7f7f7; }
  .section-head { font-weight: bold; font-size: 10px; text-transform: uppercase; color: #555; }
  .small { font-size: 10px; margin-top: 4px; }
  .summary-table { margin-top: 8px; }
  .bank-section { margin-top: 10px; border-top: 1px solid #ccc; padding-top: 8px; display: flex; justify-content: space-between; }
  .sign-box { text-align: right; min-width: 180px; border-left: 1px solid #ccc; padding-left: 16px; }
  @media print { body { padding: 10px; } }
</style>
</head>
<body>
<h1>Tax Invoice</h1>

<!-- Header: Supplier + Invoice Details -->
<table class="outer">
  <tr>
    <td class="td" style="width:55%;vertical-align:top;">
      <strong style="font-size:13px;">${be?.name ?? 'N/A'}</strong><br/>
      ${be?.address ? be.address + '<br/>' : ''}
      ${be?.city ? be.city + (be.state ? ', ' + be.state : '') + '<br/>' : ''}
      ${be?.gstin ? `GSTIN/UIN: <strong>${be.gstin}</strong><br/>` : ''}
      ${be?.stateCode ? `State: ${be.state ?? ''}, Code: ${be.stateCode}<br/>` : ''}
      ${be?.email ? `E-Mail: ${be.email}<br/>` : ''}
      ${be?.phone ? `Phone: ${be.phone}` : ''}
    </td>
    <td class="td" style="width:45%;vertical-align:top;">
      <table style="width:100%;border-collapse:collapse;">
        <tr>
          <td style="width:50%;padding:2px 4px;font-weight:bold;">Invoice No.</td>
          <td style="width:50%;padding:2px 4px;font-weight:bold;">Dated</td>
        </tr>
        <tr>
          <td style="padding:2px 4px;font-size:13px;font-weight:bold;">${inv.invoiceNumber}</td>
          <td style="padding:2px 4px;">${fmtD(inv.invoiceDate)}</td>
        </tr>
        <tr><td style="padding:2px 4px;">Delivery Note</td><td style="padding:2px 4px;">Mode/Terms of Payment</td></tr>
        <tr><td style="padding:2px 4px;">&nbsp;</td><td style="padding:2px 4px;">&nbsp;</td></tr>
        <tr><td style="padding:2px 4px;">Reference No. &amp; Date.</td><td style="padding:2px 4px;">Other References</td></tr>
        <tr><td style="padding:2px 4px;">&nbsp;</td><td style="padding:2px 4px;">&nbsp;</td></tr>
      </table>
    </td>
  </tr>
  <!-- Buyer section -->
  <tr>
    <td class="td" style="vertical-align:top;">
      <div class="section-head">Buyer (Bill to)</div>
      <strong style="font-size:12px;">${(inv.client as any)?.legalName ?? inv.client?.clientName ?? 'N/A'}</strong><br/>
      ${cg?.address ? cg.address + '<br/>' : ''}
      ${cg?.city ? cg.city + (cg.state ? ', ' + cg.state : '') + '<br/>' : ''}
      ${cg?.gstin ? `GSTIN/UIN: <strong>${cg.gstin}</strong><br/>` : cg ? `Type: ${cg.gstType}<br/>` : ''}
      ${cg?.stateCode ? `State: ${cg.state ?? ''}, Code: ${cg.stateCode}` : ''}
    </td>
    <td class="td" style="vertical-align:top;">
      <table style="width:100%;border-collapse:collapse;">
        <tr><td style="padding:2px 4px;">Buyer's Order No.</td><td style="padding:2px 4px;">Dated</td></tr>
        <tr><td style="padding:2px 4px;">&nbsp;</td><td style="padding:2px 4px;">&nbsp;</td></tr>
        <tr><td style="padding:2px 4px;">Dispatch Doc No.</td><td style="padding:2px 4px;">Delivery Note Date</td></tr>
        <tr><td style="padding:2px 4px;">&nbsp;</td><td style="padding:2px 4px;">&nbsp;</td></tr>
        <tr><td style="padding:2px 4px;">Dispatched through</td><td style="padding:2px 4px;">Destination</td></tr>
        <tr><td style="padding:2px 4px;" colspan="2">Terms of Delivery</td></tr>
      </table>
    </td>
  </tr>
</table>

<!-- Line Items -->
<table style="margin-top:4px;" class="outer">
  <thead>
    <tr>
      <th class="th" style="width:4%">Sl<br/>No.</th>
      <th class="th">Particulars</th>
      <th class="th" style="width:10%">HSN/SAC</th>
      <th class="th" style="width:8%">Quantity</th>
      <th class="th" style="width:12%">Rate</th>
      <th class="th" style="width:14%">Amount</th>
    </tr>
  </thead>
  <tbody>
    ${lineRows}
    ${taxRows}
    <tr>
      <td class="td right bold" colspan="5">Total</td>
      <td class="td right bold" style="font-size:13px;">&#x20B9; ${Number(total).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
    </tr>
  </tbody>
</table>

<p class="small" style="margin-top:6px;"><strong>Amount Chargeable (in words)</strong> <span style="float:right;">E. &amp; O.E</span></p>
<p style="font-weight:bold;font-size:12px;margin-top:2px;">${numToWords(total)}</p>

${inv.taxType !== 'NONE' ? `
<!-- Tax Summary -->
<table class="summary-table outer" style="margin-top:6px;">
  <thead>
    <tr>
      <th class="th">HSN/SAC</th>
      <th class="th">Taxable Value</th>
      ${inv.taxType === 'CGST_SGST' ? `<th class="th">CGST Rate</th><th class="th">CGST Amt</th><th class="th">SGST/UTGST Rate</th><th class="th">SGST Amt</th>` : `<th class="th" colspan="2">CGST</th><th class="th">IGST Rate</th><th class="th">IGST Amt</th>`}
      <th class="th">Total Tax</th>
    </tr>
  </thead>
  <tbody>
    ${taxSummaryRows}
    ${taxSummaryTotal}
  </tbody>
</table>
${taxAmtWords}` : ''}

<!-- Bank + Signatory -->
<div class="bank-section">
  <div>
    ${be?.pan ? `<p><strong>Company's PAN:</strong> ${be.pan}</p>` : ''}
    ${be?.bankName ? `<br/><p class="section-head">Company's Bank Details</p>
    <p>Bank Name : <strong>${be.bankName}</strong></p>
    <p>A/c No. : <strong>${be.bankAccount ?? ''}</strong></p>
    <p>Branch &amp; IFS Code: ${be.bankBranch ?? ''} &amp; ${be.bankIfsc ?? ''}</p>` : ''}
    ${inv.notes ? `<p class="small" style="margin-top:8px;"><em>${inv.notes}</em></p>` : ''}
  </div>
  <div class="sign-box">
    <p>for <strong>${be?.name ?? ''}</strong></p>
    <br/><br/><br/>
    <p>Authorised Signatory</p>
  </div>
</div>
<p class="small" style="text-align:center;margin-top:12px;color:#888;">${invoiceFooterText}</p>
</body>
</html>`;
}

// ── GST Preview Modal ─────────────────────────────────────────────────────────

interface GSTPreviewProps { invoice: Invoice; onClose: () => void; }

export const GSTPreviewModal: React.FC<GSTPreviewProps> = ({ invoice, onClose }) => {
  const [pdfLoading, setPdfLoading] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const html = useMemo(() => buildGSTHTML(invoice, 1), [invoice]);

  useEffect(() => {
    if (iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        // Inject a smaller default zoom + center the page so more of the
        // invoice is visible at once without the user manually zooming out.
        // `zoom` is widely supported (Chrome / Edge / Safari); we add a
        // -moz-transform fallback for Firefox.
        const zoomedHtml = html.replace(
          '</head>',
          `<style>
            html, body { background:#ffffff; }
            body { zoom: 0.78; -moz-transform: scale(0.78); -moz-transform-origin: top center; }
            /* When Firefox uses the transform fallback we need to widen the
               box to compensate for the visual shrink. */
            @-moz-document url-prefix() {
              body { width: 128%; }
            }
          </style></head>`,
        );
        doc.open(); doc.write(zoomedHtml); doc.close();
      }
    }
  }, [html]);

  const doPrint = () => {
    const win = window.open('', '_blank', 'width=900,height=1000');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 400);
  };

  const downloadPDF = async () => {
    setPdfLoading(true);
    try {
      await generateInvoicePDF(invoice);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('PDF generation failed. Please use the Print button instead.');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex flex-col items-center justify-center z-50 p-2">
      {/* Wider + taller so the invoice fits on screen without scrolling; the
          previous max-w-4xl / max-h-[90vh] was cramped, especially with the
          default zoom we now apply inside the iframe. */}
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[1400px] flex flex-col max-h-[96vh] h-[96vh]">
        <div className="flex items-center justify-between px-6 py-3 border-b flex-shrink-0 flex-wrap gap-2">
          <h2 className="text-lg font-semibold text-gray-800">Invoice Preview — {invoice.invoiceNumber}</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={downloadPDF}
              disabled={pdfLoading}
              className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white text-sm font-medium px-4 py-1.5 rounded transition-colors"
            >
              {pdfLoading
                ? <><span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full" /> Generating…</>
                : <>📥 Download PDF</>}
            </button>
            <button onClick={doPrint} className="btn-primary text-sm px-4 py-1.5">🖨 Print</button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          <iframe ref={iframeRef} className="w-full h-full border-0" title="Invoice Preview" />
        </div>
      </div>
    </div>
  );
};

// ── Extra-attachments picker (shared by single + batch send modals) ──────

interface ExtraFile { filename: string; contentType: string; base64: string; size: number; }

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

async function fileToExtra(f: File): Promise<ExtraFile> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = String(r.result || '');
      const idx = result.indexOf(';base64,');
      const base64 = idx >= 0 ? result.slice(idx + ';base64,'.length) : result;
      resolve({ filename: f.name, contentType: f.type || 'application/octet-stream', base64, size: f.size });
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(f);
  });
}

const ExtraAttachmentsBlock: React.FC<{
  extras: ExtraFile[];
  onChange: (next: ExtraFile[]) => void;
  /** Bytes already committed by the system (invoice PDFs + cert if attached). */
  baselineBytes: number;
  limitMB: number;
  // Free-text describing what's always inlined (e.g. "Invoice PDF" or "3 invoice PDFs + 2 certificate files")
  baselineDescription: string;
}> = ({ extras, onChange, baselineBytes, limitMB, baselineDescription }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const extrasBytes = useMemo(() => extras.reduce((s, e) => s + e.size, 0), [extras]);
  const totalBytes = baselineBytes + extrasBytes;
  const limitBytes = limitMB * 1024 * 1024;
  const over = totalBytes > limitBytes;
  const near = !over && totalBytes > limitBytes * 0.75;

  const addFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const newOnes: ExtraFile[] = [];
    for (const f of Array.from(files)) {
      try { newOnes.push(await fileToExtra(f)); }
      catch { showToast(`Failed to read ${f.name}`, 'error'); }
    }
    onChange([...extras, ...newOnes]);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="rounded-md border border-gray-200 p-3 text-xs space-y-2 bg-gray-50">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <strong className="text-gray-800">📎 Attachments</strong>
          <span className="text-gray-500"> · auto: {baselineDescription}</span>
        </div>
        <div>
          <input ref={inputRef} type="file" multiple className="hidden" id="extra-attach-input"
            onChange={(e) => addFiles(e.target.files)} />
          <label htmlFor="extra-attach-input" className="btn-secondary text-xs cursor-pointer">
            + Add attachment
          </label>
        </div>
      </div>
      {extras.length > 0 && (
        <ul className="space-y-1">
          {extras.map((e, i) => (
            <li key={i} className="flex items-center gap-2 bg-white border border-gray-200 rounded px-2 py-1">
              <span className="truncate flex-1" title={e.filename}>
                {e.contentType.startsWith('image/') ? '🖼️' : e.contentType.includes('pdf') ? '📄' : '📎'} {e.filename}
                <span className="text-gray-400 ml-1">· {humanSize(e.size)}</span>
              </span>
              <button type="button"
                onClick={() => onChange(extras.filter((_, idx) => idx !== i))}
                className="text-red-600 hover:text-red-800 px-1">✕</button>
            </li>
          ))}
        </ul>
      )}
      <div className={`flex items-center justify-between pt-1 border-t ${over ? 'border-red-300' : near ? 'border-amber-300' : 'border-gray-200'}`}>
        <span className={over ? 'text-red-700 font-semibold' : near ? 'text-amber-700' : 'text-gray-600'}>
          Total: <b>{humanSize(totalBytes)}</b> of {limitMB} MB limit
        </span>
        {over && (
          <span className="text-red-700">
            ⚠ Exceeds Microsoft 365 limit — remove attachments, or larger files will fall back to download links where supported.
          </span>
        )}
        {near && !over && (
          <span className="text-amber-700">⚠ Near limit</span>
        )}
      </div>
    </div>
  );
};

// ── Send Invoice Email Modal ──────────────────────────────────────────────────

interface SendInvoiceEmailModalProps {
  invoice: Invoice;
  onClose: () => void;
  onSent?: () => void;
}

export const SendInvoiceEmailModal: React.FC<SendInvoiceEmailModalProps> = ({ invoice, onClose, onSent }) => {
  const { settings } = useOrgSettings();
  const firmName = useFirmName();
  const billing = (settings as any).billing || {};

  // Editable fields — initialize from server-side template via lazy render
  // (server does the placeholder substitution on send, but we show a preview
  // here so the user can edit before clicking Send).
  const renderTemplate = (tpl: string) => {
    const fmtINR = (n: any) => Number(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const fmtD = (d: any) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
    const vars: Record<string, string> = {
      invoiceNumber:    invoice.invoiceNumber,
      clientName:       invoice.client?.clientName || '',
      // Falls back to clientName so the template never renders blank.
      clientLegalName:  (invoice.client as any)?.legalName || invoice.client?.clientName || '',
      amount:           fmtINR((invoice as any).totalAmount ?? invoice.amount),
      invoiceDate:      fmtD(invoice.invoiceDate),
      dueDate:          fmtD((invoice as any).dueDate),
      billingEntity:    invoice.billingEntity?.name || '',
      firmName:         invoice.billingEntity?.name || firmName,
      senderName:       billing.senderName || '',
    };
    return (tpl || '').replace(/\{(\w+)\}/g, (m: string, k: string) =>
      Object.prototype.hasOwnProperty.call(vars, k) ? vars[k] : m);
  };

  const [subject, setSubject] = useState(() => renderTemplate(billing.subjectTemplate || `Invoice ${invoice.invoiceNumber}`));
  const [body,    setBody]    = useState(() => renderTemplate(billing.bodyTemplate || '<p>Please find attached.</p>'));

  // Multi-email chip input state. Stored as string[]; UI shows a chip per
  // entry with a text input at the end for the next one.
  const [toList, setToList] = useState<string[]>([]);
  const [toDraft, setToDraft] = useState('');
  const [ccList, setCcList] = useState<string[]>(() =>
    (billing.defaultCc || '').split(',').map((s: string) => s.trim()).filter(Boolean),
  );
  const [ccDraft, setCcDraft] = useState('');

  const [suggestions, setSuggestions] = useState<{ to: string[]; cc: string[] }>({ to: [], cc: [] });
  const [history, setHistory] = useState<any[]>([]);
  const [sending, setSending] = useState(false);
  // Extra user-picked attachments + size meter inputs.
  const [extras, setExtras] = useState<ExtraFile[]>([]);
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const sizeLimitMB = Number((settings as any).billing?.attachmentSizeLimitMB || 4);
  const certMode = ((settings as any).billing?.certificateAttachmentMode || 'AUTO') as 'AUTO' | 'ALWAYS_LINK' | 'NEVER';
  const certInfo = (invoice as any).certificate as { fileSize?: number; originalName?: string } | undefined;
  // Approximate baseline bytes for the size meter: invoice PDF + cert file
  // (if it would attach inline). When the cert mode is link / cert missing,
  // the file isn't sent inline so it doesn't count toward the limit.
  const pdfBytes = pdfBase64 ? Math.floor((pdfBase64.length * 3) / 4) : 0;
  const certWouldAttach = certMode !== 'NEVER' && !!certInfo?.fileSize;
  const baselineBytes = pdfBytes + (certWouldAttach ? Number(certInfo?.fileSize || 0) : 0);
  const baselineDesc = `Invoice PDF${certWouldAttach ? ` + certificate (${certInfo?.originalName ?? 'attached'})` : ''}${certMode === 'ALWAYS_LINK' && certInfo?.originalName ? ' (sent as link)' : ''}`;

  useEffect(() => {
    // Pre-generate the invoice PDF so the size meter is accurate the moment
    // the modal opens (not only at click-Send time). Cheap one-shot.
    (async () => {
      try {
        const b64 = await generateInvoicePDF(invoice, (invoice as any).template ?? 1, true) as string;
        setPdfBase64(b64);
      } catch { /* size meter just stays at extras-only */ }
    })();
  }, [invoice.id]);

  useEffect(() => {
    // Pull past TO/CC for this client + send history for this specific invoice
    Promise.allSettled([
      getInvoiceEmailSuggestions(invoice.id),
      getInvoiceEmailHistory(invoice.id),
    ]).then(([s, h]) => {
      if (s.status === 'fulfilled') setSuggestions(s.value.data || { to: [], cc: [] });
      if (h.status === 'fulfilled') setHistory(h.value.data || []);
    });
  }, [invoice.id]);

  const addEmail = (target: 'to' | 'cc', value: string) => {
    const v = value.trim().replace(/[,;]+$/, '');
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { showToast(`Invalid email: ${v}`, 'error'); return; }
    if (target === 'to') {
      if (!toList.includes(v)) setToList([...toList, v]);
      setToDraft('');
    } else {
      if (!ccList.includes(v)) setCcList([...ccList, v]);
      setCcDraft('');
    }
  };

  const removeEmail = (target: 'to' | 'cc', value: string) => {
    if (target === 'to') setToList(toList.filter((e) => e !== value));
    else                 setCcList(ccList.filter((e) => e !== value));
  };

  const handleSend = async () => {
    // Sweep any text still in the draft inputs as a final email
    if (toDraft.trim()) addEmail('to', toDraft);
    if (ccDraft.trim()) addEmail('cc', ccDraft);
    const finalTo = [...toList];
    if (toDraft.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toDraft.trim()) && !finalTo.includes(toDraft.trim())) {
      finalTo.push(toDraft.trim());
    }
    const finalCc = [...ccList];
    if (ccDraft.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ccDraft.trim()) && !finalCc.includes(ccDraft.trim())) {
      finalCc.push(ccDraft.trim());
    }
    if (finalTo.length === 0) { showToast('Add at least one recipient', 'error'); return; }

    setSending(true);
    try {
      // Reuse the PDF we pre-generated on mount when possible (the size meter
      // already produced it). Regenerate as a fallback if something went
      // wrong with the pre-gen.
      const base64 = pdfBase64 ?? (await generateInvoicePDF(invoice, (invoice as any).template ?? 1, true) as string);
      const res = await sendInvoiceEmail(invoice.id, {
        to: finalTo,
        cc: finalCc,
        subject,
        body,
        pdfBase64: base64,
        pdfFilename: `Invoice-${invoice.invoiceNumber}.pdf`,
        // Extras forwarded so the server attaches them alongside the
        // auto-built invoice PDF + cert (per OrgSettings).
        extraAttachments: extras.map((e) => ({
          filename: e.filename, contentType: e.contentType, base64: e.base64,
        })),
      });
      if ((res.data as any)?.ok) {
        const transitioned = (res.data as any)?.statusTransitioned;
        showToast(
          transitioned ? '✓ Invoice sent — status updated to SENT' : '✓ Invoice sent',
          'success',
        );
        onSent?.();
        onClose();
      } else {
        showToast(`Send failed: ${(res.data as any)?.error || 'unknown'}`, 'error');
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'Send failed', 'error');
    } finally {
      setSending(false);
    }
  };

  // Suggestion chips: previously-used emails not yet in the current selection
  const suggToNot = suggestions.to.filter((e) => !toList.includes(e));
  const suggCcNot = suggestions.cc.filter((e) => !ccList.includes(e));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-indigo-50 to-blue-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900">📧 Send Invoice — {invoice.invoiceNumber}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              To: <strong>{invoice.client?.clientName}</strong> · Amount: <strong>₹{Number((invoice as any).totalAmount ?? invoice.amount).toLocaleString('en-IN')}</strong>
              {billing.senderEmail && <> · From: <strong>{billing.senderEmail}</strong></>}
            </p>
          </div>
          <button className="text-gray-400 hover:text-gray-600 text-2xl leading-none" onClick={onClose}>✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* To */}
          <div>
            <label className="label">To *</label>
            <div className="border border-gray-300 rounded-md px-2 py-1 flex flex-wrap gap-1 min-h-[38px] focus-within:ring-1 focus-within:ring-blue-500">
              {toList.map((e) => (
                <span key={e} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                  {e}
                  <button type="button" onClick={() => removeEmail('to', e)} className="hover:text-blue-900">✕</button>
                </span>
              ))}
              <input
                className="flex-1 min-w-[150px] outline-none text-sm py-0.5"
                placeholder={toList.length === 0 ? 'client@example.com — press Enter or comma to add' : ''}
                value={toDraft}
                onChange={(e) => setToDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
                    e.preventDefault();
                    addEmail('to', toDraft);
                  } else if (e.key === 'Backspace' && !toDraft && toList.length > 0) {
                    removeEmail('to', toList[toList.length - 1]);
                  }
                }}
                onBlur={() => toDraft.trim() && addEmail('to', toDraft)}
              />
            </div>
            {suggToNot.length > 0 && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1 text-xs">
                <span className="text-gray-500">Used before for this client:</span>
                {suggToNot.map((e) => (
                  <button key={e} type="button" onClick={() => addEmail('to', e)}
                    className="bg-gray-100 hover:bg-blue-100 hover:text-blue-700 text-gray-600 px-2 py-0.5 rounded-full transition-colors">
                    + {e}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* CC */}
          <div>
            <label className="label">CC</label>
            <div className="border border-gray-300 rounded-md px-2 py-1 flex flex-wrap gap-1 min-h-[38px] focus-within:ring-1 focus-within:ring-blue-500">
              {ccList.map((e) => (
                <span key={e} className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded-full">
                  {e}
                  <button type="button" onClick={() => removeEmail('cc', e)} className="hover:text-amber-900">✕</button>
                </span>
              ))}
              <input
                className="flex-1 min-w-[150px] outline-none text-sm py-0.5"
                placeholder="CC additional recipients (Enter or comma to add)"
                value={ccDraft}
                onChange={(e) => setCcDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
                    e.preventDefault();
                    addEmail('cc', ccDraft);
                  } else if (e.key === 'Backspace' && !ccDraft && ccList.length > 0) {
                    removeEmail('cc', ccList[ccList.length - 1]);
                  }
                }}
                onBlur={() => ccDraft.trim() && addEmail('cc', ccDraft)}
              />
            </div>
            {suggCcNot.length > 0 && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1 text-xs">
                <span className="text-gray-500">Used before:</span>
                {suggCcNot.map((e) => (
                  <button key={e} type="button" onClick={() => addEmail('cc', e)}
                    className="bg-gray-100 hover:bg-amber-100 hover:text-amber-700 text-gray-600 px-2 py-0.5 rounded-full transition-colors">
                    + {e}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Subject */}
          <div>
            <label className="label">Subject</label>
            <input className="input-field w-full" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>

          {/* Body */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="label mb-0">Body</label>
              <button
                type="button"
                onClick={() => setBody(renderTemplate(DEFAULT_INVOICE_EMAIL_BODY))}
                className="text-[11px] text-blue-600 hover:text-blue-800 hover:underline"
                title="Replace the body with the latest built-in professional template"
              >
                ✨ Use built-in professional template
              </button>
            </div>
            <RichTextEditor value={body} onChange={setBody} minHeight={260} />
            <p className="text-[11px] text-gray-400 mt-1">
              Edit freely with the toolbar above. The default uses your saved template from <em>Org Settings → Billing Emails</em>.
            </p>
          </div>

          {/* Attachments — invoice PDF (and cert when applicable) auto-attach;
              user can add extras and sees a live size meter vs the Graph limit. */}
          <ExtraAttachmentsBlock
            extras={extras}
            onChange={setExtras}
            baselineBytes={baselineBytes}
            limitMB={sizeLimitMB}
            baselineDescription={baselineDesc}
          />

          {/* Previous sends */}
          {history.length > 0 && (
            <details className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs">
              <summary className="cursor-pointer text-blue-800 font-semibold">
                Previously sent ({history.length})
              </summary>
              <div className="mt-2 space-y-1">
                {history.map((h: any) => (
                  <div key={h.id} className="flex items-center gap-2 text-[11px]">
                    <span className={h.status === 'SENT' ? 'text-green-600' : 'text-red-600'}>
                      {h.status === 'SENT' ? '✓' : '✗'}
                    </span>
                    <span className="text-gray-500">{new Date(h.sentAt).toLocaleString('en-IN')}</span>
                    <span className="text-gray-700">→ {(h.toAddresses || []).join(', ')}</span>
                    {h.errorMessage && <span className="text-red-600 italic truncate">— {h.errorMessage}</span>}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        <div className="px-6 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
          <button className="btn-secondary text-sm" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn-primary text-sm" onClick={handleSend} disabled={sending || toList.length === 0}>
            {sending ? 'Sending…' : '📧 Send Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Send Invoice BATCH Email Modal ───────────────────────────────────────
// Triggered from the bulk-action bar when ≥ 2 invoices are selected. Sends
// ONE email containing one PDF attachment per invoice, with a summary
// table in the body. The bulk-action bar already enforces the same-client
// guard, so we can confidently address the recipient as that one client.

interface SendInvoiceBatchModalProps {
  invoices: Invoice[];
  onClose: () => void;
  onSent?: () => void;
}

const SendInvoiceBatchModal: React.FC<SendInvoiceBatchModalProps> = ({ invoices, onClose, onSent }) => {
  const { settings } = useOrgSettings();
  const orgFirmName = useFirmName();
  const billing = (settings as any).billing || {};
  const client = invoices[0]?.client;
  const sortedInvoices = useMemo(
    () => [...invoices].sort((a, b) => a.invoiceNumber.localeCompare(b.invoiceNumber, undefined, { numeric: true })),
    [invoices],
  );
  const firmName  = invoices[0]?.billingEntity?.name || orgFirmName;
  const senderName = billing.senderName || '';
  const totalDue = useMemo(
    () => invoices.reduce((s, i) => s + Number((i as any).totalAmount ?? i.amount), 0),
    [invoices],
  );

  // Auto subject (caller can override).
  const initialSubject = (() => {
    if (sortedInvoices.length === 1) return `Invoice ${sortedInvoices[0].invoiceNumber} from ${firmName}`;
    if (sortedInvoices.length === 2) return `Invoices ${sortedInvoices[0].invoiceNumber} & ${sortedInvoices[1].invoiceNumber} from ${firmName}`;
    return `${sortedInvoices.length} Invoices (${sortedInvoices[0].invoiceNumber} – ${sortedInvoices[sortedInvoices.length - 1].invoiceNumber}) from ${firmName}`;
  })();

  // Pre-render the table body. The `Reset` button rebuilds it from current
  // invoice data (useful if the user mucks up the editor by accident).
  const buildBody = () => buildBatchInvoiceEmailBody({
    clientName: client?.clientName || '',
    firmName,
    senderName,
    invoices: sortedInvoices.map((i) => ({
      invoiceNumber: i.invoiceNumber,
      invoiceDate: i.invoiceDate ? new Date(i.invoiceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—',
      dueDate: (i as any).dueDate ? new Date((i as any).dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—',
      totalAmount: Number((i as any).totalAmount ?? i.amount),
    })),
  });

  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(() => buildBody());
  // Extras + size meter inputs for batch send.
  const [extras, setExtras] = useState<ExtraFile[]>([]);
  const [pdfBytes, setPdfBytes] = useState(0);
  const sizeLimitMB = Number(billing.attachmentSizeLimitMB || 4);
  const certMode = (billing.certificateAttachmentMode || 'AUTO') as 'AUTO' | 'ALWAYS_LINK' | 'NEVER';
  const certBytes = sortedInvoices.reduce((s, i) => s + Number((i as any).certificate?.fileSize || 0), 0);
  const certCount = sortedInvoices.filter((i) => !!(i as any).certificate?.fileName).length;
  const baselineBytes = pdfBytes + (certMode !== 'NEVER' ? certBytes : 0);
  const baselineDesc = `${sortedInvoices.length} invoice PDF${sortedInvoices.length === 1 ? '' : 's'}${certCount > 0 ? ` + ${certCount} certificate file${certCount === 1 ? '' : 's'}${certMode === 'ALWAYS_LINK' ? ' (sent as links)' : ''}` : ''}`;

  // Pre-generate all invoice PDFs on mount so the size meter is accurate
  // before the user clicks Send.
  const [pdfMap, setPdfMap] = useState<Map<number, string>>(new Map());
  useEffect(() => {
    (async () => {
      const entries = await Promise.all(
        sortedInvoices.map(async (inv) => {
          try {
            const b64 = await generateInvoicePDF(inv, (inv as any).template ?? 1, true) as string;
            return [inv.id, b64] as const;
          } catch { return null; }
        }),
      );
      const map = new Map<number, string>();
      let bytes = 0;
      for (const e of entries) {
        if (e) { map.set(e[0], e[1]); bytes += Math.floor((e[1].length * 3) / 4); }
      }
      setPdfMap(map);
      setPdfBytes(bytes);
    })();
    // eslint-disable-next-line
  }, []);

  const [toList, setToList] = useState<string[]>([]);
  const [toDraft, setToDraft] = useState('');
  const [ccList, setCcList] = useState<string[]>(() =>
    (billing.defaultCc || '').split(',').map((s: string) => s.trim()).filter(Boolean),
  );
  const [ccDraft, setCcDraft] = useState('');

  const [suggestions, setSuggestions] = useState<{ to: string[]; cc: string[] }>({ to: [], cc: [] });
  const [sending, setSending] = useState(false);

  // Pull recipient suggestions from the FIRST invoice — same client across
  // the batch (UI-enforced), so the pool is identical regardless of which
  // invoice we ask about.
  useEffect(() => {
    if (!invoices[0]) return;
    getInvoiceEmailSuggestions(invoices[0].id)
      .then((r) => setSuggestions(r.data || { to: [], cc: [] }))
      .catch(() => {});
  }, [invoices[0]?.id]); // eslint-disable-line

  const addEmail = (target: 'to' | 'cc', value: string) => {
    const v = value.trim().replace(/[,;]+$/, '');
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { showToast(`Invalid email: ${v}`, 'error'); return; }
    if (target === 'to') {
      if (!toList.includes(v)) setToList([...toList, v]);
      setToDraft('');
    } else {
      if (!ccList.includes(v)) setCcList([...ccList, v]);
      setCcDraft('');
    }
  };

  const removeEmail = (target: 'to' | 'cc', value: string) => {
    if (target === 'to') setToList(toList.filter((e) => e !== value));
    else                 setCcList(ccList.filter((e) => e !== value));
  };

  const handleSend = async () => {
    if (toDraft.trim()) addEmail('to', toDraft);
    if (ccDraft.trim()) addEmail('cc', ccDraft);
    const finalTo = [...toList];
    if (toDraft.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toDraft.trim()) && !finalTo.includes(toDraft.trim())) {
      finalTo.push(toDraft.trim());
    }
    const finalCc = [...ccList];
    if (ccDraft.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ccDraft.trim()) && !finalCc.includes(ccDraft.trim())) {
      finalCc.push(ccDraft.trim());
    }
    if (finalTo.length === 0) { showToast('Add at least one recipient', 'error'); return; }

    setSending(true);
    try {
      // Reuse the PDFs pre-generated on mount (for the size meter).
      // Regenerate any that didn't make it (network blip, etc.).
      const pdfs = await Promise.all(
        sortedInvoices.map(async (inv) => ({
          invoiceId: inv.id,
          filename: `Invoice-${inv.invoiceNumber.replace(/[\/\\]/g, '-')}.pdf`,
          base64: pdfMap.get(inv.id) ?? (await generateInvoicePDF(inv, (inv as any).template ?? 1, true) as string),
        })),
      );

      const res = await sendInvoiceBatchEmail({
        invoiceIds: sortedInvoices.map((i) => i.id),
        to: finalTo,
        cc: finalCc,
        subject,
        body,
        pdfs,
        extraAttachments: extras.map((e) => ({
          filename: e.filename, contentType: e.contentType, base64: e.base64,
        })),
      });
      if ((res.data as any)?.ok) {
        const transitioned = (res.data as any)?.draftsTransitioned ?? 0;
        showToast(
          transitioned > 0
            ? `✓ ${sortedInvoices.length} invoices sent — ${transitioned} flipped DRAFT → SENT`
            : `✓ ${sortedInvoices.length} invoices sent`,
          'success',
        );
        onSent?.();
        onClose();
      } else {
        showToast(`Send failed: ${(res.data as any)?.error || 'unknown'}`, 'error');
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Send failed', 'error');
    } finally {
      setSending(false);
    }
  };

  const suggToNot = suggestions.to.filter((e) => !toList.includes(e));
  const suggCcNot = suggestions.cc.filter((e) => !ccList.includes(e));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-indigo-50 to-blue-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              📧 Send {sortedInvoices.length} Invoice{sortedInvoices.length === 1 ? '' : 's'} — {client?.clientName ?? 'Client'}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Total: <strong>₹{totalDue.toLocaleString('en-IN')}</strong>
              {billing.senderEmail && <> · From: <strong>{billing.senderEmail}</strong></>}
            </p>
          </div>
          <button className="text-gray-400 hover:text-gray-600 text-2xl leading-none" onClick={onClose}>✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Invoice list summary */}
          <div className="rounded-md bg-gray-50 border border-gray-200 p-3 text-xs">
            <div className="font-semibold text-gray-700 mb-2">Invoices in this email</div>
            <div className="space-y-1">
              {sortedInvoices.map((inv) => (
                <div key={inv.id} className="flex items-center gap-2">
                  <span className="font-mono text-blue-700">{inv.invoiceNumber}</span>
                  <span className="text-gray-500">·</span>
                  <span className="text-gray-600">{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}</span>
                  <span className="ml-auto text-gray-900 font-medium">₹{Number((inv as any).totalAmount ?? inv.amount).toLocaleString('en-IN')}</span>
                  {inv.status === 'DRAFT' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">DRAFT</span>}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 pt-2 border-t border-gray-200 text-gray-700">
              <span className="font-semibold">Total ({sortedInvoices.length} attachment{sortedInvoices.length === 1 ? '' : 's'})</span>
              <span className="font-bold">₹{totalDue.toLocaleString('en-IN')}</span>
            </div>
          </div>

          {/* To */}
          <div>
            <label className="label">To *</label>
            <div className="border border-gray-300 rounded-md px-2 py-1 flex flex-wrap gap-1 min-h-[38px] focus-within:ring-1 focus-within:ring-blue-500">
              {toList.map((e) => (
                <span key={e} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                  {e}
                  <button type="button" onClick={() => removeEmail('to', e)} className="hover:text-blue-900">✕</button>
                </span>
              ))}
              <input
                className="flex-1 min-w-[150px] outline-none text-sm py-0.5"
                placeholder={toList.length === 0 ? 'client@example.com — press Enter or comma to add' : ''}
                value={toDraft}
                onChange={(e) => setToDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
                    e.preventDefault(); addEmail('to', toDraft);
                  } else if (e.key === 'Backspace' && !toDraft && toList.length > 0) {
                    removeEmail('to', toList[toList.length - 1]);
                  }
                }}
                onBlur={() => toDraft.trim() && addEmail('to', toDraft)}
              />
            </div>
            {suggToNot.length > 0 && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1 text-xs">
                <span className="text-gray-500">Used before for this client:</span>
                {suggToNot.map((e) => (
                  <button key={e} type="button" onClick={() => addEmail('to', e)}
                    className="bg-gray-100 hover:bg-blue-100 hover:text-blue-700 text-gray-600 px-2 py-0.5 rounded-full">
                    + {e}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* CC */}
          <div>
            <label className="label">CC</label>
            <div className="border border-gray-300 rounded-md px-2 py-1 flex flex-wrap gap-1 min-h-[38px] focus-within:ring-1 focus-within:ring-blue-500">
              {ccList.map((e) => (
                <span key={e} className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded-full">
                  {e}
                  <button type="button" onClick={() => removeEmail('cc', e)} className="hover:text-amber-900">✕</button>
                </span>
              ))}
              <input
                className="flex-1 min-w-[150px] outline-none text-sm py-0.5"
                placeholder="CC additional recipients (Enter or comma to add)"
                value={ccDraft}
                onChange={(e) => setCcDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',' || e.key === ';') { e.preventDefault(); addEmail('cc', ccDraft); }
                  else if (e.key === 'Backspace' && !ccDraft && ccList.length > 0) removeEmail('cc', ccList[ccList.length - 1]);
                }}
                onBlur={() => ccDraft.trim() && addEmail('cc', ccDraft)}
              />
            </div>
            {suggCcNot.length > 0 && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1 text-xs">
                <span className="text-gray-500">Used before:</span>
                {suggCcNot.map((e) => (
                  <button key={e} type="button" onClick={() => addEmail('cc', e)}
                    className="bg-gray-100 hover:bg-amber-100 hover:text-amber-700 text-gray-600 px-2 py-0.5 rounded-full">
                    + {e}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Subject */}
          <div>
            <label className="label">Subject</label>
            <input className="input-field w-full" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>

          {/* Body */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="label mb-0">Body</label>
              <button
                type="button"
                onClick={() => setBody(buildBody())}
                className="text-[11px] text-blue-600 hover:text-blue-800 hover:underline"
                title="Rebuild the body from the current invoice list (resets any edits)"
              >
                ↻ Reset to built-in batch template
              </button>
            </div>
            <RichTextEditor value={body} onChange={setBody} minHeight={300} />
            <p className="text-[11px] text-gray-400 mt-1">
              Pre-built from the branded batch template. Edit freely with the toolbar above. Each invoice's PDF is attached automatically.
            </p>
          </div>

          {/* Attachments — invoice PDFs (and certs when applicable) auto-
              attach; user can add extras with a live size meter that warns
              if the Graph limit would be exceeded. */}
          <ExtraAttachmentsBlock
            extras={extras}
            onChange={setExtras}
            baselineBytes={baselineBytes}
            limitMB={sizeLimitMB}
            baselineDescription={baselineDesc}
          />
        </div>

        <div className="px-6 py-3 border-t bg-gray-50 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={sending}>Cancel</button>
          <button onClick={handleSend} className="btn-primary text-sm min-w-[140px]" disabled={sending}>
            {sending ? `Sending…` : `📧 Send Email`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Invoice Create Wizard ─────────────────────────────────────────────────────

// Two modes only. A single task/cert is just a multi-block invoice with one block.
type WizardMode = 'TASK' | 'CERT';

const sevenDaysAhead = (base: string) => {
  const d = new Date(base); d.setDate(d.getDate() + 7);
  return format(d, 'yyyy-MM-dd');
};

interface WizardLine {
  slNo: number; description: string; hsnSac: string;
  quantity: number; rate: number; unit: string; amount: number;
  taskId?: number;
  certId?: number;      // wizard-local: which certificate this line belongs to (MULTI_CERT)
  groupKey: number;     // wizard-local: groups lines into the same task/cert block (MULTI_* modes)
  isOpe: boolean; expenseId?: number;
}

interface TaskExpenseItem { id: number; description: string; amount: number; date: string; category?: string; isBilled?: boolean; }

interface TaskDetailFull {
  id: number; taskId: string; taskName: string;
  clientId?: number; client?: ClientInfo;
  category?: { id: number; name: string; hsnSacCode?: string };
  expenses?: TaskExpenseItem[];
  billedAmount?: number;
  profitCentreId?: number;
}

interface CertSummary {
  id: number; certificateNumber: string; name: string; taskId: number;
  task?: { id: number; taskName: string; clientId?: number; client?: ClientInfo; category?: { id: number; name: string; hsnSacCode?: string }; };
}

// An OPE already billed on a (live) invoice has no business being offered as
// a suggestion again — that's what isBilled (computed server-side from the
// expense's invoiceLines) is for.
const unbilledOnly = (exps: TaskExpenseItem[] | undefined): TaskExpenseItem[] =>
  (exps ?? []).filter(e => !e.isBilled);

const mkLine = (slNo: number, overrides: Partial<WizardLine> = {}): WizardLine => ({
  slNo, description: '', hsnSac: '', quantity: 1, rate: 0, unit: '', amount: 0, isOpe: false, groupKey: 1, ...overrides,
});

interface WizardProps {
  tasks: TaskInfo[];
  billingEntities: BillingEntityFull[];
  onClose: () => void;
  onCreated: () => void;
  defaultTaskId?: number;
  defaultCertificateId?: number;
  defaultCertificateIds?: number[];   // Multi-cert bootstrap from Certificates page
  // Litigation bootstrap from the Litigations panel — these are pure pass-
  // through fields; the wizard reuses its existing TASK-mode flow for the
  // form, and just appends litigationId / litigationStepId to the create
  // payload so the server links the new invoice properly.
  defaultLitigationId?: number;
  defaultLitigationStepId?: number;
}

export const InvoiceCreateWizard: React.FC<WizardProps> = ({
  tasks, billingEntities, onClose, onCreated, defaultTaskId, defaultCertificateId, defaultCertificateIds,
  defaultLitigationId, defaultLitigationStepId,
}) => {
  const { settings: orgSettings } = useOrgSettings();
  const firmName = useFirmName();

  const hasCertBootstrap = (defaultCertificateIds?.length ?? 0) >= 1 || !!defaultCertificateId;
  const [mode, setMode] = useState<WizardMode>(hasCertBootstrap ? 'CERT' : 'TASK');
  const bootstrapApplied = useRef(false);

  // ── Client picker (always shown) ──────────────────────────────────────────
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDrop, setShowClientDrop] = useState(false);

  // ── Certs list (lazily loaded when mode = CERT) ───────────────────────────
  const [allCerts, setAllCerts] = useState<CertSummary[]>([]);
  const [certsLoading, setCertsLoading] = useState(false);

  // ── Available clients ─────────────────────────────────────────────────────
  const [allClients, setAllClients] = useState<{ id: number; clientCode: string; clientName: string; legalName?: string }[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  // ── Bill to another party ─────────────────────────────────────────────────
  // selectedClientId stays the client the work was done for (its tasks and
  // certificates fill the invoice). When this is on, the invoice is raised on
  // billToClientId instead: its GSTIN, its receivable, its TDS.
  const [billToOther, setBillToOther] = useState(false);
  const [billToClientId, setBillToClientId] = useState<number | null>(null);
  const [billToSearch, setBillToSearch] = useState('');
  const [showBillToDrop, setShowBillToDrop] = useState(false);
  const gstinClientId = billToOther ? billToClientId : selectedClientId;
  const autoNote = useRef('');

  // ── Billing + GSTIN + Signatory ───────────────────────────────────────────
  const [billingEntityId, setBillingEntityId] = useState('');
  const [clientGstins, setClientGstins] = useState<ClientGstin[]>([]);
  const [clientGstinId, setClientGstinId] = useState('');
  const [signatoryId, setSignatoryId] = useState('');

  // Auto-select default signatory when billing entity changes
  const selectedBESignatories = useMemo(
    () => billingEntities.find(b => String(b.id) === billingEntityId)?.signatories ?? [],
    [billingEntities, billingEntityId]
  );

  // Default billing entity → the one matching the firm name if there is one,
  // else the first. Previously hardcoded to look for "mgsg", which silently
  // did nothing for every other firm.
  useEffect(() => {
    if (billingEntityId || billingEntities.length === 0) return;
    const needle = firmName.trim().toLowerCase();
    const match = needle
      ? billingEntities.find(b => b.name.toLowerCase().includes(needle))
      : undefined;
    setBillingEntityId(String((match ?? billingEntities[0]).id));
    // eslint-disable-next-line
  }, [billingEntities]);

  // ── Tax + dates ───────────────────────────────────────────────────────────
  const [taxType, setTaxType] = useState<TaxType>((orgSettings.invoicing.defaultTaxType as TaxType) || 'NONE');
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [dueDate, setDueDate] = useState(sevenDaysAhead(today()));
  const [dueDateDirty, setDueDateDirty] = useState(false);
  const [hsnSacCode, setHsnSacCode] = useState('');
  const [notes, setNotes] = useState('');
  const [template] = useState(1);
  const [rcmApplicable, setRcmApplicable] = useState(false);

  // ── Line items ────────────────────────────────────────────────────────────
  const [lineItems, setLineItems] = useState<WizardLine[]>([mkLine(1)]);
  // Keyed by taskId so expenses stay attributable as blocks are added/removed/
  // re-picked — a flat array couldn't be pruned when a task was deselected.
  // Populated by the effect below for every task currently in play (TASK mode:
  // each block's task; CERT mode: each block's certificate's underlying task),
  // not just the one task from a ?taskId= deep link — see the effect for why
  // that distinction is the actual fix.
  const [expensesByTask, setExpensesByTask] = useState<Record<number, TaskExpenseItem[]>>({});
  const taskExpenses = useMemo(() => Object.values(expensesByTask).flat(), [expensesByTask]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedBE = billingEntities.find(b => String(b.id) === billingEntityId);
  const selectedCG = clientGstins.find(g => String(g.id) === clientGstinId);

  // ── Sync due date to invoiceDate + 7 days unless user has edited it ───────
  useEffect(() => {
    if (!dueDateDirty) setDueDate(sevenDaysAhead(invoiceDate));
  }, [invoiceDate, dueDateDirty]);

  // ── Load certs when mode = CERT ──────────────────────────────────────────
  useEffect(() => {
    if (mode !== 'CERT') return;
    if (allCerts.length > 0) return;
    setCertsLoading(true);
    getAllCertificates().then(r => setAllCerts(r.data)).catch(() => {}).finally(() => setCertsLoading(false));
  }, [mode]);

  // ── Load all clients always ──────────────────────────────────────────────
  useEffect(() => {
    if (allClients.length > 0) return;
    getClients().then(r => setAllClients(r.data)).catch(() => {});
  }, []);

  // ── Bootstrap: TASK mode (from ?taskId=X) ────────────────────────────────
  useEffect(() => {
    if (bootstrapApplied.current) return;
    if (!defaultTaskId) return;
    const task = tasks.find(t => t.id === defaultTaskId);
    if (!task) return;
    bootstrapApplied.current = true;
    setMode('TASK');
    if (task.clientId) setSelectedClientId(task.clientId);
    const desc = task.category ? `${task.category.name} — ${task.taskName}` : task.taskName;
    const hsn = task.category?.hsnSacCode ?? '';
    setLineItems([mkLine(1, {
      groupKey: 1, taskId: task.id, description: desc, hsnSac: hsn,
      rate: Number(task.billedAmount ?? 0), amount: Number(task.billedAmount ?? 0),
    })]);
    // Pull full task detail (for expenses / category HSN) — non-blocking
    fetchedExpenseTaskIds.current.add(task.id);
    getTask(task.id).then(r => {
      const d: TaskDetailFull = r.data;
      setExpensesByTask(prev => ({ ...prev, [task.id]: unbilledOnly(d.expenses) }));
    }).catch(() => { fetchedExpenseTaskIds.current.delete(task.id); });
  }, [defaultTaskId, tasks]);

  // ── Bootstrap: CERT mode (from ?certificateId=X or ?certificateIds=…) ────
  // Builds one block per certificate with description / HSN pre-filled.
  useEffect(() => {
    if (bootstrapApplied.current) return;
    if (!hasCertBootstrap) return;
    if (allCerts.length === 0) return; // wait until certs loaded
    const ids = defaultCertificateIds && defaultCertificateIds.length > 0
      ? defaultCertificateIds
      : (defaultCertificateId ? [defaultCertificateId] : []);
    const certs = ids
      .map(id => allCerts.find(c => c.id === id))
      .filter(Boolean) as CertSummary[];
    if (certs.length === 0) return;
    // All certs must belong to the same client
    const clientIds = Array.from(new Set(certs.map(c => c.task?.clientId).filter(Boolean)));
    if (clientIds.length > 1) {
      setError('Selected certificates belong to multiple clients. Pick certificates of a single client.');
      bootstrapApplied.current = true;
      return;
    }
    bootstrapApplied.current = true;
    setMode('CERT');
    const cid = clientIds[0] as number | undefined;
    if (cid) setSelectedClientId(cid);
    const lines: WizardLine[] = certs.map((cert, idx) => ({
      slNo: idx + 1, groupKey: idx + 1,
      taskId: cert.taskId, certId: cert.id,
      description: cert.task?.category ? `${cert.task.category.name} — ${cert.name}` : cert.name,
      hsnSac: cert.task?.category?.hsnSacCode ?? '',
      quantity: 1, rate: 0, unit: '', amount: 0, isOpe: false,
    }));
    setLineItems(lines);
  }, [hasCertBootstrap, defaultCertificateId, defaultCertificateIds, allCerts]);

  // ── Load GSTINs when multi-task client chosen ─────────────────────────────
  // The GSTIN always belongs to whoever the invoice is raised on.
  useEffect(() => {
    setClientGstins([]); setClientGstinId('');
    if (!gstinClientId) return;
    let stale = false;
    getClientGstins(gstinClientId).then(gr => {
      if (stale) return;
      const gs: ClientGstin[] = gr.data;
      setClientGstins(gs);
      const primary = gs.find(g => g.isPrimary);
      if (primary) setClientGstinId(String(primary.id));
    }).catch(() => {});
    return () => { stale = true; };
  }, [gstinClientId]);

  // The printed invoice names only the billed party, so say whose work it was
  // in the notes (which print). Only ever replaces its own text, never the user's.
  useEffect(() => {
    const real = billToOther && billToClientId ? allClients.find(c => c.id === selectedClientId) : undefined;
    const next = real ? `For services rendered to: ${real.legalName || real.clientName}` : '';
    const previous = autoNote.current;
    autoNote.current = next;
    setNotes(prev => (prev === previous ? next : prev));
  }, [billToOther, billToClientId, selectedClientId, allClients]);

  // ── Auto-detect tax type ──────────────────────────────────────────────────
  useEffect(() => { setTaxType(autoTaxType(selectedBE, selectedCG)); }, [selectedBE, selectedCG]);

  // ── Auto-select default signatory when billing entity changes ─────────────
  useEffect(() => {
    const defaultSig = selectedBESignatories.find(s => s.isDefault);
    setSignatoryId(defaultSig ? String(defaultSig.id) : (selectedBESignatories.length === 1 ? String(selectedBESignatories[0].id) : ''));
  }, [billingEntityId, selectedBESignatories]);

  // ── Reset state on mode change (TASK ⇄ CERT) ─────────────────────────────
  const resetSelection = (newMode: WizardMode) => {
    setMode(newMode);
    setExpensesByTask({});
    setSelectedClientId(null); setClientSearch('');
    setBillToOther(false); setBillToClientId(null); setBillToSearch('');
    setLineItems([mkLine(1, { groupKey: 1 })]); setHsnSacCode(''); setError('');
  };

  // Changing the client the work was for empties the task blocks; the GSTIN
  // list follows gstinClientId on its own.
  const clearWorkClient = (search: string) => {
    setSelectedClientId(null); setClientSearch(search);
    setLineItems([mkLine(1, { groupKey: 1 })]); setExpensesByTask({});
  };

  const billToClients = useMemo(() => {
    const q = billToSearch.toLowerCase();
    return allClients.filter(c => c.id !== selectedClientId && (
      c.clientCode.toLowerCase().includes(q) ||
      c.clientName.toLowerCase().includes(q) ||
      (c.legalName ?? '').toLowerCase().includes(q)
    )).slice(0, 20);
  }, [allClients, billToSearch, selectedClientId]);

  const clientTasks = useMemo(() =>
    selectedClientId ? tasks.filter(t => t.clientId === selectedClientId) : [],
    [tasks, selectedClientId]
  );

  const filteredClients = useMemo(() => {
    const q = clientSearch.toLowerCase();
    return allClients.filter(c =>
      c.clientCode.toLowerCase().includes(q) ||
      c.clientName.toLowerCase().includes(q) ||
      (c.legalName ?? '').toLowerCase().includes(q)
    ).slice(0, 20);
  }, [allClients, clientSearch]);

  const clientCerts = useMemo(() =>
    selectedClientId
      ? allCerts.filter(c => (c.task?.clientId ?? c.task?.client?.id) === selectedClientId)
      : [],
    [allCerts, selectedClientId],
  );

  // ── Line item helpers ─────────────────────────────────────────────────────
  const updateLine = (idx: number, field: keyof WizardLine, val: any) => {
    setLineItems(prev => {
      const next = [...prev];
      (next[idx] as any)[field] = val;
      if (field === 'quantity' || field === 'rate') {
        next[idx].amount = parseFloat((Number(next[idx].quantity) * Number(next[idx].rate)).toFixed(2));
      }
      return next;
    });
  };

  const addServiceLine = () => setLineItems(prev => [...prev, mkLine(prev.length + 1, { hsnSac: hsnSacCode })]);
  // OPE lines inherit the parent task's HSN/SAC — the same code applies because
  // the OPE recovery is treated as part of the same composite supply.
  const addOpeLine = () => setLineItems(prev => [...prev, mkLine(prev.length + 1, { isOpe: true, hsnSac: hsnSacCode })]);

  const addExpenseLine = (exp: TaskExpenseItem) => {
    setLineItems(prev => [...prev, mkLine(prev.length + 1, {
      description: exp.description, hsnSac: hsnSacCode,
      rate: Number(exp.amount), amount: Number(exp.amount),
      isOpe: true, expenseId: exp.id,
    })]);
  };

  const removeLine = (idx: number) =>
    setLineItems(prev => prev.filter((_, i) => i !== idx).map((l, i) => ({ ...l, slNo: i + 1 })));

  // ── Grouped-block helpers ────────────────────────────────────────────────
  // Every invoice is now a "multi" invoice. A single-task/cert invoice is
  // simply a multi invoice with one block. Each block has a unique groupKey.
  const lineGroups = useMemo(() => {
    const map = new Map<number, { groupKey: number; taskId?: number; certId?: number; lines: WizardLine[]; indices: number[] }>();
    lineItems.forEach((li, idx) => {
      let g = map.get(li.groupKey);
      if (!g) {
        g = { groupKey: li.groupKey, taskId: li.taskId, certId: li.certId, lines: [], indices: [] };
        map.set(li.groupKey, g);
      } else {
        // Keep group's task/cert in sync with its first line
        if (li.taskId !== undefined) g.taskId = li.taskId;
        if (li.certId !== undefined) g.certId = li.certId;
      }
      g.lines.push(li);
      g.indices.push(idx);
    });
    return Array.from(map.values());
  }, [lineItems]);

  // ── OPE suggestions follow whichever tasks are actually in the invoice ────
  // Previously the "click to add as OPE line" panel only ever populated from
  // the ?taskId= deep link a task's own "Raise Invoice" button sets — picking
  // a task from the in-wizard dropdown (the normal path from + New Invoice)
  // never fetched that task's expenses, so nothing was ever suggested there.
  // This re-derives the set of tasks actually selected — one per TASK-mode
  // block, or a cert block's underlying task in CERT mode — and fetches any
  // task's expenses the moment it's picked, from whichever entry point.
  const selectedTaskIdsForExpenses = useMemo(() => {
    const ids = new Set<number>();
    for (const g of lineGroups) {
      if (mode === 'TASK' && g.taskId) ids.add(g.taskId);
      if (mode === 'CERT' && g.certId) {
        const cert = allCerts.find(c => c.id === g.certId);
        if (cert?.taskId) ids.add(cert.taskId);
      }
    }
    return ids;
  }, [lineGroups, mode, allCerts]);

  const fetchedExpenseTaskIds = useRef(new Set<number>());
  useEffect(() => {
    // Drop expenses for tasks no longer represented by any block, so
    // deselecting a task also retracts its suggestions.
    setExpensesByTask(prev => {
      let changed = false;
      const next: Record<number, TaskExpenseItem[]> = {};
      for (const [idStr, exps] of Object.entries(prev)) {
        const id = Number(idStr);
        if (selectedTaskIdsForExpenses.has(id)) next[id] = exps;
        else changed = true;
      }
      return changed ? next : prev;
    });
    Array.from(fetchedExpenseTaskIds.current).forEach(id => {
      if (!selectedTaskIdsForExpenses.has(id)) fetchedExpenseTaskIds.current.delete(id);
    });
    Array.from(selectedTaskIdsForExpenses).forEach(taskId => {
      if (fetchedExpenseTaskIds.current.has(taskId)) return;
      fetchedExpenseTaskIds.current.add(taskId);
      getTask(taskId).then(r => {
        const d: TaskDetailFull = r.data;
        setExpensesByTask(prev => ({ ...prev, [taskId]: unbilledOnly(d.expenses) }));
      }).catch(() => { fetchedExpenseTaskIds.current.delete(taskId); });
    });
  }, [selectedTaskIdsForExpenses]);

  const nextGroupKey = () => (lineItems.length === 0 ? 1 : Math.max(...lineItems.map(l => l.groupKey)) + 1);

  const addBlock = () => {
    const gk = nextGroupKey();
    setLineItems(prev => [...prev, mkLine(prev.length + 1, { groupKey: gk })]);
  };

  const removeBlock = (gk: number) => {
    setLineItems(prev => {
      const kept = prev.filter(l => l.groupKey !== gk).map((l, i) => ({ ...l, slNo: i + 1 }));
      return kept.length === 0 ? [mkLine(1, { groupKey: nextGroupKey() })] : kept;
    });
  };

  const addLineToBlock = (gk: number, isOpe: boolean) => {
    setLineItems(prev => {
      const blockLines = prev.filter(l => l.groupKey === gk);
      const proto = blockLines[0];
      const next = [...prev, mkLine(prev.length + 1, {
        groupKey: gk,
        taskId: proto?.taskId,
        certId: proto?.certId,
        hsnSac: hsnSacCode,
        isOpe,
      })];
      return next;
    });
  };

  const setBlockTask = (gk: number, taskId: number | undefined) => {
    const task = taskId ? tasks.find(t => t.id === taskId) : undefined;
    setLineItems(prev => {
      const blockLines = prev.filter(l => l.groupKey === gk);
      // Only the FIRST line in the block is auto-derived from the task; any
      // additional lines (OPE recoveries, manual additions) the user added
      // are preserved as-is. The first line gets the new task's description,
      // HSN, and billed amount on every change — so re-picking a task in the
      // dropdown updates the line, not just the taskId.
      const firstLineIdx = prev.findIndex(l => l.groupKey === gk);
      return prev.map((l, idx) => {
        if (l.groupKey !== gk) return l;
        if (idx === firstLineIdx && task) {
          const desc = task.category ? `${task.category.name} — ${task.taskName}` : task.taskName;
          return {
            ...l, taskId,
            description: desc,
            hsnSac: task.category?.hsnSacCode ?? l.hsnSac,
            rate: Number(task.billedAmount ?? 0),
            amount: Number(task.billedAmount ?? 0),
          };
        }
        // Clearing the task (taskId === undefined): just unset the link
        if (idx === firstLineIdx && !task && blockLines.length === 1) {
          return { ...l, taskId: undefined, description: '', hsnSac: '', rate: 0, amount: 0 };
        }
        return { ...l, taskId };
      });
    });
  };

  const setBlockCert = (gk: number, certId: number | undefined) => {
    const cert = certId ? allCerts.find(c => c.id === certId) : undefined;
    setLineItems(prev => {
      const firstLineIdx = prev.findIndex(l => l.groupKey === gk);
      return prev.map((l, idx) => {
        if (l.groupKey !== gk) return l;
        // First line gets re-derived on every cert change. HSN comes from the
        // underlying task's category (certificates don't carry their own
        // HSN — they share their parent task's classification).
        if (idx === firstLineIdx && cert) {
          const cat = cert.task?.category;
          const desc = cat ? `${cat.name} — ${cert.name}` : cert.name;
          return {
            ...l,
            certId,
            taskId: cert.taskId,
            description: desc,
            hsnSac: cat?.hsnSacCode ?? l.hsnSac,
          };
        }
        if (idx === firstLineIdx && !cert) {
          return { ...l, certId: undefined, description: '', hsnSac: '' };
        }
        return { ...l, certId, taskId: cert?.taskId ?? l.taskId };
      });
    });
  };

  const taskLabel = (t: TaskInfo) => {
    const cat = t.category?.name;
    return `${t.taskId} — ${t.taskName}${cat ? ` (${cat})` : ''}`;
  };

  const certLabel = (c: CertSummary) => {
    const cat = c.task?.category?.name;
    return `${c.certificateNumber} — ${c.name}${cat ? ` (${cat})` : ''}`;
  };

  // ── Tax math ──────────────────────────────────────────────────────────────
  const cgstR = Number(orgSettings.invoicing.defaultCgstRate);
  const sgstR = Number(orgSettings.invoicing.defaultSgstRate);
  const igstR = Number(orgSettings.invoicing.defaultIgstRate);
  const taxableAmt = lineItems.reduce((s, l) => s + Number(l.amount), 0);
  const cgst = taxType === 'CGST_SGST' ? taxableAmt * (cgstR / 100) : 0;
  const sgst = taxType === 'CGST_SGST' ? taxableAmt * (sgstR / 100) : 0;
  const igst = taxType === 'IGST' ? taxableAmt * (igstR / 100) : 0;
  const totalAmt = taxableAmt + cgst + sgst + igst;

  const expenseIdsAdded = new Set(lineItems.filter(l => l.expenseId).map(l => l.expenseId));

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!billingEntityId) { setError('Billing entity is required.'); return; }
    if (!clientGstinId) { setError('Client GSTIN is required.'); return; }
    // NONE is only valid for a zero-rated supply under LUT/bond (without
    // payment of tax). Exports AND supplies to an SEZ unit both qualify
    // (IGST Act §16) — autoTaxType defaults exports to NONE and SEZ to IGST,
    // and SEZ may be flipped to NONE for an LUT/bond supply, which is what
    // makes it SEZWOP in saleTypeLabel. Every other GST type must pick an
    // actual tax type. The backend allows both (see createInvoice); this
    // guard omitted SEZ until 10-Sep-26 and blocked zero-rated SEZ invoices.
    if (taxType === 'NONE' && selectedCG?.gstType !== 'EXPORT' && selectedCG?.gstType !== 'SEZ') {
      setError('Tax type cannot be NONE — select CGST+SGST or IGST.'); return;
    }
    if (lineItems.some(l => !l.description.trim())) { setError('All line items must have a description.'); return; }
    if (!selectedClientId) { setError('Please select a client.'); return; }
    if (billToOther && !billToClientId) { setError('Select the party to bill, or untick "Bill to another party".'); return; }
    if (mode === 'TASK' && lineGroups.some(g => !g.taskId)) { setError('Each block must have a task selected.'); return; }
    if (mode === 'CERT' && lineGroups.some(g => !g.certId)) { setError('Each block must have a certificate selected.'); return; }

    // ── Unbilled-OPE guard ───────────────────────────────────────────────────
    // The suggestion panel sits above the task picker, so it is easy to invoice
    // a task and never notice money the firm has already paid out on it. Say
    // what is being left behind before the invoice goes out, rather than
    // discovering it months later (IVY T0281 went out missing Rs 9,054).
    // It is a confirm, not a block: billing OPE separately is legitimate.
    const leftBehind = taskExpenses.filter(exp => !expenseIdsAdded.has(exp.id));
    if (leftBehind.length > 0) {
      const lines = leftBehind
        .map(exp => `  • ${exp.description} — ₹${Number(exp.amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`)
        .join('\n');
      const total = leftBehind.reduce((s, exp) => s + Number(exp.amount), 0);
      const ok = window.confirm(
        `This task has ${leftBehind.length} unbilled out-of-pocket expense${leftBehind.length > 1 ? 's' : ''} `
        + `totalling ₹${total.toLocaleString('en-IN', { maximumFractionDigits: 0 })} that are NOT on this invoice:\n\n`
        + `${lines}\n\n`
        + 'Add them from the "Task Out-of-Pocket Expenses" panel above, or press OK to raise the invoice without them.',
      );
      if (!ok) return;
    }

    setSaving(true); setError('');
    try {
      await createInvoice({
        clientId: billToOther ? billToClientId : selectedClientId, isMultiTask: true,
        ...(billToOther ? { serviceClientId: selectedClientId } : {}),
        invoiceType: defaultLitigationStepId || defaultLitigationId
          ? 'LITIGATION'
          : (mode === 'CERT' ? 'CERTIFICATE' : 'TASK'),
        // Pass through litigation linkage so the server attaches the new
        // invoice to the right step / parent litigation. Step-level uses
        // the 1:1 column and triggers the duplicate-prevention check.
        ...(defaultLitigationStepId ? { litigationStepId: defaultLitigationStepId } : {}),
        ...(defaultLitigationId     ? { litigationId:     defaultLitigationId     } : {}),
        billingEntityId: Number(billingEntityId),
        clientGstinId: Number(clientGstinId),
        taxType,
        invoiceDate,
        dueDate: dueDate || undefined,
        hsnSacCode: hsnSacCode || undefined,
        template,
        rcmApplicable,
        notes: notes || undefined,
        amount: taxableAmt,
        signatoryId: signatoryId ? Number(signatoryId) : undefined,
        lineItems: lineItems.map(li => ({
          slNo: li.slNo, description: li.description,
          hsnSac: li.hsnSac || hsnSacCode || undefined,
          quantity: li.quantity, rate: li.rate,
          amount: li.amount,
          taskId: li.taskId || undefined,
          certId: li.certId || undefined,
          isOpe: li.isOpe,
          expenseId: li.expenseId || undefined,
        })),
      });
      onCreated();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create invoice.');
    } finally { setSaving(false); }
  };

  // No global clientDisplayName needed — client is always picked explicitly via the typeahead now.

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-0 sm:p-4 overflow-y-auto">
      {/* Full-bleed on a phone: 1rem of dimmed backdrop either side of a
          form this dense is a waste of the only 375px there is. */}
      <div className="bg-white sm:rounded-xl shadow-2xl w-full max-w-4xl min-h-full sm:min-h-0 my-0 sm:my-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 sm:rounded-t-xl sticky top-0 z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900">New GST Invoice</h2>
            <p className="text-xs text-gray-500 mt-0.5">Fill in the details to generate a tax invoice</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="px-4 sm:px-6 py-5 space-y-5">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">{error}</div>}

          {/* Mode tabs — Task or Certificate */}
          <div>
            <label className="label">Invoice For</label>
            <div className="inline-flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
              {([
                ['TASK', '📄 Task(s)'],
                ['CERT', '🎓 Certificate(s)'],
              ] as [WizardMode, string][]).map(([m, label]) => (
                <button key={m} type="button" onClick={() => resetSelection(m)}
                  className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors ${mode === m ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-1">Add one or more {mode === 'CERT' ? 'certificates' : 'tasks'} to a single invoice for the same client.</p>
          </div>

          {/* ── Client picker (always visible) ───────────────────────────────── */}
          <div>
            <label className="label">Client (all {mode === 'CERT' ? 'certificates' : 'tasks'} must belong to the same client) *</label>
            <div className="relative">
              <input className="input-field pr-7"
                placeholder="Search client by name or code…"
                value={selectedClientId
                  ? (allClients.find(c => c.id === selectedClientId)?.clientName ?? clientSearch)
                  : clientSearch}
                onChange={e => { clearWorkClient(e.target.value); setShowClientDrop(true); }}
                onFocus={() => { if (!selectedClientId) setShowClientDrop(true); }}
                onBlur={() => setTimeout(() => setShowClientDrop(false), 180)}
              />
              {selectedClientId && (
                <button type="button" onClick={() => clearWorkClient('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm">✕</button>
              )}
              {showClientDrop && !selectedClientId && (
                <div className="absolute z-20 w-full bg-white border border-gray-200 rounded shadow-lg mt-1 max-h-56 overflow-y-auto">
                  {filteredClients.length === 0
                    ? <p className="px-3 py-2 text-sm text-gray-400">No clients found.</p>
                    : filteredClients.map(c => (
                      <button key={c.id} type="button" onMouseDown={() => { setSelectedClientId(c.id); setClientSearch(''); setShowClientDrop(false); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2">
                        <span className="font-mono text-xs text-gray-400 shrink-0">{c.clientCode}</span>
                        <span className="font-medium">{c.clientName}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>

            {/* ── Bill to another party ─────────────────────────────────────── */}
            <label className="inline-flex items-center gap-2 mt-2 text-xs text-gray-700 cursor-pointer">
              <input type="checkbox" checked={billToOther}
                onChange={e => { setBillToOther(e.target.checked); if (!e.target.checked) { setBillToClientId(null); setBillToSearch(''); } }} />
              Bill this invoice to another party (not the client above)
            </label>
            {billToOther && (
              <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <label className="label">Party to bill *</label>
                <div className="relative">
                  <input className="input-field pr-7"
                    placeholder="Search the party to bill by name or code…"
                    value={billToClientId
                      ? (allClients.find(c => c.id === billToClientId)?.clientName ?? billToSearch)
                      : billToSearch}
                    onChange={e => { setBillToSearch(e.target.value); setBillToClientId(null); setShowBillToDrop(true); }}
                    onFocus={() => { if (!billToClientId) setShowBillToDrop(true); }}
                    onBlur={() => setTimeout(() => setShowBillToDrop(false), 180)}
                  />
                  {billToClientId && (
                    <button type="button" onClick={() => { setBillToClientId(null); setBillToSearch(''); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm">✕</button>
                  )}
                  {showBillToDrop && !billToClientId && (
                    <div className="absolute z-20 w-full bg-white border border-gray-200 rounded shadow-lg mt-1 max-h-56 overflow-y-auto">
                      {billToClients.length === 0
                        ? <p className="px-3 py-2 text-sm text-gray-400">No match. Add the party in Clients (with its GSTIN) first.</p>
                        : billToClients.map(c => (
                          <button key={c.id} type="button" onMouseDown={() => { setBillToClientId(c.id); setBillToSearch(''); setShowBillToDrop(false); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2">
                            <span className="font-mono text-xs text-gray-400 shrink-0">{c.clientCode}</span>
                            <span className="font-medium">{c.clientName}</span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-amber-800 mt-1.5">
                  The invoice, GSTIN, receivable, receipts and TDS go under this party. The tasks and the billing figures stay with the client above.
                </p>
              </div>
            )}
          </div>

          {/* ── Invoice Details (Billing, GSTIN, Tax, Dates) ─────────────────── */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Invoice Details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label">Billing Entity (Our Firm) *</label>
                <select className="input-field" value={billingEntityId} onChange={e => setBillingEntityId(e.target.value)} required>
                  <option value="">— Select —</option>
                  {billingEntities.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label">{billToOther ? 'Billed Party GSTIN / Address *' : 'Client GSTIN / Address *'}</label>
                <select className="input-field" value={clientGstinId} onChange={e => setClientGstinId(e.target.value)}
                  disabled={clientGstins.length === 0} required>
                  <option value="">— Select —</option>
                  {clientGstins.map(g => (
                    <option key={g.id} value={g.id}>
                      {g.label ? `${g.label}: ` : ''}{g.gstType === 'REGISTERED' ? g.gstin : g.gstType === 'SEZ' && g.gstin ? `${g.gstin} (SEZ)` : g.gstType}{g.isPrimary ? ' ★' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="label">Tax Type *</label>
                <select className={`input-field ${taxType === 'NONE' && selectedCG?.gstType === 'REGISTERED' ? 'border-red-400 bg-red-50' : ''}`}
                  value={taxType} onChange={e => setTaxType(e.target.value as TaxType)}>
                  <option value="NONE">No Tax (Export)</option>
                  <option value="CGST_SGST">CGST + SGST (Intrastate)</option>
                  <option value="IGST">IGST (Interstate)</option>
                </select>
                {taxType === 'NONE' && selectedCG?.gstType === 'REGISTERED' && (
                  <p className="text-red-600 text-xs mt-0.5">Blocked for registered clients.</p>
                )}
              </div>
              <div>
                <label className="label">Invoice Date *</label>
                <input type="date" className="input-field" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} required />
              </div>
              <div>
                <label className="label">Due Date</label>
                <input type="date" className="input-field" value={dueDate} onChange={e => { setDueDate(e.target.value); setDueDateDirty(true); }} />
              </div>
            </div>
            <p className="text-[10px] text-gray-400 -mt-1">
              HSN/SAC is set per line item below — defaults from each task's category.
            </p>
          </div>

          {/* ── OPE from task expenses ────────────────────────────────────────── */}
          {taskExpenses.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-amber-800 mb-2">Task Out-of-Pocket Expenses — click to add as OPE line item</p>
              <div className="flex flex-wrap gap-2">
                {taskExpenses.map(exp => {
                  const added = expenseIdsAdded.has(exp.id);
                  return (
                    <button key={exp.id} type="button" disabled={added} onClick={() => addExpenseLine(exp)}
                      className={`text-xs px-2.5 py-1 rounded border font-medium ${added ? 'border-green-300 bg-green-50 text-green-700 cursor-default' : 'border-amber-400 bg-white text-amber-800 hover:bg-amber-100'}`}>
                      {added ? '✓ ' : '+ '}{exp.description} — ₹{Number(exp.amount).toLocaleString('en-IN')}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Tasks / Certificates & line-item blocks ─────────────────────── */}
          {selectedClientId && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label mb-0">
                  {mode === 'TASK' ? 'Tasks & Line Items' : 'Certificates & Line Items'}
                </label>
                <button type="button" onClick={addBlock}
                  className="text-xs px-3 py-1 border border-indigo-300 text-indigo-600 rounded hover:bg-indigo-50 font-medium">
                  + Add {mode === 'TASK' ? 'Task' : 'Certificate'}
                </button>
              </div>

              {mode === 'CERT' && certsLoading && (
                <p className="text-xs text-gray-400 mb-2">Loading certificates…</p>
              )}

              <div className="space-y-3">
                {lineGroups.map(g => {
                  const pickedTaskIds = new Set(
                    lineGroups.filter(other => other.groupKey !== g.groupKey && other.taskId).map(other => other.taskId!)
                  );
                  const pickedCertIds = new Set(
                    lineGroups.filter(other => other.groupKey !== g.groupKey && other.certId).map(other => other.certId!)
                  );
                  return (
                    <div key={g.groupKey} className="border border-gray-200 rounded-lg bg-white">
                      {/* Header: task/cert selector */}
                      <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50/40 border-b border-gray-200 rounded-t-lg">
                        <span className="text-xs font-semibold text-indigo-700 shrink-0">
                          {mode === 'TASK' ? 'Task:' : 'Certificate:'}
                        </span>
                        {mode === 'TASK' ? (
                          <select className="input-field text-xs py-1 flex-1"
                            value={g.taskId ?? ''}
                            onChange={e => setBlockTask(g.groupKey, e.target.value ? Number(e.target.value) : undefined)}>
                            <option value="">— Select task —</option>
                            {clientTasks.filter(t => !pickedTaskIds.has(t.id)).map(t => (
                              <option key={t.id} value={t.id}>{taskLabel(t)}</option>
                            ))}
                          </select>
                        ) : (
                          <select className="input-field text-xs py-1 flex-1"
                            value={g.certId ?? ''}
                            onChange={e => setBlockCert(g.groupKey, e.target.value ? Number(e.target.value) : undefined)}>
                            <option value="">— Select certificate —</option>
                            {clientCerts.filter(c => !pickedCertIds.has(c.id)).map(c => (
                              <option key={c.id} value={c.id}>{certLabel(c)}</option>
                            ))}
                          </select>
                        )}
                        {lineGroups.length > 1 && (
                          <button type="button" onClick={() => removeBlock(g.groupKey)}
                            className="text-red-500 hover:text-red-700 text-xs font-medium shrink-0" title="Remove this block">
                            × Remove
                          </button>
                        )}
                      </div>

                      {/* Lines for this block */}
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600 w-6">#</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Description *</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600 w-20">HSN/SAC</th>
                              <th className="px-2 py-1.5 text-center font-medium text-gray-600 w-14">Qty</th>
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600 w-24">Rate (₹)</th>
                              <th className="px-2 py-1.5 text-right font-medium text-gray-600 w-24">Amount</th>
                              <th className="w-5"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.lines.map((li, i) => {
                              const idx = g.indices[i];
                              return (
                                <tr key={idx} className={`border-t border-gray-100 ${li.isOpe ? 'bg-amber-50/30' : ''}`}>
                                  <td className="px-2 py-1 text-gray-400">
                                    {li.slNo}
                                    {li.isOpe && <span className="ml-1 text-[9px] font-semibold text-amber-700">OPE</span>}
                                  </td>
                                  <td className="px-2 py-1 min-w-[160px]">
                                    <input className="w-full border-0 outline-none text-xs" value={li.description}
                                      onChange={e => updateLine(idx, 'description', e.target.value)} required placeholder="Service description…" />
                                  </td>
                                  <td className="px-2 py-1">
                                    <input className="w-full border-0 outline-none text-xs font-mono" value={li.hsnSac}
                                      onChange={e => updateLine(idx, 'hsnSac', e.target.value)} placeholder={hsnSacCode} />
                                  </td>
                                  <td className="px-2 py-1">
                                    <input type="number" className="w-full border-0 outline-none text-xs text-center" value={li.quantity}
                                      min={0} step="0.001" onChange={e => updateLine(idx, 'quantity', parseFloat(e.target.value) || 0)} />
                                  </td>
                                  <td className="px-2 py-1">
                                    <input type="number" className="w-full border-0 outline-none text-xs" value={li.rate}
                                      min={0} step="0.01" onChange={e => updateLine(idx, 'rate', parseFloat(e.target.value) || 0)} />
                                  </td>
                                  <td className="px-2 py-1 text-right font-medium">{fmt(li.amount)}</td>
                                  <td className="px-2 py-1">
                                    {g.lines.length > 1 && (
                                      <button type="button" onClick={() => removeLine(idx)} className="text-red-400 hover:text-red-600 leading-none">×</button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Per-block add buttons */}
                      <div className="flex gap-2 px-3 py-2 border-t border-gray-100 bg-gray-50/50">
                        <button type="button" onClick={() => addLineToBlock(g.groupKey, false)}
                          className="text-xs px-2 py-1 border border-blue-300 text-blue-600 rounded hover:bg-blue-50">+ Service Line</button>
                        <button type="button" onClick={() => addLineToBlock(g.groupKey, true)}
                          className="text-xs px-2 py-1 border border-amber-300 text-amber-700 rounded hover:bg-amber-50">+ OPE Line</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Tax summary ───────────────────────────────────────────────────── */}
          <div className="bg-gray-50 rounded p-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-gray-500">Taxable Amount</span><span className="font-medium">{fmt(taxableAmt)}</span></div>
            {taxType === 'CGST_SGST' && <>
              <div className="flex justify-between text-gray-500"><span>CGST @ {cgstR}%</span><span>{fmt(cgst)}</span></div>
              <div className="flex justify-between text-gray-500"><span>SGST/UTGST @ {sgstR}%</span><span>{fmt(sgst)}</span></div>
            </>}
            {taxType === 'IGST' && <div className="flex justify-between text-gray-500"><span>IGST @ {igstR}%</span><span>{fmt(igst)}</span></div>}
            <div className="flex justify-between font-bold text-base border-t border-gray-200 pt-1">
              <span>Total</span><span className="text-blue-700">{fmt(totalAmt)}</span>
            </div>
          </div>

          {/* ── RCM, Signatory, Notes ────────────────────────────────────────── */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Tax Treatment & Signature</p>
            <div>
              <label className="flex items-center gap-2 p-2.5 border border-gray-300 rounded cursor-pointer hover:bg-white bg-white/60">
                <input type="checkbox" checked={rcmApplicable} onChange={e => setRcmApplicable(e.target.checked)}
                  className="w-4 h-4 accent-blue-600" />
                <span className="text-sm text-gray-700">Tax payable under Reverse Charge Mechanism (RCM)</span>
              </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label">Signatory</label>
                <select className="input-field" value={signatoryId} onChange={e => setSignatoryId(e.target.value)}
                  disabled={selectedBESignatories.length === 0}>
                  <option value="">— None / Default —</option>
                  {selectedBESignatories.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}{s.designation ? ` (${s.designation})` : ''}{s.isDefault ? ' ★' : ''}
                    </option>
                  ))}
                </select>
                {selectedBESignatories.length === 0 && billingEntityId && (
                  <p className="text-[10px] text-gray-400 mt-0.5">No signatories configured for this entity.</p>
                )}
              </div>
              <div>
                <label className="label">Notes</label>
                <textarea className="input-field" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes…" />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-gray-200 -mx-6 -mb-5 px-6 py-4 bg-gray-50 rounded-b-xl">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Creating…' : 'Create Invoice'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Payment Modal ─────────────────────────────────────────────────────────────

const MODE_LABELS: Record<PaymentMode, string> = { CASH: 'Cash', BANK: 'Bank Transfer', CHEQUE: 'Cheque', UPI: 'UPI', OTHER: 'Other' };

export const PaymentModal: React.FC<{ invoice: Invoice; onClose: () => void; onSaved: () => void; canEdit: boolean }> = ({ invoice, onClose, onSaved, canEdit }) => {
  const { settings: orgSettings } = useOrgSettings();
  const tdsAutoSuggestFrac = Number(orgSettings.receipts.tdsAutoSuggestPct) / 100;
  const [invoiceData, setInvoiceData] = useState<Invoice>(invoice);
  const total = Number(invoiceData.totalAmount ?? invoiceData.amount);
  const tds = Number(invoiceData.tdsAmount ?? 0);
  const credited = Number(invoiceData.creditedAmount ?? 0);
  const writtenOff = Number(invoiceData.writtenOffAmount ?? 0);
  const netReceivable = total - tds;
  const paid = Number(invoiceData.paymentAmount ?? 0);
  const outstanding = netReceivable - paid - credited - writtenOff;

  const [payments, setPayments] = useState<Payment[]>(invoice.payments ?? []);
  const [loadingPay, setLoadingPay] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showWriteOff, setShowWriteOff] = useState(false);
  const [amount, setAmount] = useState(String(outstanding > 0 ? outstanding.toFixed(2) : ''));
  const [tdsDeducted, setTdsDeducted] = useState('');
  const [tdsAutoFilled, setTdsAutoFilled] = useState(false);
  const [payDate, setPayDate] = useState(today());
  const [mode, setMode] = useState<PaymentMode>('BANK');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Auto-detect TDS: if received = totalAmount - round(TDS% of taxable), pre-fill TDS field
  useEffect(() => {
    const entered = parseFloat(amount);
    if (isNaN(entered) || entered <= 0) return;
    const taxable = Number(invoiceData.amount);
    const invTotal = Number(invoiceData.totalAmount ?? invoiceData.amount);
    const expectedTds = Math.round(taxable * tdsAutoSuggestFrac);
    if (expectedTds <= 0) return;
    if (Math.round(entered) === invTotal - expectedTds) {
      setTdsDeducted(String(expectedTds));
      setTdsAutoFilled(true);
    } else if (tdsAutoFilled) {
      setTdsDeducted('');
      setTdsAutoFilled(false);
    }
  }, [amount]); // intentionally watching only amount

  const reload = async () => {
    setLoadingPay(true);
    try {
      const [payRes, invRes] = await Promise.all([
        getInvoicePayments(invoice.id),
        getInvoice(invoice.id),
      ]);
      setPayments(payRes.data);
      setInvoiceData(invRes.data);
    } finally { setLoadingPay(false); }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await recordPayment(invoice.id, {
        paymentAmount: parseFloat(amount),
        paymentDate: payDate,
        mode,
        reference: reference || undefined,
        paymentNotes: notes || undefined,
        tdsDeducted: tdsDeducted ? parseFloat(tdsDeducted) : undefined,
      });
      await reload();
      onSaved();
      setShowAdd(false); setAmount(''); setTdsDeducted(''); setReference(''); setNotes('');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to record payment.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (p: Payment) => {
    if (!window.confirm(`Delete ₹${Number(p.amount).toLocaleString('en-IN')} payment on ${fmtD(p.paymentDate)}?`)) return;
    try {
      await deleteInvoicePayment(p.id);
      await reload();
      onSaved();
    } catch (err: any) { alert(err?.response?.data?.message || 'Failed.'); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Payments — {invoiceData.invoiceNumber}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>
        <div className="px-6 py-3 bg-gray-50 border-b text-sm text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
          <span>Invoice: <strong>{fmt(total)}</strong></span>
          {tds > 0 && <span>TDS ({invoiceData.tdsSection}): <strong className="text-orange-600">−{fmt(tds)}</strong></span>}
          {tds > 0 && <span>Net Receivable: <strong>{fmt(netReceivable)}</strong></span>}
          {credited > 0 && <span>Credited (CN): <strong className="text-purple-600">−{fmt(credited)}</strong></span>}
          {writtenOff > 0 && <span>Written Off: <strong className="text-orange-700">−{fmt(writtenOff)}</strong></span>}
          <span>Received: <strong className="text-green-700">{fmt(paid)}</strong></span>
          <span>Outstanding: <strong className={outstanding > 0 ? 'text-red-600' : 'text-green-600'}>{fmt(Math.max(0, outstanding))}</strong></span>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-2">
          {loadingPay ? <p className="text-sm text-gray-400 text-center py-4">Loading…</p>
            : payments.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No receipts recorded yet.</p>
            : payments.map(p => (
              <div key={p.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  {(p as any).receiptNumber && <span className="font-mono text-xs text-gray-500">{(p as any).receiptNumber}</span>}
                  <span className="font-semibold text-green-700">{fmt(Number(p.amount))}</span>
                  {p.tdsDeducted && Number(p.tdsDeducted) > 0 && (
                    <span className="text-orange-600 text-xs">+TDS {fmt(Number(p.tdsDeducted))}</span>
                  )}
                  <span className="text-gray-500">{fmtD(p.paymentDate)}</span>
                  <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">{MODE_LABELS[p.mode]}</span>
                  {p.reference && <span className="text-gray-400 text-xs">Ref: {p.reference}</span>}
                  {p.notes && <span className="text-gray-400 text-xs">{p.notes}</span>}
                </div>
                {canEdit && <button onClick={() => handleDelete(p)} className="text-red-400 hover:text-red-600 text-xs ml-2">✕</button>}
              </div>
            ))
          }
        </div>

        {canEdit && !showAdd && (
          <div className="px-6 pb-4 border-t pt-3 space-y-2">
            <button onClick={() => setShowAdd(true)} className="btn-primary text-sm w-full">+ Add Receipt</button>
            {outstanding > 0 && (
              <button onClick={() => setShowWriteOff(true)} className="w-full text-xs text-orange-600 hover:text-orange-800 font-medium">
                ✂️ Write off remaining {fmt(outstanding)}
              </button>
            )}
          </div>
        )}

        {canEdit && showAdd && (
          <form onSubmit={handleAdd} className="px-6 pb-4 border-t pt-3 space-y-3">
            {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">{error}</div>}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Amount Received (₹) *</label>
                <input type="number" step="0.01" min="0.01" className="input-field" value={amount} onChange={e => setAmount(e.target.value)} required />
              </div>
              <div><label className="label">TDS Deducted (₹){tdsAutoFilled && <span className="ml-1 text-xs text-orange-500">(auto)</span>}</label>
                <input type="number" step="0.01" min="0" className="input-field" value={tdsDeducted} onChange={e => { setTdsDeducted(e.target.value); setTdsAutoFilled(false); }} placeholder="Optional" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Date *</label>
                <input type="date" className="input-field" value={payDate} onChange={e => setPayDate(e.target.value)} required />
              </div>
              <div><label className="label">Mode</label>
                <select className="input-field" value={mode} onChange={e => setMode(e.target.value as PaymentMode)}>
                  {(Object.keys(MODE_LABELS) as PaymentMode[]).map(m => <option key={m} value={m}>{MODE_LABELS[m]}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Reference / UTR / Cheque #</label>
                <input className="input-field" value={reference} onChange={e => setReference(e.target.value)} placeholder="Optional" />
              </div>
              <div><label className="label">Notes</label>
                <input className="input-field" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowAdd(false)} className="btn-secondary text-sm">Cancel</button>
              <button type="submit" className="btn-primary text-sm" disabled={saving}>{saving ? 'Saving…' : 'Save Receipt'}</button>
            </div>
          </form>
        )}
      </div>
      {showWriteOff && (
        <WriteOffModal
          targetType="invoice" targetId={invoiceData.id} targetLabel={invoiceData.invoiceNumber}
          outstanding={Math.max(0, outstanding)} invoiceDate={invoiceData.invoiceDate}
          onClose={() => setShowWriteOff(false)}
          onSaved={async () => { await reload(); onSaved(); }}
        />
      )}
    </div>
  );
};

// ── Opening-Balance Payment Modal ─────────────────────────────────────────────
// Mirrors the invoice PaymentModal (receipt history + Add Receipt with TDS and
// auto-compute) so opening balances are settled the same way.

const OB_MODES = ['BANK', 'CASH', 'CHEQUE', 'UPI', 'OTHER'];

const OpeningBalancePaymentModal: React.FC<{ ob: any; canEdit: boolean; onClose: () => void; onSaved: () => void }> = ({ ob, canEdit, onClose, onSaved }) => {
  const { settings: orgSettings } = useOrgSettings();
  const tdsAutoSuggestPct = Number(orgSettings.receipts.tdsAutoSuggestPct);
  const [obData, setObData] = useState<any>(ob);
  const total = Number(obData.amount);
  const paid = Number(obData.amountPaid);
  const credited = Number(obData.creditedAmount ?? 0);
  const writtenOff = Number(obData.writtenOffAmount ?? 0);
  const outstanding = Math.max(0, total - paid - credited - writtenOff);
  const payments: any[] = obData.payments ?? [];

  const [showAdd, setShowAdd] = useState(payments.length === 0);
  const [showWriteOff, setShowWriteOff] = useState(false);
  const [amount, setAmount] = useState(String(outstanding > 0 ? outstanding.toFixed(2) : ''));
  const [tdsDeducted, setTdsDeducted] = useState('');
  const [tdsAutoFilled, setTdsAutoFilled] = useState(false);
  const [payDate, setPayDate] = useState(today());
  const [mode, setMode] = useState('BANK');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // OB amounts are gross (incl. 18% GST): TDS = TDS% of pre-tax = gross × pct/118.
  // Auto-fill when the entered amount looks like a net-of-TDS full settlement.
  useEffect(() => {
    const entered = parseFloat(amount);
    if (isNaN(entered) || entered <= 0 || outstanding <= 0) return;
    const expectedTds = Math.round(outstanding * tdsAutoSuggestPct / 118);
    if (expectedTds <= 0) return;
    if (Math.round(entered) === Math.round(outstanding) - expectedTds) {
      setTdsDeducted(String(expectedTds)); setTdsAutoFilled(true);
    } else if (tdsAutoFilled) {
      setTdsDeducted(''); setTdsAutoFilled(false);
    }
  }, [amount]); // intentionally watching only amount

  // No single-OB GET endpoint exists — refetch by client and pick this row
  // out, same approach used elsewhere in this file for OB refreshes.
  const reloadOb = async () => {
    try {
      const res = await getOpeningBalances({ clientId: obData.clientId ?? obData.client?.id });
      const fresh = (res.data || []).find((o: any) => o.id === obData.id);
      if (fresh) setObData(fresh);
    } catch { /* keep stale data rather than erroring the modal shut */ }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const res = await recordOpeningBalancePayment(obData.id, {
        paymentDate: payDate,
        amount: parseFloat(amount),
        tdsDeducted: tdsDeducted ? parseFloat(tdsDeducted) : undefined,
        mode,
        reference: reference || undefined,
        notes: notes || undefined,
      });
      setObData(res.data);
      onSaved();
      setShowAdd(false); setAmount(''); setTdsDeducted(''); setReference(''); setNotes('');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to record payment.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Payments — {obData.invoiceNumber} <span className="text-xs font-normal text-amber-600">(Opening Balance)</span></h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>
        <div className="px-6 py-3 bg-gray-50 border-b text-sm text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
          <span>Invoice: <strong>{fmt(total)}</strong></span>
          {credited > 0 && <span>Credited (CN): <strong className="text-purple-600">−{fmt(credited)}</strong></span>}
          {writtenOff > 0 && <span>Written Off: <strong className="text-orange-700">−{fmt(writtenOff)}</strong></span>}
          <span>Received: <strong className="text-green-700">{fmt(paid)}</strong></span>
          <span>Outstanding: <strong className={outstanding > 0 ? 'text-red-600' : 'text-green-600'}>{fmt(outstanding)}</strong></span>
        </div>

        <div className="px-6 py-3 overflow-y-auto flex-1 space-y-2">
          {payments.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No receipts recorded yet.</p>
          ) : payments.map((p: any) => (
            <div key={p.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                {p.receiptNumber && <span className="font-mono text-xs text-gray-500">{p.receiptNumber}</span>}
                <span className="font-semibold text-green-700">{fmt(Number(p.amount))}</span>
                {p.tdsDeducted && Number(p.tdsDeducted) > 0 && (
                  <span className="text-orange-600 text-xs">+TDS {fmt(Number(p.tdsDeducted))}</span>
                )}
                <span className="text-gray-500">{fmtD(p.paymentDate)}</span>
                <span className="text-gray-400 text-xs">{p.mode}</span>
                {p.reference && <span className="text-gray-400 text-xs">· {p.reference}</span>}
                {p.notes && <span className="text-gray-400 text-xs">· {p.notes}</span>}
              </div>
            </div>
          ))}
        </div>

        {canEdit && !showAdd && (
          <div className="px-6 pb-4 border-t pt-3 space-y-2">
            <button onClick={() => setShowAdd(true)} className="btn-primary text-sm w-full">+ Add Receipt</button>
            {outstanding > 0 && (
              <button onClick={() => setShowWriteOff(true)} className="w-full text-xs text-orange-600 hover:text-orange-800 font-medium">
                ✂️ Write off remaining {fmt(outstanding)}
              </button>
            )}
          </div>
        )}

        {canEdit && showAdd && (
          <form onSubmit={handleAdd} className="px-6 pb-4 border-t pt-3 space-y-3">
            {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">{error}</div>}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Amount Received (₹) *</label>
                <input type="number" step="0.01" min="0.01" className="input-field" value={amount} onChange={e => setAmount(e.target.value)} required />
              </div>
              <div><label className="label">TDS Deducted (₹){tdsAutoFilled && <span className="ml-1 text-xs text-orange-500">(auto)</span>}</label>
                <input type="number" step="0.01" min="0" className="input-field" value={tdsDeducted} onChange={e => { setTdsDeducted(e.target.value); setTdsAutoFilled(false); }} placeholder="Optional" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Date *</label>
                <input type="date" className="input-field" value={payDate} onChange={e => setPayDate(e.target.value)} required />
              </div>
              <div><label className="label">Mode</label>
                <select className="input-field" value={mode} onChange={e => setMode(e.target.value)}>
                  {OB_MODES.map(m => <option key={m} value={m}>{m.charAt(0) + m.slice(1).toLowerCase()}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Reference / UTR / Cheque #</label>
                <input className="input-field" value={reference} onChange={e => setReference(e.target.value)} placeholder="Optional" />
              </div>
              <div><label className="label">Notes</label>
                <input className="input-field" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowAdd(false)} className="btn-secondary text-sm">Cancel</button>
              <button type="submit" className="btn-primary text-sm" disabled={saving}>{saving ? 'Saving…' : 'Save Receipt'}</button>
            </div>
          </form>
        )}
      </div>
      {showWriteOff && (
        <WriteOffModal
          targetType="openingBalance" targetId={obData.id} targetLabel={obData.invoiceNumber}
          outstanding={outstanding}
          onClose={() => setShowWriteOff(false)}
          onSaved={async () => { await reloadOb(); onSaved(); }}
        />
      )}
    </div>
  );
};

// ── Write-off Modal (Short & Excess / Bad Debt) ───────────────────────────────
// Shared by both invoice and opening-balance targets. Two distinct intents:
//   • SHORT_EXCESS — small rounding/bank-charge residual; treated as settled.
//   • BAD_DEBT     — a deliberate collections call; flips the invoice/OB to
//     WRITTEN_OFF instead of PAID so it stays visible as not actually realised.
// GST allows a credit note (which recovers the tax) up to 30 Nov following the
// end of the FY the original supply fell in — after that, only a write-off
// (which keeps the GST cost) is possible. Nudge the user toward a CN first
// while that window is still open.
function cnWindowOpen(invoiceDateIso: string): boolean {
  const d = new Date(invoiceDateIso);
  if (isNaN(d.getTime())) return false;
  const fyStartYear = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1; // FY = Apr–Mar
  const deadline = new Date(fyStartYear + 1, 10, 30); // 30 Nov following FY end
  return new Date() <= deadline;
}

const WriteOffModal: React.FC<{
  targetType: 'invoice' | 'openingBalance';
  targetId: number;
  targetLabel: string;
  outstanding: number;
  invoiceDate?: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ targetType, targetId, targetLabel, outstanding, invoiceDate, onClose, onSaved }) => {
  const { settings: orgSettings } = useOrgSettings();
  const shortExcessCap = Number(orgSettings.receivables.maxShortExcessRupees ?? 100);
  const [type, setType] = useState<'SHORT_EXCESS' | 'BAD_DEBT'>(outstanding <= shortExcessCap ? 'SHORT_EXCESS' : 'BAD_DEBT');
  const [amount, setAmount] = useState(String(outstanding > 0 ? outstanding.toFixed(2) : ''));
  const [adjustmentDate, setAdjustmentDate] = useState(today());
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const showCnNudge = targetType === 'invoice' && invoiceDate && cnWindowOpen(invoiceDate);
  const overCap = type === 'SHORT_EXCESS' && Number(amount) > shortExcessCap;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!(Number(amount) > 0)) { setError('Amount must be greater than zero'); return; }
    if (overCap) { setError(`Short & Excess is capped at ${fmt(shortExcessCap)} — switch to Bad Debt for larger amounts`); return; }
    setSaving(true);
    try {
      await createAdjustment({
        targetType, targetId, type, amount: Number(amount),
        adjustmentDate, reason: reason || undefined,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to write off.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Write Off — {targetLabel}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-3">
          <p className="text-sm text-gray-500">Outstanding: <strong className="text-red-600">{fmt(outstanding)}</strong></p>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">{error}</div>}

          {showCnNudge && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-3 py-2 text-xs">
              💡 The credit-note window for this invoice is still open (until 30-Nov). Issuing a credit note instead reverses the GST too — a write-off does not. Consider a Credit Note first unless this is a genuine bad debt.
            </div>
          )}

          <div>
            <label className="label">Type *</label>
            <div className="flex gap-3">
              <label className={`flex-1 flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer text-sm ${type === 'SHORT_EXCESS' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                <input type="radio" checked={type === 'SHORT_EXCESS'} onChange={() => setType('SHORT_EXCESS')} />
                Short &amp; Excess
              </label>
              <label className={`flex-1 flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer text-sm ${type === 'BAD_DEBT' ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}>
                <input type="radio" checked={type === 'BAD_DEBT'} onChange={() => setType('BAD_DEBT')} />
                Bad Debt
              </label>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {type === 'SHORT_EXCESS'
                ? `Small rounding/bank-charge residual — capped at ${fmt(shortExcessCap)}. Invoice is treated as settled.`
                : 'A deliberate collections decision (dispute, insolvency, etc.) — requires Partner/Admin approval. Invoice is flagged Written Off, not Paid.'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Amount (₹) *</label>
              <input type="number" step="0.01" min="0.01" max={outstanding} className="input-field"
                value={amount} onChange={e => setAmount(e.target.value)} required />
              {overCap && <p className="text-xs text-red-500 mt-1">Exceeds Short &amp; Excess cap of {fmt(shortExcessCap)}</p>}
            </div>
            <div>
              <label className="label">Date *</label>
              <input type="date" className="input-field" value={adjustmentDate} onChange={e => setAdjustmentDate(e.target.value)} required />
            </div>
          </div>

          <div>
            <label className="label">Reason {type === 'BAD_DEBT' ? '*' : '(optional)'}</label>
            <textarea className="input-field" rows={2} value={reason} onChange={e => setReason(e.target.value)}
              placeholder={type === 'BAD_DEBT' ? 'Why this is being written off as a bad debt…' : 'e.g. Bank charges, rounding difference'}
              required={type === 'BAD_DEBT'} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button type="submit" className="btn-primary text-sm" disabled={saving}>{saving ? 'Saving…' : 'Write Off'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Invoice Edit Modal ────────────────────────────────────────────────────────

const InvoiceEditModal: React.FC<{
  invoice: Invoice;
  billingEntities: BillingEntityFull[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ invoice, billingEntities, onClose, onSaved }) => {
  const { settings: orgSettings } = useOrgSettings();
  const [billingEntityId, setBillingEntityId] = useState(String(invoice.billingEntity?.id ?? ''));
  const [clientGstinId, setClientGstinId] = useState(String(invoice.clientGstinId ?? ''));
  const [clientGstins, setClientGstins] = useState<ClientGstin[]>([]);
  const [signatoryId, setSignatoryId] = useState<string>(String(invoice.signatory?.id ?? ''));
  const [taxType, setTaxType] = useState<TaxType>(invoice.taxType);
  const [invoiceDate, setInvoiceDate] = useState(invoice.invoiceDate.slice(0, 10));
  const [dueDate, setDueDate] = useState(invoice.dueDate?.slice(0, 10) ?? '');
  const [hsnSacCode, setHsnSacCode] = useState(invoice.hsnSacCode ?? '');
  const [notes, setNotes] = useState(invoice.notes ?? '');
  const [template] = useState(invoice.template ?? 1);
  const [rcmApplicable, setRcmApplicable] = useState(!!(invoice as any).rcmApplicable);
  // Signatories available on the chosen Billing Entity. Mirrors the Create
  // modal's `cmSignatories` derivation.
  const editSignatories = useMemo(
    () => billingEntities.find((b) => String(b.id) === billingEntityId)?.signatories ?? [],
    [billingEntities, billingEntityId],
  );
  // When the user switches billing entity, the current signatory may no
  // longer belong to it — auto-pick the default (or first) signatory of the
  // new entity so the dropdown isn't silently invalid.
  useEffect(() => {
    if (!editSignatories.length) { setSignatoryId(''); return; }
    if (signatoryId && editSignatories.some((s) => String(s.id) === signatoryId)) return;
    const def = editSignatories.find((s) => (s as any).isDefault) ?? editSignatories[0];
    setSignatoryId(def ? String(def.id) : '');
    // eslint-disable-next-line
  }, [billingEntityId, editSignatories]);
  const [lineItems, setLineItems] = useState<WizardLine[]>(
    (invoice.lineItems && invoice.lineItems.length > 0)
      ? invoice.lineItems.map(li => ({ slNo: li.slNo, description: li.description, hsnSac: li.hsnSac ?? '', quantity: Number(li.quantity), rate: Number(li.rate), unit: li.unit ?? '', amount: Number(li.amount), taskId: li.taskId, certId: li.certificateId, isOpe: !!li.isOpe, expenseId: li.expenseId, groupKey: 1 }))
      : [mkLine(1, { description: invoice.task?.taskName ?? '', rate: Number(invoice.amount), amount: Number(invoice.amount) })]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const clientId = invoice.client?.id;
    if (!clientId) return;
    getClientGstins(clientId).then(r => setClientGstins(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const be = billingEntities.find(b => String(b.id) === billingEntityId);
    const cg = clientGstins.find(g => String(g.id) === clientGstinId) ?? invoice.clientGstin;
    if (be && cg) setTaxType(autoTaxType(be, cg));
  }, [billingEntityId, clientGstinId, clientGstins]);

  const updateLine = (idx: number, field: keyof WizardLine, val: any) => {
    setLineItems(prev => {
      const next = [...prev];
      (next[idx] as any)[field] = val;
      if (field === 'quantity' || field === 'rate')
        next[idx].amount = parseFloat((Number(next[idx].quantity) * Number(next[idx].rate)).toFixed(2));
      return next;
    });
  };

  const cgstR = Number(orgSettings.invoicing.defaultCgstRate);
  const sgstR = Number(orgSettings.invoicing.defaultSgstRate);
  const igstR = Number(orgSettings.invoicing.defaultIgstRate);
  const taxableAmt = lineItems.reduce((s, l) => s + Number(l.amount), 0);
  const cgst = taxType === 'CGST_SGST' ? taxableAmt * (cgstR / 100) : 0;
  const sgst = taxType === 'CGST_SGST' ? taxableAmt * (sgstR / 100) : 0;
  const igst = taxType === 'IGST' ? taxableAmt * (igstR / 100) : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (lineItems.some(l => !l.description.trim())) { setError('All line items need a description.'); return; }
    setSaving(true); setError('');
    try {
      await updateInvoice(invoice.id, {
        billingEntityId: billingEntityId ? Number(billingEntityId) : undefined,
        clientGstinId: clientGstinId ? Number(clientGstinId) : undefined,
        // Send signatoryId on every save (null when blank) so the server's
        // "set to null if blank, leave unchanged if undefined" branch
        // correctly persists user changes — including explicitly clearing it.
        signatoryId: signatoryId ? Number(signatoryId) : null,
        taxType, invoiceDate,
        dueDate: dueDate || undefined,
        hsnSacCode: hsnSacCode || undefined,
        notes: notes || undefined,
        template,
        rcmApplicable,
        amount: taxableAmt,
        lineItems: lineItems.map(li => ({
          slNo: li.slNo, description: li.description,
          hsnSac: li.hsnSac || hsnSacCode || undefined,
          quantity: li.quantity, rate: li.rate,
          amount: li.amount,
          // Preserve task / certificate / expense links so editing doesn't
          // flip the underlying tasks/certificates back to "unbilled", or
          // drop an OPE line's link to its Expense (which would make that
          // expense look unbilled again and risk it being invoiced twice).
          taskId: li.taskId, certId: li.certId, isOpe: li.isOpe, expenseId: li.expenseId,
        })),
      });
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update invoice.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Edit Invoice — {invoice.invoiceNumber}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{invoice.client?.clientName}{invoice.task?.taskName ? ` · ${invoice.task.taskName}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">{error}</div>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Billing Entity</label>
              <select className="input-field" value={billingEntityId} onChange={e => setBillingEntityId(e.target.value)}>
                <option value="">— Select —</option>
                {billingEntities.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Client GSTIN</label>
              <select className="input-field" value={clientGstinId} onChange={e => setClientGstinId(e.target.value)}>
                <option value="">— Select —</option>
                {clientGstins.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.label ? `${g.label}: ` : ''}{g.gstType === 'REGISTERED' ? g.gstin : g.gstType === 'SEZ' && g.gstin ? `${g.gstin} (SEZ)` : g.gstType}{g.isPrimary ? ' ★' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Signatory — editable on DRAFT (and any other status the server
              accepts the update for). Disabled when the billing entity has
              no signatories configured. */}
          <div>
            <label className="label">Authorised Signatory</label>
            <select
              className="input-field max-w-md"
              value={signatoryId}
              onChange={(e) => setSignatoryId(e.target.value)}
              disabled={editSignatories.length === 0}
            >
              <option value="">— None —</option>
              {editSignatories.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}{s.designation ? ` — ${s.designation}` : ''}{(s as any).isDefault ? ' ★' : ''}
                </option>
              ))}
            </select>
            {editSignatories.length === 0 && (
              <p className="text-[11px] text-amber-600 mt-1">
                No signatories defined for this billing entity. Add one in Masters → Billing Entities.
              </p>
            )}
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="label">Tax Type</label>
              <select className="input-field" value={taxType} onChange={e => setTaxType(e.target.value as TaxType)}>
                <option value="NONE">No Tax</option>
                <option value="CGST_SGST">CGST + SGST</option>
                <option value="IGST">IGST</option>
              </select>
            </div>
            <div>
              <label className="label">Invoice Date *</label>
              <input type="date" className="input-field" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} required />
            </div>
            <div>
              <label className="label">Due Date</label>
              <input type="date" className="input-field" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
            <div>
              <label className="label">HSN / SAC</label>
              <input className="input-field font-mono" value={hsnSacCode} onChange={e => setHsnSacCode(e.target.value)} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label mb-0">Line Items</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setLineItems(p => [...p, mkLine(p.length + 1, { hsnSac: hsnSacCode })])} className="text-xs px-2 py-1 border border-blue-300 text-blue-600 rounded hover:bg-blue-50">+ Service Line</button>
                <button type="button" onClick={() => setLineItems(p => [...p, mkLine(p.length + 1, { isOpe: true, hsnSac: hsnSacCode })])} className="text-xs px-2 py-1 border border-amber-300 text-amber-700 rounded hover:bg-amber-50">+ OPE Line</button>
              </div>
            </div>
            <div className="overflow-x-auto border border-gray-200 rounded">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-2 py-1.5 text-left font-medium text-gray-600 w-6">#</th>
                    <th className="px-2 py-1.5 text-left font-medium text-gray-600">Description *</th>
                    <th className="px-2 py-1.5 text-left font-medium text-gray-600 w-20">HSN/SAC</th>
                    <th className="px-2 py-1.5 text-center font-medium text-gray-600 w-14">Qty</th>
                    <th className="px-2 py-1.5 text-left font-medium text-gray-600 w-24">Rate (₹)</th>
                    <th className="px-2 py-1.5 text-right font-medium text-gray-600 w-24">Amount</th>
                    <th className="w-5"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li, idx) => (
                    <tr key={idx} className={`border-t border-gray-100 ${li.isOpe ? 'bg-amber-50/30' : ''}`}>
                      <td className="px-2 py-1 text-gray-400">
                        {li.slNo}
                        {li.isOpe && <span className="ml-1 text-[9px] font-semibold text-amber-700">OPE</span>}
                      </td>
                      <td className="px-2 py-1 min-w-[160px]"><textarea className="w-full border-0 outline-none text-xs resize-y leading-snug" rows={2} value={li.description} onChange={e => updateLine(idx, 'description', e.target.value)} required /></td>
                      <td className="px-2 py-1"><input className="w-full border-0 outline-none text-xs font-mono" value={li.hsnSac} onChange={e => updateLine(idx, 'hsnSac', e.target.value)} placeholder={hsnSacCode} /></td>
                      <td className="px-2 py-1"><input type="number" className="w-full border-0 outline-none text-xs text-center" value={li.quantity} min={0} step="0.001" onChange={e => updateLine(idx, 'quantity', parseFloat(e.target.value) || 0)} /></td>
                      <td className="px-2 py-1"><input type="number" className="w-full border-0 outline-none text-xs" value={li.rate} min={0} step="0.01" onChange={e => updateLine(idx, 'rate', parseFloat(e.target.value) || 0)} /></td>
                      <td className="px-2 py-1 text-right font-medium">{fmt(li.amount)}</td>
                      <td className="px-2 py-1">{lineItems.length > 1 && <button type="button" onClick={() => setLineItems(p => p.filter((_, i) => i !== idx).map((l, i) => ({ ...l, slNo: i + 1 })))} className="text-red-400 hover:text-red-600 leading-none">×</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-gray-50 rounded p-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-gray-500">Taxable Amount</span><span className="font-medium">{fmt(taxableAmt)}</span></div>
            {taxType === 'CGST_SGST' && <>
              <div className="flex justify-between text-gray-500"><span>CGST @ {cgstR}%</span><span>{fmt(cgst)}</span></div>
              <div className="flex justify-between text-gray-500"><span>SGST/UTGST @ {sgstR}%</span><span>{fmt(sgst)}</span></div>
            </>}
            {taxType === 'IGST' && <div className="flex justify-between text-gray-500"><span>IGST @ {igstR}%</span><span>{fmt(igst)}</span></div>}
            <div className="flex justify-between font-bold text-base border-t border-gray-200 pt-1"><span>Total</span><span className="text-blue-700">{fmt(taxableAmt + cgst + sgst + igst)}</span></div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Reverse Charge (RCM)</label>
              <label className="flex items-center gap-2 p-2 border border-gray-300 rounded cursor-pointer hover:bg-gray-50">
                <input type="checkbox" checked={rcmApplicable} onChange={e => setRcmApplicable(e.target.checked)}
                  className="w-4 h-4 accent-blue-600" />
                <span className="text-sm text-gray-700">Tax payable under Reverse Charge</span>
              </label>
            </div>
            <div>
              <label className="label">Notes</label>
              <textarea className="input-field" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Invoices Tab ──────────────────────────────────────────────────────────────

const InvoicesTab: React.FC<{ tasks: TaskInfo[]; billingEntities: BillingEntityFull[]; isAdmin: boolean; isHR?: boolean; isPartner?: boolean; defaultTaskId?: number; defaultCertificateId?: number; defaultCertificateIds?: number[]; defaultLitigationId?: number; defaultLitigationStepId?: number; onCreateCN?: (inv: Invoice) => void; docType: InvoiceDocType; onDocTypeChange: (v: InvoiceDocType) => void }> = ({ tasks, billingEntities, isAdmin, isHR, isPartner, defaultTaskId, defaultCertificateId, defaultCertificateIds, defaultLitigationId, defaultLitigationStepId, onCreateCN, docType, onDocTypeChange }) => {
  const navigate = useNavigate();
  // Staff may raise their own invoices, but the firm-wide billing sweep and
  // the collections actions belong to billing managers.
  const canSeeBilling = useCanSeeBilling();
  // Both bulk exports are firm-level accounting extracts — HR, partners and
  // Admin only. Mirrors requirePartnerHROrAdmin on the two endpoints.
  const canExport = useIsPartnerHROrAdmin();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [invSearch, setInvSearch] = useState('');
  const [invDateFrom, setInvDateFrom] = useState('');
  const [invDateTo, setInvDateTo] = useState('');
  const [invSortField, setInvSortField] = useState<'invoiceNumber' | 'client' | 'invoiceDate' | 'totalAmount'>('invoiceDate');
  const [invSortDir, setInvSortDir] = useState<'asc' | 'desc'>('desc');
  const [invPage, setInvPage] = useState(1);
  const [invPageSize, setInvPageSize] = useState(25);
  // Server-side pagination: `invoices` holds only the current page; `invTotal`
  // counts the whole filtered set; `summary` powers the four cards (computed
  // server-side over every matching invoice, not just the loaded page).
  const [invTotal, setInvTotal] = useState(0);
  const [debouncedInvSearch, setDebouncedInvSearch] = useState('');
  const [summary, setSummary] = useState({ totalInvoiced: 0, totalCollected: 0, outstanding: 0, overdueCount: 0 });
  // Selection can span pages, so keep the selected rows themselves — bulk
  // actions need status/client fields after the page they came from unloads.
  const [selectedRows, setSelectedRows] = useState<Map<number, Invoice>>(new Map());
  // Auto-open the create wizard whenever any pre-selection arrived in the
  // URL — taskId, single certificateId, or one-or-more certificateIds. The
  // earlier >= 2 threshold was scoped to the multi-cert flow and silently
  // swallowed the single-cert "+ Invoice" link from the Certificates page,
  // which is the common path for task managers raising a cert invoice.
  const [showCreate, setShowCreate] = useState(!!(defaultTaskId || defaultCertificateId || defaultLitigationId || defaultLitigationStepId || (defaultCertificateIds && defaultCertificateIds.length >= 1)));
  const [showWizard, setShowWizard] = useState(false);
  const [editTarget, setEditTarget] = useState<Invoice | null>(null);
  // Derived view of the selection map — keeps the checkbox render sites simple.
  const selectedIds = useMemo(() => new Set(selectedRows.keys()), [selectedRows]);
  const [paymentTarget, setPaymentTarget] = useState<Invoice | null>(null);
  const [printTarget, setPrintTarget] = useState<Invoice | null>(null);
  const [sendEmailTarget, setSendEmailTarget] = useState<Invoice | null>(null);
  // Bulk-send modal target: the snapshot of invoices being emailed in one
  // batch. Set when the user clicks "📧 Send Email" in the bulk-action bar.
  const [batchEmailTarget, setBatchEmailTarget] = useState<Invoice[] | null>(null);
  const [pdfDownloading, setPdfDownloading] = useState<number | null>(null);

  const handleQuickPDF = async (inv: Invoice) => {
    setPdfDownloading(inv.id);
    try { await generateInvoicePDF(inv, inv.template ?? 1); }
    catch { alert('PDF failed. Try the Print preview instead.'); }
    finally { setPdfDownloading(null); }
  };
  const [exporting, setExporting] = useState(false);

  // Debounce search so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedInvSearch(invSearch), 400);
    return () => clearTimeout(t);
  }, [invSearch]);

  // Shared by load() and loadSummary() so the four summary cards can never
  // drift from what the filtered/searched list is actually showing.
  const buildInvoiceParams = useCallback(() => {
    const params: any = {};
    if (statusFilter) params.status = statusFilter;
    else params.excludeCancelled = '1'; // the Invoices tab hides cancelled unless explicitly filtered
    if (debouncedInvSearch) params.search = debouncedInvSearch;
    if (invDateFrom) params.dateFrom = invDateFrom;
    if (invDateTo) params.dateTo = invDateTo;
    return params;
  }, [statusFilter, debouncedInvSearch, invDateFrom, invDateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...buildInvoiceParams(), page: invPage, pageSize: invPageSize, sortField: invSortField, sortDir: invSortDir };
      const res = await getInvoices(params);
      const rows = res.data.rows ?? [];
      const rowTotal = res.data.total ?? 0;
      setInvoices(rows);
      setInvTotal(rowTotal);
      // A mutation (e.g. bulk-delete) can shrink the result set below the
      // current page's offset — retreat to the last valid page instead of
      // showing an empty table while rows still exist on an earlier page.
      const lastPage = Math.max(1, Math.ceil(rowTotal / invPageSize));
      if (invPage > lastPage) setInvPage(lastPage);
    } catch { setInvoices([]); setInvTotal(0); } finally { setLoading(false); }
  }, [buildInvoiceParams, invSortField, invSortDir, invPage, invPageSize]);

  useEffect(() => { load(); }, [load]);

  const loadSummary = useCallback(async () => {
    try {
      const res = await getInvoiceSummary(buildInvoiceParams());
      setSummary(res.data);
    } catch { /* keep the last known numbers */ }
  }, [buildInvoiceParams]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // After any mutation, refresh both the page rows and the summary cards.
  const reload = useCallback(() => { load(); loadSummary(); }, [load, loadSummary]);

  const handleMarkSent = async (inv: Invoice) => {
    if (!window.confirm(`Mark invoice ${inv.invoiceNumber} as Sent?`)) return;
    try { await updateInvoice(inv.id, { status: 'SENT' }); reload(); } catch (err: any) { alert(err?.response?.data?.message || 'Failed.'); }
  };
  const handleCancel = async (inv: Invoice) => {
    if (!window.confirm(`Cancel invoice ${inv.invoiceNumber}?`)) return;
    try { await cancelInvoice(inv.id); reload(); } catch (err: any) { alert(err?.response?.data?.message || 'Failed.'); }
  };
  const handleDelete = async (inv: Invoice) => {
    const isPaid = ['PAID', 'PARTIALLY_PAID'].includes(inv.status);
    const msg = isPaid
      ? `Delete ${STATUS_LABEL[inv.status]} invoice ${inv.invoiceNumber}? Recorded payments will also be deleted. This cannot be undone.`
      : `Delete invoice ${inv.invoiceNumber}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try { await deleteInvoice(inv.id); reload(); } catch (err: any) { alert(err?.response?.data?.message || 'Failed.'); }
  };
  const handleMarkUnpaid = async (inv: Invoice) => {
    if (!window.confirm(`Mark ${inv.invoiceNumber} as unpaid? Recorded payments will be cleared and status will revert to Sent.`)) return;
    try { await markInvoiceUnpaid(inv.id); reload(); } catch (err: any) { alert(err?.response?.data?.message || 'Failed.'); }
  };

  // Selection spans pages: rows are snapshotted into selectedRows as they're
  // ticked, because with server-side pagination a row from another page is no
  // longer in `invoices` when a bulk action finally runs.
  const toggleSelect = (inv: Invoice) => setSelectedRows(prev => {
    const next = new Map(prev);
    next.has(inv.id) ? next.delete(inv.id) : next.set(inv.id, inv);
    return next;
  });
  const toggleSelectAll = () => {
    const allOnPageSelected = pagedInvoices.length > 0 && pagedInvoices.every(i => selectedRows.has(i.id));
    setSelectedRows(prev => {
      const next = new Map(prev);
      pagedInvoices.forEach(i => (allOnPageSelected ? next.delete(i.id) : next.set(i.id, i)));
      return next;
    });
  };

  const handleBulkMarkSent = async () => {
    const targets = Array.from(selectedRows.values()).filter(i => i.status === 'DRAFT');
    if (!targets.length) return alert('No DRAFT invoices selected.');
    if (!window.confirm(`Mark ${targets.length} invoice(s) as Sent?`)) return;
    await Promise.allSettled(targets.map(i => updateInvoice(i.id, { status: 'SENT' })));
    setSelectedRows(new Map());
    reload();
  };
  const handleBulkCancel = async () => {
    const targets = Array.from(selectedRows.values()).filter(i => ['DRAFT','SENT','OVERDUE'].includes(i.status));
    if (!targets.length) return alert('No cancellable invoices selected.');
    if (!window.confirm(`Cancel ${targets.length} invoice(s)?`)) return;
    await Promise.allSettled(targets.map(i => cancelInvoice(i.id)));
    setSelectedRows(new Map());
    reload();
  };
  const handleBulkMarkUnpaid = async () => {
    const targets = Array.from(selectedRows.values()).filter(i => ['PAID','PARTIALLY_PAID'].includes(i.status));
    if (!targets.length) return alert('No paid invoices selected.');
    if (!window.confirm(`Mark ${targets.length} invoice(s) as unpaid? Recorded payments on these invoices will be cleared.`)) return;
    await Promise.allSettled(targets.map(i => markInvoiceUnpaid(i.id)));
    setSelectedRows(new Map());
    reload();
  };
  const handleBulkDelete = async () => {
    const targets = Array.from(selectedRows.values());
    if (!targets.length) return alert('No invoices selected.');
    const paidCount = targets.filter(i => ['PAID','PARTIALLY_PAID'].includes(i.status)).length;
    const msg = paidCount > 0
      ? `Delete ${targets.length} invoice(s)? ${paidCount} are PAID/Partially paid — recorded payments will also be deleted. This cannot be undone.`
      : `Delete ${targets.length} invoice(s)? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    await Promise.allSettled(targets.map(i => deleteInvoice(i.id)));
    setSelectedRows(new Map());
    reload();
  };
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportInvoices();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `invoices-${format(new Date(), 'yyyy-MM-dd')}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('Export failed.'); } finally { setExporting(false); }
  };

  // Card totals come from /invoices/summary — the loaded page alone can't
  // reproduce firm-wide numbers.
  const { totalInvoiced, totalCollected, outstanding, overdueCount } = summary;

  const handleInvSort = (f: typeof invSortField) => {
    if (invSortField === f) setInvSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setInvSortField(f); setInvSortDir('asc'); }
    setInvPage(1);
  };
  const InvSortIcon = ({ f }: { f: typeof invSortField }) =>
    invSortField !== f ? <span className="ml-1 text-gray-300">↕</span>
    : <span className="ml-1 text-blue-600">{invSortDir === 'asc' ? '↑' : '↓'}</span>;

  // Filtering, sorting and paging all happen server-side — `invoices` is
  // already the current page of the filtered, sorted result.
  const totalInvPages = Math.max(1, Math.ceil(invTotal / invPageSize));
  const pagedInvoices = invoices;

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Total Invoiced" value={fmt(totalInvoiced)} />
        <SummaryCard label="Total Collected" value={fmt(totalCollected)} color="text-green-700" />
        <SummaryCard label="Outstanding" value={fmt(outstanding)} color="text-orange-600" />
        <SummaryCard label="Overdue" value={String(overdueCount)} color={overdueCount > 0 ? 'text-red-600' : 'text-gray-800'} sub={overdueCount > 0 ? 'Needs attention' : 'None overdue'} />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input className="input-field w-52" placeholder="Search invoice #, client, task…" value={invSearch}
          onChange={e => { setInvSearch(e.target.value); setInvPage(1); }} />
        <DocTypeSelect value={docType} onChange={onDocTypeChange} />
        <select className="input-field w-40" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setInvPage(1); }}>
          <option value="">All Statuses</option>
          {(['DRAFT','SENT','PAID','PARTIALLY_PAID','OVERDUE','CANCELLED'] as InvoiceStatus[]).map(s => (
            <option key={s} value={s}>{STATUS_LABEL[s]}</option>
          ))}
        </select>
        <div className="ml-auto flex flex-wrap justify-end items-center gap-2">
          {isAdmin && <button onClick={() => navigate('/org-settings?tab=invoicing')} className="btn-secondary text-sm">⚙ Numbering</button>}
          {canExport && (
            <>
              <button onClick={handleExport} className="btn-secondary text-sm" disabled={exporting}>{exporting ? 'Exporting…' : '↓ Export'}</button>
              <RegisterDownload label="Sales Register" fileSlug="sales-register"
                title="Detailed sales register (CSV) for the accountant — pick any date range"
                fetchRegister={async ({ from, to, includeCancelled }) => {
                  const params: any = { dateFrom: from, dateTo: to };
                  if (!includeCancelled) params.excludeCancelled = '1';
                  return new Blob([(await exportSalesRegister(params)).data], { type: 'text/csv' });
                }} />
            </>
          )}
          {canSeeBilling && <button onClick={() => setShowWizard(true)} className="btn-secondary text-sm bg-indigo-100 text-indigo-700 hover:bg-indigo-200 border-indigo-200">📅 Monthly Wizard</button>}
          <button onClick={() => setShowCreate(true)} className="btn-primary text-sm">+ New Invoice</button>
        </div>
      </div>
      {showWizard && (
        <MonthlyInvoiceWizard
          onClose={() => setShowWizard(false)}
          onComplete={() => { reload(); }}
        />
      )}

      {selectedIds.size > 0 && (() => {
        // Same-client guard for the batch email action. We only enable the
        // button when every selected invoice belongs to one client AND the
        // selection contains at least one DRAFT / SENT / OVERDUE / PARTIAL
        // — cancelled invoices can't be emailed.
        const selectedInvoices = Array.from(selectedRows.values());
        const distinctClients = new Set(selectedInvoices.map((i) => i.client?.id).filter(Boolean));
        const sendableInvoices = selectedInvoices.filter((i) => i.status !== 'CANCELLED');
        const sendableSameClient = distinctClients.size === 1 && sendableInvoices.length > 0;
        const sendDisabledHint =
          distinctClients.size > 1
            ? 'Tick invoices of one client at a time'
            : sendableInvoices.length === 0
              ? 'No sendable invoices in selection (cancelled invoices skipped)'
              : '';
        return (
        <div className="flex items-center gap-3 mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm flex-wrap">
          <span className="text-blue-700 font-medium">{selectedIds.size} selected</span>
          <button
            onClick={() => setBatchEmailTarget(sendableInvoices)}
            disabled={!sendableSameClient}
            title={sendableSameClient
              ? `Send ${sendableInvoices.length} invoice${sendableInvoices.length === 1 ? '' : 's'} in one email`
              : sendDisabledHint}
            className={`px-3 py-1 rounded font-medium ${sendableSameClient ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
          >
            📧 Send Email
          </button>
          {!sendableSameClient && distinctClients.size > 1 && (
            <span className="text-[11px] text-amber-700">⚠ {distinctClients.size} clients selected — pick one client at a time to send by email</span>
          )}
          {(isAdmin || isHR || isPartner) && (
            <button onClick={handleBulkMarkSent} className="px-3 py-1 rounded bg-blue-600 text-white font-medium hover:bg-blue-700">Mark Sent</button>
          )}
          {(isAdmin || isHR || isPartner) && (
            <button onClick={handleBulkCancel} className="px-3 py-1 rounded bg-orange-500 text-white font-medium hover:bg-orange-600">Cancel</button>
          )}
          {(isAdmin || isPartner) && (
            <button onClick={handleBulkMarkUnpaid} className="px-3 py-1 rounded bg-amber-600 text-white font-medium hover:bg-amber-700" title="Revert PAID / Partially paid invoices to Sent">
              Mark Unpaid
            </button>
          )}
          {isAdmin && (
            <button onClick={handleBulkDelete} className="px-3 py-1 rounded bg-red-600 text-white font-medium hover:bg-red-700" title="Delete selected invoices — admin can delete any status">
              Delete
            </button>
          )}
          <button onClick={() => setSelectedRows(new Map())} className="ml-auto text-blue-500 hover:text-blue-700">✕ Clear</button>
        </div>
        );
      })()}

      <div className="card overflow-x-auto p-0">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="bg-gray-50 px-2 py-2 w-8 border-b border-gray-100">
                <input type="checkbox" className="rounded"
                  checked={pagedInvoices.length > 0 && pagedInvoices.every(i => selectedIds.has(i.id))}
                  onChange={toggleSelectAll} />
              </th>
              <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap cursor-pointer select-none" onClick={() => handleInvSort('invoiceNumber')}>Inv# <InvSortIcon f="invoiceNumber" /></th>
              <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 cursor-pointer select-none" onClick={() => handleInvSort('client')}>Client <InvSortIcon f="client" /></th>
              <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">Task</th>
              <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Taxable</th>
              <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Tax</th>
              <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap cursor-pointer select-none" onClick={() => handleInvSort('totalAmount')}>Total <InvSortIcon f="totalAmount" /></th>
              <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">
                <DateRangeFilter
                  label="Date"
                  fromValue={invDateFrom}
                  toValue={invDateTo}
                  onChange={(f, t) => { setInvDateFrom(f); setInvDateTo(t); setInvPage(1); }}
                  sortable
                  onSort={() => handleInvSort('invoiceDate')}
                  sortIcon={<InvSortIcon f="invoiceDate" />}
                />
              </th>
              <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">Status</th>
              <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">TDS</th>
              <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Paid</th>
              <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Outstanding</th>
              <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={13} className="px-2 py-10 text-center text-gray-400 border-b border-gray-100">Loading…</td></tr>
            ) : pagedInvoices.length === 0 ? (
              <tr><td colSpan={13} className="px-2 py-10 text-center text-gray-400 border-b border-gray-100">No invoices found.</td></tr>
            ) : pagedInvoices.map(inv => {
              const taxAmt = Number(inv.totalAmount ?? inv.amount) - Number(inv.amount);
              const taxLabel = inv.taxType === 'CGST_SGST' ? 'CGST+SGST' : inv.taxType === 'IGST' ? 'IGST' : 'No tax';
              // Derive multi-task display from actual line-item content, not the
              // stored isMultiTask flag (which the new-invoice form sets true the
              // moment a multi-task client is picked, even if only one task ends
              // up on the invoice). Multiple certs of the same task → still one
              // task → show task name.
              const liArr = ((inv as any).lineItems ?? []) as any[];
              const liTaskIds = Array.from(new Set(
                liArr.filter(li => !li.isOpe && li.taskId).map(li => li.taskId as number)
              ));
              const showAsMulti = liTaskIds.length > 1;
              return (
              <tr key={inv.id} className={`border-b border-gray-50 hover:bg-gray-50 ${selectedIds.has(inv.id) ? 'bg-blue-50' : ''} ${inv.status === 'CANCELLED' ? 'line-through text-gray-400' : ''}`}>
                <td className="px-2 py-2 w-8 border-b border-gray-100">
                  <input type="checkbox" className="rounded" checked={selectedIds.has(inv.id)} onChange={() => toggleSelect(inv)} />
                </td>
                <td className="px-2 py-2 font-mono font-medium text-blue-700 border-b border-gray-100 whitespace-nowrap">{inv.invoiceNumber}</td>
                <td className="px-2 py-2 text-gray-700 border-b border-gray-100 max-w-[140px] truncate" title={inv.serviceClient ? `${inv.client?.clientName || ''} — for ${inv.serviceClient.clientName}` : (inv.client?.clientName || '')}>
                  {inv.client?.clientName ?? '—'}
                  {inv.serviceClient && <div className="text-[10px] text-indigo-600 truncate">for {inv.serviceClient.clientName}</div>}
                </td>
                <td className="px-2 py-2 text-gray-700 border-b border-gray-100 max-w-[160px]">
                  {showAsMulti ? (
                    <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded">Multi-task</span>
                  ) : inv.task ? (
                    <div className="truncate" title={`${inv.task.taskId} — ${inv.task.taskName}`}>
                      <span className="text-xs text-gray-400">{inv.task.taskId}</span> <span className="text-xs">{inv.task.taskName}</span>
                    </div>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-2 py-2 text-right border-b border-gray-100 whitespace-nowrap">{fmt(Number(inv.amount))}</td>
                <td className="px-2 py-2 text-right border-b border-gray-100 whitespace-nowrap" title={taxLabel}>
                  {taxAmt > 0 ? <span>{fmt(taxAmt)}</span> : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-2 py-2 text-right font-semibold border-b border-gray-100 whitespace-nowrap">{fmt(Number(inv.totalAmount ?? inv.amount))}</td>
                <td className="px-2 py-2 text-gray-600 border-b border-gray-100 whitespace-nowrap">{fmtD(inv.invoiceDate)}</td>
                <td className="px-2 py-2 border-b border-gray-100"><StatusBadge status={inv.status} /></td>
                <td className="px-2 py-2 text-right border-b border-gray-100 whitespace-nowrap">
                  {(inv.tdsAmount ?? 0) > 0
                    ? <span className="text-orange-600 font-medium" title={`${inv.tdsSection ?? ''} @ ${inv.tdsRate}%`}>{fmt(inv.tdsAmount!)}</span>
                    : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-2 py-2 text-right border-b border-gray-100 whitespace-nowrap">
                  {(inv.paymentAmount ?? 0) > 0 ? <span className="text-green-600 font-medium">{fmt(inv.paymentAmount!)}</span> : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-2 py-2 text-right border-b border-gray-100 whitespace-nowrap">
                  {['SENT','OVERDUE','PARTIALLY_PAID'].includes(inv.status) ? (() => {
                    const o = outstandingOf(inv);
                    return <span className={o > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>{fmt(o)}</span>;
                  })() : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-2 py-2 border-b border-gray-100 whitespace-nowrap">
                  <div className="flex items-center gap-0.5">
                    {['DRAFT', 'SENT', 'OVERDUE'].includes(inv.status) && (
                      <button onClick={() => setEditTarget(inv)} title="Edit" aria-label="Edit"
                        className="p-1 rounded hover:bg-indigo-50 text-indigo-600">✏</button>
                    )}
                    {canSeeBilling && ((inv.payments?.length ?? 0) > 0 || ['SENT', 'PARTIALLY_PAID', 'OVERDUE'].includes(inv.status)) && (
                      <button onClick={() => setPaymentTarget(inv)}
                        title={(inv.payments?.length ?? 0) > 0 ? 'View / add receipts' : 'Record payment'}
                        aria-label="Receipts"
                        className={`p-1 rounded ${(inv.payments?.length ?? 0) > 0 ? 'hover:bg-blue-50 text-blue-600' : 'hover:bg-green-50 text-green-600'}`}>
                        {(inv.payments?.length ?? 0) > 0 ? '🧾' : '💰'}
                      </button>
                    )}
                    {onCreateCN && !['DRAFT', 'CANCELLED'].includes(inv.status) && (
                      <button onClick={() => onCreateCN(inv)} title="Create Credit Note" aria-label="Create Credit Note"
                        className="p-1 rounded hover:bg-purple-50 text-purple-600">📝</button>
                    )}
                    {(isAdmin || isHR || isPartner) && inv.status === 'DRAFT' && (
                      <button onClick={() => handleMarkSent(inv)} title="Mark Sent" aria-label="Mark Sent"
                        className="p-1 rounded hover:bg-blue-50 text-blue-600">📤</button>
                    )}
                    {(isAdmin || isHR || isPartner) && ['DRAFT', 'SENT', 'OVERDUE'].includes(inv.status) && (
                      <button onClick={() => handleCancel(inv)} title="Cancel invoice" aria-label="Cancel"
                        className="p-1 rounded hover:bg-orange-50 text-orange-600">🚫</button>
                    )}
                    {(isAdmin || isPartner) && ['PAID', 'PARTIALLY_PAID'].includes(inv.status) && (
                      <button onClick={() => handleMarkUnpaid(inv)} title="Mark Unpaid (clears payments, reverts to Sent)" aria-label="Mark Unpaid"
                        className="p-1 rounded hover:bg-amber-50 text-amber-600">↩</button>
                    )}
                    {isAdmin && (
                      <button onClick={() => handleDelete(inv)} title={inv.status === 'DRAFT' ? 'Delete invoice' : 'Admin: delete invoice (including payments)'} aria-label="Delete"
                        className="p-1 rounded hover:bg-red-50 text-red-600">🗑</button>
                    )}
                    <button onClick={() => setPrintTarget(inv)} title="Preview / Print" aria-label="Print"
                      className="p-1 rounded hover:bg-gray-100 text-gray-600">🖨</button>
                    <button onClick={() => handleQuickPDF(inv)} disabled={pdfDownloading === inv.id}
                      title="Download PDF" aria-label="Download PDF"
                      className="p-1 rounded hover:bg-green-50 text-green-600 disabled:opacity-40">
                      {pdfDownloading === inv.id ? '…' : '📥'}
                    </button>
                    <button onClick={() => setSendEmailTarget(inv)}
                      title="Send to client by email" aria-label="Send by email"
                      className="p-1 rounded hover:bg-indigo-50 text-indigo-600">📧</button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalInvPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Rows:</span>
            <select className="border rounded px-2 py-1 text-sm" value={invPageSize}
              onChange={e => { setInvPageSize(Number(e.target.value)); setInvPage(1); }}>
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="text-gray-400">{invTotal} total</span>
          </div>
          <div className="flex items-center gap-1">
            <button className="px-2 py-1 text-sm border rounded disabled:opacity-40" disabled={invPage === 1} onClick={() => setInvPage(1)}>«</button>
            <button className="px-2 py-1 text-sm border rounded disabled:opacity-40" disabled={invPage === 1} onClick={() => setInvPage(p => p - 1)}>‹</button>
            <span className="px-3 py-1 text-sm">{invPage} / {totalInvPages}</span>
            <button className="px-2 py-1 text-sm border rounded disabled:opacity-40" disabled={invPage === totalInvPages} onClick={() => setInvPage(p => p + 1)}>›</button>
            <button className="px-2 py-1 text-sm border rounded disabled:opacity-40" disabled={invPage === totalInvPages} onClick={() => setInvPage(totalInvPages)}>»</button>
          </div>
        </div>
      )}

      {showCreate && <InvoiceCreateWizard tasks={tasks} billingEntities={billingEntities} defaultTaskId={defaultTaskId} defaultCertificateId={defaultCertificateId} defaultCertificateIds={defaultCertificateIds} defaultLitigationId={defaultLitigationId} defaultLitigationStepId={defaultLitigationStepId} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); reload(); }} />}
      {editTarget && <InvoiceEditModal invoice={editTarget} billingEntities={billingEntities} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); reload(); }} />}
      {paymentTarget && <PaymentModal invoice={paymentTarget} canEdit={isAdmin || isPartner || ['SENT','PARTIALLY_PAID','OVERDUE'].includes(paymentTarget.status)} onClose={() => setPaymentTarget(null)} onSaved={() => { reload(); }} />}
      {printTarget && <GSTPreviewModal invoice={printTarget} onClose={() => setPrintTarget(null)} />}
      {sendEmailTarget && <SendInvoiceEmailModal invoice={sendEmailTarget} onClose={() => setSendEmailTarget(null)} onSent={() => { setSendEmailTarget(null); reload(); }} />}
      {batchEmailTarget && (
        <SendInvoiceBatchModal
          invoices={batchEmailTarget}
          onClose={() => setBatchEmailTarget(null)}
          onSent={() => { setBatchEmailTarget(null); setSelectedRows(new Map()); reload(); }}
        />
      )}
    </div>
  );
};

// ── Receipts Tab ──────────────────────────────────────────────────────────────
// Every receipt recorded in the system: invoice payments plus opening-balance
// payments (scoped server-side), each with its sequential FY-wise receipt
// number and a link back to the document it settles.

interface ReceiptRow {
  id: number;
  source: 'invoice' | 'opening';
  receiptNumber: string | null;
  paymentDate: string;
  amount: number | string;
  tdsDeducted: number | string | null;
  mode: PaymentMode | string;
  reference: string | null;
  notes: string | null;
  bankEntryId: number | null;
  createdBy?: { id: number; staffName: string } | null;
  invoice: {
    id: number; invoiceNumber: string; invoiceDate: string; totalAmount: number | string; status: string;
    client?: { id: number; clientCode: string; clientName: string } | null;
    task?: { id: number; taskId: string; taskName: string } | null;
    billingEntity?: { id: number; name: string } | null;
  };
}

// ₹ amount → Indian-system words, e.g. "Rupees One Lakh Twenty-Three Thousand
// Four Hundred Fifty and Paise Fifty Only". Used on printed receipts.
const rupeesInWords = (amount: number): string => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve',
    'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const two = (n: number): string => n < 20 ? ones[n] : `${tens[Math.floor(n / 10)]}${n % 10 ? ' ' + ones[n % 10] : ''}`;
  const three = (n: number): string => n < 100 ? two(n) : `${ones[Math.floor(n / 100)]} Hundred${n % 100 ? ' ' + two(n % 100) : ''}`;
  const words = (n: number): string => {
    if (n === 0) return 'Zero';
    let out = '';
    const crore = Math.floor(n / 10000000); n %= 10000000;
    const lakh = Math.floor(n / 100000); n %= 100000;
    const thousand = Math.floor(n / 1000); n %= 1000;
    if (crore) out += `${words(crore)} Crore `;
    if (lakh) out += `${three(lakh)} Lakh `;
    if (thousand) out += `${three(thousand)} Thousand `;
    if (n) out += three(n);
    return out.trim();
  };
  const rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);
  let s = `Rupees ${words(rupees)}`;
  if (paise > 0) s += ` and Paise ${words(paise)}`;
  return `${s} Only`;
};

// A5-landscape payment receipt. Print rule: amounts carry 2 decimals on paper.
const generateReceiptPDF = (r: ReceiptRow, returnBase64?: boolean): string | void => {
  const { documents, receipts } = getLatestOrgSettings();
  const [hr, hg, hb] = hexToRgb(documents.pdfHeaderColor, [12, 35, 64]);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a5' });
  const PW = 210, M = 12;
  const R2 = (n: number) => 'Rs. ' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const rcptNo = r.receiptNumber || `RCPT-${r.id}`;

  // Header bar — same brand colour as the invoice template.
  doc.setFillColor(hr, hg, hb);
  doc.rect(0, 0, PW, 24, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(255, 255, 255);
  doc.text(r.invoice.billingEntity?.name || 'Payment Receipt', M, 11);
  doc.setFontSize(13);
  doc.text('PAYMENT RECEIPT', PW - M, 11, { align: 'right' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5);
  doc.text(`# ${rcptNo}`, PW - M, 18, { align: 'right' });

  let y = 34;
  const line = (label: string, value: string, bold = false) => {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(120, 130, 145);
    doc.text(label, M, y);
    doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(10); doc.setTextColor(25, 35, 55);
    doc.text(value, M + 52, y);
    y += 7.5;
  };
  line('Receipt Date', format(new Date(r.paymentDate), 'dd-MMM-yy'));
  line('Received From', `${r.invoice.client?.clientName || '—'}${r.invoice.client?.clientCode ? ` (${r.invoice.client.clientCode})` : ''}`, true);
  line(r.source === 'opening' ? 'Against Opening Balance' : 'Against Invoice', `${r.invoice.invoiceNumber} dt. ${format(new Date(r.invoice.invoiceDate), 'dd-MMM-yy')}`);
  line('Amount Received', R2(Number(r.amount)), true);
  if (r.tdsDeducted && Number(r.tdsDeducted) > 0) line('TDS Deducted by Payer', R2(Number(r.tdsDeducted)));
  line('Mode', String(r.mode));
  if (r.reference) line('Reference / UTR', r.reference);
  if (r.notes) line('Notes', r.notes);

  y += 2;
  doc.setFillColor(240, 244, 250);
  doc.setDrawColor(210, 218, 230);
  const wordsText = doc.splitTextToSize(rupeesInWords(Number(r.amount)), PW - 2 * M - 8);
  const boxH = 8 + wordsText.length * 4.5;
  doc.rect(M, y, PW - 2 * M, boxH, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(120, 130, 145);
  doc.text('AMOUNT IN WORDS', M + 4, y + 5);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(25, 35, 55);
  doc.text(wordsText, M + 4, y + 10);
  y += boxH + 10;

  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(140, 148, 160);
  const disclaimer = receipts.disclaimerText || 'System-generated receipt. Subject to realisation of the instrument.';
  doc.text(`${disclaimer}${r.createdBy?.staffName ? ` Recorded by ${r.createdBy.staffName}.` : ''}`, M, 138);

  if (returnBase64) {
    // Same ";base64," split as generateInvoicePDF — jsPDF's datauristring
    // output carries a filename= segment before the base64 body.
    const dataUri = doc.output('datauristring');
    const idx = dataUri.indexOf(';base64,');
    return idx >= 0 ? dataUri.slice(idx + ';base64,'.length) : dataUri;
  }
  doc.save(`${rcptNo.replace(/\//g, '-')}.pdf`);
};

// ── Email Receipt Modal ───────────────────────────────────────────────────────
// Lighter-weight sibling of SendInvoiceEmailModal — a receipt is a single
// small PDF (no cert/attachment picker, no size meter needed).

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SendReceiptEmailModal: React.FC<{ receipt: ReceiptRow; onClose: () => void; onSent?: () => void }> = ({ receipt: r, onClose, onSent }) => {
  const rcptNo = r.receiptNumber || `RCPT-${r.id}`;
  const [subject, setSubject] = useState(`Payment Receipt ${rcptNo}`);
  const [body, setBody] = useState(
    `<p>Dear ${r.invoice.client?.clientName || ''},</p><p>Please find attached the receipt for your payment of ₹${Number(r.amount).toLocaleString('en-IN')} dated ${format(new Date(r.paymentDate), 'dd-MMM-yy')}.</p><p>Thank you.</p>`,
  );
  const [toList, setToList] = useState<string[]>([]);
  const [toDraft, setToDraft] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    // Suggestions come from past invoice-email recipients for this client —
    // only available when the receipt is tied to an invoice (not an
    // opening-balance entry, which has no invoice id to look up).
    if (r.source !== 'invoice') return;
    getInvoiceEmailSuggestions(r.invoice.id)
      .then((res) => setSuggestions(res.data?.to || []))
      .catch(() => {});
  }, [r.source, r.invoice.id]);

  const addEmail = (value: string) => {
    const v = value.trim().replace(/[,;]+$/, '');
    if (!v) return;
    if (!emailRe.test(v)) { showToast(`Invalid email: ${v}`, 'error'); return; }
    if (!toList.includes(v)) setToList([...toList, v]);
    setToDraft('');
  };

  const handleSend = async () => {
    if (toDraft.trim()) addEmail(toDraft);
    const finalTo = [...toList];
    if (toDraft.trim() && emailRe.test(toDraft.trim()) && !finalTo.includes(toDraft.trim())) finalTo.push(toDraft.trim());
    if (finalTo.length === 0) { showToast('Add at least one recipient', 'error'); return; }

    setSending(true);
    try {
      const base64 = generateReceiptPDF(r, true) as string;
      await sendReceiptEmail(r.source, r.id, {
        to: finalTo,
        subject,
        body,
        pdfBase64: base64,
        pdfFilename: `${rcptNo.replace(/\//g, '-')}.pdf`,
      });
      showToast('✓ Receipt emailed', 'success');
      onSent?.();
      onClose();
    } catch (err: any) {
      showToast(err?.response?.data?.error || err?.response?.data?.message || 'Send failed', 'error');
    } finally {
      setSending(false);
    }
  };

  const suggNotAdded = suggestions.filter((e) => !toList.includes(e));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-indigo-50 to-blue-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900">✉ Email Receipt — {rcptNo}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              To: <strong>{r.invoice.client?.clientName}</strong> · Amount: <strong>{fmt(Number(r.amount))}</strong>
            </p>
          </div>
          <button className="text-gray-400 hover:text-gray-600 text-2xl leading-none" onClick={onClose}>✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="label">To *</label>
            <div className="border border-gray-300 rounded-md px-2 py-1 flex flex-wrap gap-1 min-h-[38px] focus-within:ring-1 focus-within:ring-blue-500">
              {toList.map((e) => (
                <span key={e} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                  {e}
                  <button type="button" onClick={() => setToList(toList.filter((x) => x !== e))} className="hover:text-blue-900">✕</button>
                </span>
              ))}
              <input
                className="flex-1 min-w-[120px] outline-none text-sm py-0.5"
                placeholder={toList.length === 0 ? 'client@example.com' : ''}
                value={toDraft}
                onChange={(e) => setToDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addEmail(toDraft); } }}
                onBlur={() => toDraft.trim() && addEmail(toDraft)}
              />
            </div>
            {suggNotAdded.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {suggNotAdded.map((e) => (
                  <button key={e} type="button" onClick={() => setToList([...toList, e])}
                    className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200">+ {e}</button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="label">Subject</label>
            <input className="input-field w-full" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <label className="label">Message (HTML)</label>
            <textarea className="input-field w-full font-mono text-xs" rows={5} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
          <button className="btn-secondary text-sm" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn-primary text-sm" onClick={handleSend} disabled={sending}>{sending ? 'Sending…' : 'Send Receipt'}</button>
        </div>
      </div>
    </div>
  );
};

const ReceiptsTab: React.FC = () => {
  const [receipts, setReceipts] = useState<ReceiptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [previewInv, setPreviewInv] = useState<Invoice | null>(null);
  const [emailTarget, setEmailTarget] = useState<ReceiptRow | null>(null);

  useEffect(() => {
    setLoading(true);
    getAllPayments().then(res => setReceipts(res.data || [])).catch(() => setReceipts([])).finally(() => setLoading(false));
  }, []);

  const openInvoicePreview = async (invoiceId: number) => {
    try {
      const res = await getInvoice(invoiceId);
      setPreviewInv(res.data);
    } catch (err: any) { alert(err.response?.data?.message || 'Could not load invoice'); }
  };

  const filtered = useMemo(() => receipts.filter(r => {
    if (!inDateRange(r.paymentDate, dateFrom, dateTo)) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!r.invoice.invoiceNumber.toLowerCase().includes(q) &&
          !(r.invoice.client?.clientName || '').toLowerCase().includes(q) &&
          !(r.invoice.client?.clientCode || '').toLowerCase().includes(q) &&
          !(r.reference || '').toLowerCase().includes(q) &&
          !(r.receiptNumber || '').toLowerCase().includes(q)) return false;
    }
    return true;
  }), [receipts, search, dateFrom, dateTo]);

  const totalAmount = useMemo(() => filtered.reduce((s, r) => s + Number(r.amount), 0), [filtered]);
  const totalTds = useMemo(() => filtered.reduce((s, r) => s + Number(r.tdsDeducted || 0), 0), [filtered]);

  // Export the currently-filtered receipts to Excel. Numeric columns stay as
  // real numbers (full precision) so the sheet totals reconcile in accounting.
  const exportExcel = () => {
    if (filtered.length === 0) return;
    const rows = filtered.map(r => ({
      'Receipt #': r.receiptNumber || '',
      'Date': format(new Date(r.paymentDate), 'dd-MMM-yy'),
      'Source': r.source === 'opening' ? 'Opening Bal.' : r.bankEntryId ? 'Bank Import' : 'Manual',
      'Invoice #': r.invoice.invoiceNumber,
      'Client Code': r.invoice.client?.clientCode || '',
      'Client': r.invoice.client?.clientName || '',
      'Task': r.invoice.task?.taskId || '',
      'Amount': Number(r.amount),
      'TDS': r.tdsDeducted ? Number(r.tdsDeducted) : 0,
      'Mode': String(r.mode),
      'Reference': r.reference || '',
      'Notes': r.notes || '',
      'Recorded By': r.createdBy?.staffName || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Receipts');
    XLSX.writeFile(wb, `receipts-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <SummaryCard label="Receipts" value={String(filtered.length)} />
        <SummaryCard label="Total Received" value={fmt(totalAmount)} color="text-green-700" />
        <SummaryCard label="Total TDS Deducted" value={fmt(totalTds)} color="text-amber-700" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input className="input-field text-sm max-w-[260px]" placeholder="Search receipt #, invoice #, client, reference…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <DateRangeFilter label="Date" fromValue={dateFrom} toValue={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} />
        <span className="text-sm text-gray-400">{filtered.length} of {receipts.length}</span>
        <button onClick={exportExcel} disabled={filtered.length === 0}
          className="btn-secondary text-sm ml-auto disabled:opacity-50"
          title="Export the receipts shown below to Excel">📥 Export Excel</button>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="table-header">Receipt #</th>
              <th className="table-header">Date</th>
              <th className="table-header">Source</th>
              <th className="table-header">Invoice #</th>
              <th className="table-header">Client</th>
              <th className="table-header">Task</th>
              <th className="table-header text-right">Amount</th>
              <th className="table-header text-right">TDS</th>
              <th className="table-header">Mode</th>
              <th className="table-header">Reference</th>
              <th className="table-header">Recorded By</th>
              <th className="table-header text-center">Print</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="table-cell text-center text-gray-400 py-10">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={12} className="table-cell text-center text-gray-400 py-10">No receipts recorded yet.</td></tr>
            ) : filtered.map(r => (
              <tr key={`${r.source}-${r.id}`} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="table-cell font-mono text-xs text-gray-700 whitespace-nowrap">{r.receiptNumber || '—'}</td>
                <td className="table-cell text-gray-600">{fmtD(r.paymentDate)}</td>
                <td className="table-cell">
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                    r.source === 'opening' ? 'bg-amber-100 text-amber-700'
                    : r.bankEntryId ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-blue-100 text-blue-700'}`}>
                    {r.source === 'opening' ? 'Opening Bal.' : r.bankEntryId ? 'Bank Import' : 'Manual'}
                  </span>
                </td>
                <td className="table-cell font-mono">
                  {r.source === 'invoice' ? (
                    <button className="text-blue-700 hover:underline" title="Preview invoice"
                      onClick={() => openInvoicePreview(r.invoice.id)}>{r.invoice.invoiceNumber}</button>
                  ) : (
                    <span className="text-blue-700" title="Opening balance entry">{r.invoice.invoiceNumber}</span>
                  )}
                </td>
                <td className="table-cell">
                  <span className="truncate max-w-[200px] inline-block align-bottom" title={r.invoice.client?.clientName}>
                    {r.invoice.client?.clientName || '—'}
                  </span>
                </td>
                <td className="table-cell text-gray-600">
                  <span className="truncate max-w-[160px] inline-block align-bottom" title={r.invoice.task?.taskName}>
                    {r.invoice.task?.taskId || '—'}
                  </span>
                </td>
                <td className="table-cell text-right font-semibold text-green-700">{fmt(Number(r.amount))}</td>
                <td className="table-cell text-right text-amber-700">{r.tdsDeducted ? fmt(Number(r.tdsDeducted)) : '—'}</td>
                <td className="table-cell text-gray-600">{r.mode}</td>
                <td className="table-cell font-mono text-xs text-gray-600 truncate max-w-[120px]" title={r.reference || ''}>{r.reference || '—'}</td>
                <td className="table-cell text-gray-500">{r.createdBy?.staffName || '—'}</td>
                <td className="table-cell text-center whitespace-nowrap">
                  <button onClick={() => generateReceiptPDF(r)} title="Download receipt PDF" aria-label="Download receipt"
                    className="p-1 rounded hover:bg-blue-50 text-blue-600">🖨</button>
                  <button onClick={() => setEmailTarget(r)} title="Email receipt" aria-label="Email receipt"
                    className="p-1 rounded hover:bg-blue-50 text-blue-600">✉️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {previewInv && <GSTPreviewModal invoice={previewInv} onClose={() => setPreviewInv(null)} />}
      {emailTarget && <SendReceiptEmailModal receipt={emailTarget} onClose={() => setEmailTarget(null)} />}
    </div>
  );
};

// ── Write-offs Register (Short & Excess / Bad Debt) ───────────────────────────

interface AdjustmentRow {
  id: number; type: 'SHORT_EXCESS' | 'BAD_DEBT'; amount: number; adjustmentDate: string; reason?: string;
  invoice?: { id: number; invoiceNumber: string; status: string } | null;
  openingBalance?: { id: number; invoiceNumber: string; status: string } | null;
  client: { id: number; clientCode: string; clientName: string };
  createdBy?: { id: number; staffName: string } | null;
  reversedAt?: string | null;
  reversedBy?: { id: number; staffName: string } | null;
}

const WriteOffsRegisterTab: React.FC = () => {
  // Reversing a write-off is open to partners as well as Admin — they are the
  // ones who wrote the balance off in the first place.
  const { isAdmin, isPartner } = useAuth();
  const canReverse = isAdmin || isPartner;
  const [rows, setRows] = useState<AdjustmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'' | 'SHORT_EXCESS' | 'BAD_DEBT'>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    // Always include reversed rows here — this register is the audit trail,
    // so an undone write-off should stay visible (struck through) rather
    // than silently disappearing.
    const params: any = { includeReversed: 'true' };
    if (typeFilter) params.type = typeFilter;
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    getAdjustments(params).then(res => setRows(res.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [typeFilter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    const ref = r.invoice?.invoiceNumber || r.openingBalance?.invoiceNumber || '';
    return ref.toLowerCase().includes(q) || r.client.clientName.toLowerCase().includes(q) || r.client.clientCode.toLowerCase().includes(q);
  }), [rows, search]);

  const totalShortExcess = useMemo(() => filtered.filter(r => r.type === 'SHORT_EXCESS').reduce((s, r) => s + Number(r.amount), 0), [filtered]);
  const totalBadDebt = useMemo(() => filtered.filter(r => r.type === 'BAD_DEBT').reduce((s, r) => s + Number(r.amount), 0), [filtered]);

  const handleReverse = async (r: AdjustmentRow) => {
    if (!window.confirm(`Reverse this write-off of ${fmt(Number(r.amount))}? The invoice/opening balance goes back to its prior outstanding.`)) return;
    try { await reverseAdjustment(r.id); load(); }
    catch (err: any) { alert(err?.response?.data?.message || 'Failed to reverse'); }
  };

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <SummaryCard label="Write-offs" value={String(filtered.length)} />
        <SummaryCard label="Short & Excess" value={fmt(totalShortExcess)} color="text-amber-700" />
        <SummaryCard label="Bad Debts" value={fmt(totalBadDebt)} color="text-red-700" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input className="input-field text-sm max-w-[260px]" placeholder="Search invoice #, client…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <select className="input-field text-sm max-w-[180px]" value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)}>
          <option value="">All Types</option>
          <option value="SHORT_EXCESS">Short & Excess</option>
          <option value="BAD_DEBT">Bad Debt</option>
        </select>
        <DateRangeFilter label="Date" fromValue={dateFrom} toValue={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} />
        <span className="text-sm text-gray-400">{filtered.length} of {rows.length}</span>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="table-header">Date</th>
              <th className="table-header">Type</th>
              <th className="table-header">Against</th>
              <th className="table-header">Client</th>
              <th className="table-header text-right">Amount</th>
              <th className="table-header">Reason</th>
              <th className="table-header">Created By</th>
              {canReverse && <th className="table-header text-center">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={canReverse ? 8 : 7} className="table-cell text-center text-gray-400 py-10">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={canReverse ? 8 : 7} className="table-cell text-center text-gray-400 py-10">No write-offs recorded.</td></tr>
            ) : filtered.map(r => (
              <tr key={r.id} className={`border-b border-gray-50 hover:bg-gray-50 ${r.reversedAt ? 'line-through text-gray-400' : ''}`}>
                <td className="table-cell text-gray-600">{fmtD(r.adjustmentDate)}</td>
                <td className="table-cell">
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${r.type === 'BAD_DEBT' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {r.type === 'BAD_DEBT' ? 'Bad Debt' : 'Short & Excess'}
                  </span>
                </td>
                <td className="table-cell font-mono text-xs text-blue-700">{r.invoice?.invoiceNumber || r.openingBalance?.invoiceNumber || '—'}</td>
                <td className="table-cell">
                  <span className="truncate max-w-[180px] inline-block align-bottom" title={r.client.clientName}>{r.client.clientName}</span>
                </td>
                <td className="table-cell text-right font-semibold text-orange-700">{fmt(Number(r.amount))}</td>
                <td className="table-cell text-gray-500 max-w-[220px] truncate" title={r.reason || ''}>{r.reason || '—'}</td>
                <td className="table-cell text-gray-500">{r.createdBy?.staffName || '—'}</td>
                {canReverse && (
                  <td className="table-cell text-center">
                    {r.reversedAt ? (
                      <span className="text-xs text-gray-400" title={r.reversedBy?.staffName ? `Reversed by ${r.reversedBy.staffName}` : ''}>Reversed</span>
                    ) : (
                      <button onClick={() => handleReverse(r)} title="Reverse this write-off" aria-label="Reverse"
                        className="p-1 rounded hover:bg-red-50 text-red-500">↩</button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Client Payment Reminder Modal ──────────────────────────────────────────────
// ONE consolidated dues email per client from the Receivables tab: the server
// renders a designed statement table (all outstanding invoices + opening
// balances) into the body, and every outstanding invoice's PDF is generated
// here and attached. The modal previews the same rows the pivot shows.

interface ClientReminderTarget {
  clientId: number; clientCode: string; clientName: string;
  rows: Array<{
    source: 'invoice' | 'opening'; id: number; invoiceNumber: string;
    outstanding: number; invoiceDate: string; dueDate: string | null; daysOverdue: number;
  }>;
}

const SendClientReminderModal: React.FC<{ target: ClientReminderTarget; onClose: () => void }> = ({ target: t, onClose }) => {
  const invoiceRows = useMemo(() => t.rows.filter((r) => r.source === 'invoice'), [t.rows]);
  const totalOutstanding = useMemo(() => t.rows.reduce((s, r) => s + r.outstanding, 0), [t.rows]);

  const [subject, setSubject] = useState(`Payment Reminder — ${t.clientName}`);
  const [note, setNote] = useState('');
  const [toList, setToList] = useState<string[]>([]);
  const [toDraft, setToDraft] = useState('');
  const [ccList, setCcList] = useState<string[]>([]);
  const [ccDraft, setCcDraft] = useState('');
  const [partners, setPartners] = useState<Array<{ id: number; staffName: string; email: string }>>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [pdfs, setPdfs] = useState<Array<{ invoiceId: number; base64: string; filename: string; bytes: number }>>([]);
  const [prepProgress, setPrepProgress] = useState<{ done: number; total: number } | null>(
    invoiceRows.length > 0 ? { done: 0, total: invoiceRows.length } : null,
  );
  const [prepErrors, setPrepErrors] = useState<string[]>([]);

  // Recipient suggestions from the client's invoice email history.
  useEffect(() => {
    const first = invoiceRows[0];
    if (!first) return;
    getInvoiceEmailSuggestions(first.id).then((res) => setSuggestions(res.data?.to || [])).catch(() => {});
  }, [invoiceRows]);

  // Partners (current staff flagged isPartner, with an email) as one-click CC
  // chips — read from the staff list rather than hard-coding names.
  useEffect(() => {
    getStaff()
      .then((res) => setPartners(
        (res.data || [])
          .filter((s: any) => s.isPartner && s.email && !s.exitDate)
          .map((s: any) => ({ id: s.id, staffName: s.staffName, email: String(s.email).trim() })),
      ))
      .catch(() => {});
  }, []);

  // Generate every outstanding invoice's PDF up-front (firm rule: reminders go
  // out with all invoices attached). Sequential keeps memory flat; each PDF
  // needs the full invoice (line items, GSTIN, signatory), so fetch per id.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out: Array<{ invoiceId: number; base64: string; filename: string; bytes: number }> = [];
      const errs: string[] = [];
      for (let i = 0; i < invoiceRows.length; i++) {
        const row = invoiceRows[i];
        try {
          const res = await getInvoice(row.id);
          const base64 = (await generateInvoicePDF(res.data, res.data.template ?? 1, true)) as string;
          out.push({
            invoiceId: row.id,
            base64,
            filename: `Invoice-${row.invoiceNumber.replace(/\//g, '-')}.pdf`,
            bytes: Math.floor((base64.length * 3) / 4),
          });
        } catch {
          errs.push(row.invoiceNumber);
        }
        if (cancelled) return;
        setPrepProgress({ done: i + 1, total: invoiceRows.length });
      }
      if (!cancelled) { setPdfs(out); setPrepErrors(errs); setPrepProgress(null); }
    })();
    return () => { cancelled = true; };
  }, [invoiceRows]);

  const totalBytes = pdfs.reduce((s, p) => s + p.bytes, 0);
  const limitBytes = Math.round(Number((getLatestOrgSettings() as any).billing?.attachmentSizeLimitMB || 4) * 1024 * 1024);
  const overLimit = totalBytes > limitBytes;
  const kb = (b: number) => b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

  const addEmail = (value: string) => {
    const v = value.trim().replace(/[,;]+$/, '');
    if (!v) return;
    if (!emailRe.test(v)) { showToast(`Invalid email: ${v}`, 'error'); return; }
    if (!toList.includes(v)) setToList([...toList, v]);
    setToDraft('');
  };
  const addCc = (value: string) => {
    const v = value.trim().replace(/[,;]+$/, '');
    if (!v) return;
    if (!emailRe.test(v)) { showToast(`Invalid email: ${v}`, 'error'); return; }
    if (!ccList.includes(v)) setCcList([...ccList, v]);
    setCcDraft('');
  };
  const ccHas = (email: string) => ccList.some((e) => e.toLowerCase() === email.toLowerCase());

  const handleSend = async () => {
    if (toDraft.trim()) addEmail(toDraft);
    const finalTo = [...toList];
    if (toDraft.trim() && emailRe.test(toDraft.trim()) && !finalTo.includes(toDraft.trim())) finalTo.push(toDraft.trim());
    if (finalTo.length === 0) { showToast('Add at least one recipient', 'error'); return; }
    if (ccDraft.trim()) addCc(ccDraft);
    const finalCc = [...ccList];
    if (ccDraft.trim() && emailRe.test(ccDraft.trim()) && !finalCc.includes(ccDraft.trim())) finalCc.push(ccDraft.trim());

    setSending(true);
    try {
      await sendClientReminder(t.clientId, {
        to: finalTo, cc: finalCc, subject, note: note.trim() || undefined,
        pdfs: pdfs.map(({ invoiceId, base64, filename }) => ({ invoiceId, base64, filename })),
      });
      showToast('✓ Reminder sent', 'success');
      onClose();
    } catch (err: any) {
      showToast(err?.response?.data?.message || err?.response?.data?.error || 'Send failed', 'error');
    } finally {
      setSending(false);
    }
  };

  const suggNotAdded = suggestions.filter((e) => !toList.includes(e));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900">📧 Payment Reminder — {t.clientName}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {t.rows.length} outstanding item{t.rows.length === 1 ? '' : 's'} ·
              Total: <strong className="text-red-600">{fmt(totalOutstanding)}</strong> ·
              One consolidated email with all invoices attached
            </p>
          </div>
          <button className="text-gray-400 hover:text-gray-600 text-2xl leading-none" onClick={onClose}>✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="label">To *</label>
            <div className="border border-gray-300 rounded-md px-2 py-1 flex flex-wrap gap-1 min-h-[38px] focus-within:ring-1 focus-within:ring-blue-500">
              {toList.map((e) => (
                <span key={e} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                  {e}
                  <button type="button" onClick={() => setToList(toList.filter((x) => x !== e))} className="hover:text-blue-900">✕</button>
                </span>
              ))}
              <input
                className="flex-1 min-w-[120px] outline-none text-sm py-0.5"
                placeholder={toList.length === 0 ? 'client@example.com' : ''}
                value={toDraft}
                onChange={(e) => setToDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addEmail(toDraft); } }}
                onBlur={() => toDraft.trim() && addEmail(toDraft)}
              />
            </div>
            {suggNotAdded.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {suggNotAdded.map((e) => (
                  <button key={e} type="button" onClick={() => setToList([...toList, e])}
                    className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200">+ {e}</button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="label">CC</label>
            <div className="border border-gray-300 rounded-md px-2 py-1 flex flex-wrap gap-1 min-h-[38px] focus-within:ring-1 focus-within:ring-blue-500">
              {ccList.map((e) => (
                <span key={e} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                  {e}
                  <button type="button" onClick={() => setCcList(ccList.filter((x) => x !== e))} className="hover:text-blue-900">✕</button>
                </span>
              ))}
              <input
                className="flex-1 min-w-[120px] outline-none text-sm py-0.5"
                placeholder={ccList.length === 0 ? 'Optional — e.g. a partner' : ''}
                value={ccDraft}
                onChange={(e) => setCcDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addCc(ccDraft); } }}
                onBlur={() => ccDraft.trim() && addCc(ccDraft)}
              />
            </div>
            {partners.length > 0 && (
              <div className="flex flex-wrap items-center gap-1 mt-1.5">
                <span className="text-[11px] text-gray-400 mr-1">Partners:</span>
                {partners.map((p) => {
                  const added = ccHas(p.email);
                  return (
                    <button key={p.id} type="button" disabled={added} title={p.email}
                      onClick={() => { if (!added) setCcList([...ccList, p.email]); }}
                      className={`text-xs px-2 py-0.5 rounded-full ${added ? 'bg-blue-100 text-blue-800 cursor-default' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      {added ? '✓ ' : '+ '}{p.staffName}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <label className="label">Subject</label>
            <input className="input-field w-full" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <label className="label">Personal note (optional — appears above the statement table)</label>
            <textarea className="input-field w-full text-sm" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. As discussed on call yesterday…" />
          </div>

          {/* Statement preview — the server renders this same data into the designed email body */}
          <div>
            <label className="label">Statement (rendered into the email body)</label>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-2 py-1.5 text-left text-gray-500">Invoice #</th>
                    <th className="px-2 py-1.5 text-left text-gray-500">Date</th>
                    <th className="px-2 py-1.5 text-left text-gray-500">Due</th>
                    <th className="px-2 py-1.5 text-center text-gray-500">Overdue</th>
                    <th className="px-2 py-1.5 text-right text-gray-500">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {t.rows.map((r) => (
                    <tr key={`${r.source}-${r.id}`} className="border-b border-gray-50">
                      <td className="px-2 py-1 font-mono">
                        {r.invoiceNumber}
                        {r.source === 'opening' && <span className="ml-1 text-amber-600" title="Opening balance (pre-system invoice)">*</span>}
                      </td>
                      <td className="px-2 py-1 text-gray-600">{fmtD(r.invoiceDate)}</td>
                      <td className="px-2 py-1 text-gray-600">{r.dueDate ? fmtD(r.dueDate) : '—'}</td>
                      <td className={`px-2 py-1 text-center ${r.daysOverdue > 90 ? 'text-red-600 font-semibold' : r.daysOverdue > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                        {r.daysOverdue > 0 ? `${r.daysOverdue}d` : 'Current'}
                      </td>
                      <td className="px-2 py-1 text-right font-semibold text-red-600">{fmt(r.outstanding)}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-bold">
                    <td colSpan={4} className="px-2 py-1.5 text-right">Total outstanding</td>
                    <td className="px-2 py-1.5 text-right text-red-700">{fmt(totalOutstanding)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {t.rows.some((r) => r.source === 'opening') && (
              <p className="text-[11px] text-blue-600 mt-1">* Opening-balance invoice PDFs (where available) are automatically attached by the server.</p>
            )}
          </div>

          {/* Attachments */}
          <div>
            <label className="label">Attachments</label>
            {prepProgress ? (
              <p className="text-sm text-blue-600">Preparing invoice PDFs… {prepProgress.done}/{prepProgress.total}</p>
            ) : pdfs.length === 0 ? (
              <p className="text-sm text-gray-400">No invoice PDFs to attach{invoiceRows.length === 0 ? ' (opening-balance items only)' : ''}.</p>
            ) : (
              <div className="space-y-1">
                {pdfs.map((p) => (
                  <div key={p.invoiceId} className="flex items-center justify-between text-sm bg-gray-50 rounded px-2 py-1">
                    <span className="truncate">📎 {p.filename}</span>
                    <span className="text-gray-400 text-xs ml-2 whitespace-nowrap">{kb(p.bytes)}</span>
                  </div>
                ))}
                <p className={`text-xs mt-1 ${overLimit ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                  Total {kb(totalBytes)} of {kb(limitBytes)} limit{overLimit ? ' — too large to send; the server will reject this' : ''}
                </p>
              </div>
            )}
            {prepErrors.length > 0 && (
              <p className="text-xs text-amber-700 mt-1">⚠ Could not generate PDF for: {prepErrors.join(', ')} — they will still appear in the statement table.</p>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
          <button className="btn-secondary text-sm" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn-primary text-sm" onClick={handleSend} disabled={sending || prepProgress !== null || overLimit}>
            {sending ? 'Sending…' : prepProgress ? 'Preparing…' : 'Send Reminder'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Collections follow-up remarks ─────────────────────────────────────────────
// Dated log of what the client said when chased for an invoice / opening balance.

type FollowUpTarget = { source: 'invoice' | 'opening'; id: number; label: string; clientName: string };

const FollowUpModal: React.FC<{ target: FollowUpTarget; onClose: () => void; onChanged: () => void }> = ({ target, onClose, onChanged }) => {
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const today = format(new Date(), 'yyyy-MM-dd');
  const [form, setForm] = useState({ remarkDate: today, remark: '', nextFollowUpDate: '' });
  const key = target.source === 'invoice' ? { invoiceId: target.id } : { openingBalanceId: target.id };

  const load = useCallback(() => {
    setLoading(true);
    getFollowUps(target.source === 'invoice' ? { invoiceId: target.id } : { openingBalanceId: target.id })
      .then(r => setEntries(r.data || [])).catch(() => setEntries([])).finally(() => setLoading(false));
  }, [target.source, target.id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (saving) return;
    if (!form.remark.trim()) return alert('Enter what the client said.');
    setSaving(true);
    try {
      await addFollowUp({ ...key, remarkDate: form.remarkDate, remark: form.remark.trim(), nextFollowUpDate: form.nextFollowUpDate || undefined });
      setForm({ remarkDate: today, remark: '', nextFollowUpDate: '' });
      load(); onChanged();
    } catch (err: any) { alert(err?.response?.data?.message || 'Failed to save remark.'); }
    finally { setSaving(false); }
  };
  const remove = async (id: number) => {
    if (!window.confirm('Delete this remark?')) return;
    try { await deleteFollowUp(id); load(); onChanged(); }
    catch (err: any) { alert(err?.response?.data?.message || 'Failed.'); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-gray-900">Follow-up remarks</h3>
        <p className="text-xs text-gray-500 mb-3">{target.clientName} · Invoice {target.label}</p>

        <div className="border border-gray-200 rounded-lg p-3 mb-4 space-y-2">
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs text-gray-500">Date of conversation</label>
              <input type="date" className="input-field text-sm" max={today} value={form.remarkDate}
                onChange={e => setForm(f => ({ ...f, remarkDate: e.target.value }))} />
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-500">Next follow-up / promised date (optional)</label>
              <input type="date" className="input-field text-sm" value={form.nextFollowUpDate}
                onChange={e => setForm(f => ({ ...f, nextFollowUpDate: e.target.value }))} />
            </div>
          </div>
          <textarea className="input-field text-sm w-full" rows={3} maxLength={2000} placeholder="What did the client say?"
            value={form.remark} onChange={e => setForm(f => ({ ...f, remark: e.target.value }))} />
          <div className="text-right">
            <button className="btn-primary text-sm" disabled={saving || !form.remark.trim() || !form.remarkDate} onClick={save}>
              {saving ? 'Saving…' : 'Add remark'}
            </button>
          </div>
        </div>

        {loading ? <p className="text-sm text-gray-400">Loading…</p> : entries.length === 0 ? (
          <p className="text-sm text-gray-400">No remarks yet.</p>
        ) : (
          <ul className="space-y-2">
            {entries.map(e => (
              <li key={e.id} className="border-l-2 border-blue-300 pl-3 py-1 text-sm">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span><span className="font-medium text-gray-700">{fmtD(e.remarkDate)}</span> · {e.createdByName}</span>
                  <button className="text-red-400 hover:text-red-600" title="Delete remark (author or Admin)" onClick={() => remove(e.id)}>✕</button>
                </div>
                <p className="text-gray-800 whitespace-pre-wrap">{e.remark}</p>
                {e.nextFollowUpDate && <p className="text-xs text-amber-700">Next follow-up: {fmtD(e.nextFollowUpDate)}</p>}
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-end mt-4"><button className="btn-secondary text-sm" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
};

// ── Receivables Tab ───────────────────────────────────────────────────────────

const ReceivablesTab: React.FC<{ onCreateCNFromInvoice?: (inv: any) => void; onCreateCNFromOb?: (ob: any) => void }> = ({ onCreateCNFromInvoice, onCreateCNFromOb }) => {
  const { isAdmin, isHR, isPartner } = useAuth();
  const canManageOB = isAdmin || isHR || isPartner;
  const [recvSubTab, setRecvSubTab] = useState<'outstanding' | 'receipts' | 'writeoffs'>('outstanding');
  const [items, setItems] = useState<Receivable[]>([]);
  const [openingBalances, setOpeningBalances] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'invoice' | 'opening'>('all');
  const [bucketFilter, setBucketFilter] = useState<'' | '0-30' | '31-60' | '61-90' | '90+'>('');
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [showAddOB, setShowAddOB] = useState(false);
  const [obForm, setObForm] = useState({ invoiceNumber: '', clientId: '', invoiceDate: '', dueDate: '', amount: '', notes: '' });
  // Guards the opening-balance submit against a fast repeat click — see
  // Tasks.tsx certSaving/taskSaving for the same pattern.
  const [obSaving, setObSaving] = useState(false);
  const [clientsList, setClientsList] = useState<any[]>([]);
  // Payment modals: invoices reuse PaymentModal; opening balances use OpeningBalancePaymentModal.
  const [payInv, setPayInv] = useState<Invoice | null>(null);
  const [payCanEdit, setPayCanEdit] = useState(false);
  const [obPayTarget, setObPayTarget] = useState<any | null>(null);
  const [previewInv, setPreviewInv] = useState<Invoice | null>(null);
  const [reminderTarget, setReminderTarget] = useState<ClientReminderTarget | null>(null);
  const [followTarget, setFollowTarget] = useState<FollowUpTarget | null>(null);
  const [latestFollow, setLatestFollow] = useState<{ invoice: Record<number, any>; opening: Record<number, any> }>({ invoice: {}, opening: {} });
  const [writeOffTarget, setWriteOffTarget] = useState<{
    targetType: 'invoice' | 'openingBalance'; targetId: number; targetLabel: string;
    outstanding: number; invoiceDate?: string;
  } | null>(null);
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  type PivotSortKey = 'clientCode' | 'clientName' | 'count' | 'outstanding' | 'b0to30' | 'b31to60' | 'b61to90' | 'b90plus' | 'maxDays';
  const [sortKey, setSortKey] = useState<PivotSortKey>('outstanding');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const toggleSort = (key: PivotSortKey) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'clientCode' || key === 'clientName' ? 'asc' : 'desc'); }
  };
  const toggleExpand = (key: string) => setExpandedClients(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Open the GST invoice preview for an invoice-source receivable.
  const openInvoicePreview = async (invoiceId: number) => {
    try {
      const res = await getInvoice(invoiceId);
      setPreviewInv(res.data);
    } catch (err: any) { alert(err.response?.data?.message || 'Could not load invoice'); }
  };

  const loadFollowUps = useCallback(() => {
    getLatestFollowUps().then(r => setLatestFollow(r.data || { invoice: {}, opening: {} })).catch(() => {});
  }, []);

  const loadAll = useCallback(() => {
    setLoading(true);
    loadFollowUps();
    Promise.all([
      getReceivables().catch(() => ({ data: [] })),
      getOpeningBalances().catch(() => ({ data: [] })),
      getClients().catch(() => ({ data: [] })),
    ]).then(([recRes, obRes, clRes]) => {
      const raw = recRes.data;
      setItems(Array.isArray(raw) ? raw : (raw?.invoices ?? []));
      setOpeningBalances(obRes.data || []);
      setClientsList(clRes.data || []);
    }).finally(() => setLoading(false));
  }, [loadFollowUps]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Ageing for opening balances, computed client-side to mirror the invoice backend.
  const ageingOf = (dueDate: string | null, invoiceDate: string): { bucket: string; daysOverdue: number } => {
    const ref = dueDate ? new Date(dueDate) : new Date(new Date(invoiceDate).getTime() + 30 * 86400000);
    ref.setHours(0, 0, 0, 0);
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const days = Math.floor((t.getTime() - ref.getTime()) / 86400000);
    const bucket = days <= 0 ? 'Current' : days <= 30 ? '0-30 days' : days <= 60 ? '31-60 days' : days <= 90 ? '61-90 days' : '90+ days';
    return { bucket, daysOverdue: Math.max(0, days) };
  };

  // Unified receivable rows from both sources (invoices + outstanding opening balances).
  type RecvRow = {
    key: string; source: 'invoice' | 'opening'; id: number;
    invoiceNumber: string; clientCode: string; clientName: string; clientId: number | null;
    outstanding: number; invoiceDate: string; dueDate: string | null;
    bucket: string; daysOverdue: number; hasPayments: boolean; raw: any;
  };
  const rows = useMemo<RecvRow[]>(() => {
    const invRows: RecvRow[] = (items as any[]).map((r) => ({
      key: `inv-${r.id}`, source: 'invoice', id: r.id,
      invoiceNumber: r.invoiceNumber, clientCode: r.client?.clientCode ?? '', clientName: r.client?.clientName ?? '—',
      clientId: r.client?.id ?? r.clientId ?? null,
      outstanding: outstandingOf(r),
      invoiceDate: r.invoiceDate, dueDate: r.dueDate ?? null,
      bucket: r.ageingBucket ?? '', daysOverdue: r.daysOverdue ?? 0,
      hasPayments: (r.payments?.length ?? 0) > 0, raw: r,
    }));
    const obRows: RecvRow[] = openingBalances
      .filter((ob: any) => ob.status !== 'PAID' && ob.status !== 'WRITTEN_OFF')
      .map((ob: any) => {
        const a = ageingOf(ob.dueDate ?? null, ob.invoiceDate);
        return {
          key: `ob-${ob.id}`, source: 'opening', id: ob.id,
          invoiceNumber: ob.invoiceNumber, clientCode: ob.client?.clientCode ?? '', clientName: ob.client?.clientName ?? '—',
          clientId: ob.clientId ?? ob.client?.id ?? null,
          // Subtract issued credit notes (creditedAmount) and write-offs
          // (writtenOffAmount) as well as cash paid — both reduce the
          // receivable the same way a payment does. (Invoice rows already
          // net this via outstandingOf; the OB branch was missing it.)
          outstanding: Math.max(0, Number(ob.amount) - Number(ob.amountPaid) - Number(ob.creditedAmount ?? 0) - Number(ob.writtenOffAmount ?? 0)),
          invoiceDate: ob.invoiceDate, dueDate: ob.dueDate ?? null,
          bucket: a.bucket, daysOverdue: a.daysOverdue,
          hasPayments: (ob.payments?.length ?? 0) > 0, raw: ob,
        };
      });
    // Drop anything already settled — a zero (or negative) outstanding is no
    // longer a receivable.
    return [...invRows, ...obRows].filter(r => r.outstanding > 0.5);
  }, [items, openingBalances]);

  // Map a row's ageing label to one of the four clickable bucket groups.
  const bucketGroup = (b: string): '0-30' | '31-60' | '61-90' | '90+' =>
    b === '31-60 days' ? '31-60' : b === '61-90 days' ? '61-90' : b === '90+ days' ? '90+' : '0-30';

  const filtered = useMemo(() => rows.filter(r => {
    if (sourceFilter !== 'all' && r.source !== sourceFilter) return false;
    if (clientFilter && String(r.clientId) !== clientFilter) return false;
    if (bucketFilter && bucketGroup(r.bucket) !== bucketFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!r.invoiceNumber.toLowerCase().includes(q) &&
          !r.clientName.toLowerCase().includes(q) &&
          !r.clientCode.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [rows, sourceFilter, clientFilter, bucketFilter, search]);

  // Totals + ageing buckets across BOTH sources (the buckets previously ignored OB).
  const totals = useMemo(() => {
    const t = { grand: 0, invoices: 0, opening: 0, b0to30: 0, b31to60: 0, b61to90: 0, b90plus: 0, obCount: 0 };
    for (const r of rows) {
      t.grand += r.outstanding;
      if (r.source === 'invoice') t.invoices += r.outstanding;
      else { t.opening += r.outstanding; t.obCount++; }
      if (r.bucket === '31-60 days') t.b31to60 += r.outstanding;
      else if (r.bucket === '61-90 days') t.b61to90 += r.outstanding;
      else if (r.bucket === '90+ days') t.b90plus += r.outstanding;
      else t.b0to30 += r.outstanding; // Current + 0-30 days
    }
    return t;
  }, [rows]);

  // Client-wise pivot: one row per client aggregating all of that client's
  // outstanding receivables, with the underlying invoice-wise rows attached
  // so a click can expand the detail.
  type ClientPivotRow = {
    key: string; clientId: number | null; clientCode: string; clientName: string;
    count: number; outstanding: number; b0to30: number; b31to60: number; b61to90: number; b90plus: number;
    maxDays: number; rows: RecvRow[];
  };
  const pivot = useMemo<ClientPivotRow[]>(() => {
    const map = new Map<string, ClientPivotRow>();
    for (const r of filtered) {
      const key = r.clientId != null ? String(r.clientId) : `name:${r.clientName}`;
      let g = map.get(key);
      if (!g) {
        g = { key, clientId: r.clientId, clientCode: r.clientCode, clientName: r.clientName,
          count: 0, outstanding: 0, b0to30: 0, b31to60: 0, b61to90: 0, b90plus: 0, maxDays: 0, rows: [] };
        map.set(key, g);
      }
      g.count++;
      g.outstanding += r.outstanding;
      g.maxDays = Math.max(g.maxDays, r.daysOverdue);
      g.rows.push(r);
      if (r.bucket === '31-60 days') g.b31to60 += r.outstanding;
      else if (r.bucket === '61-90 days') g.b61to90 += r.outstanding;
      else if (r.bucket === '90+ days') g.b90plus += r.outstanding;
      else g.b0to30 += r.outstanding; // Current + 0-30 days
    }
    const arr = Array.from(map.values());
    for (const g of arr) g.rows.sort((a, b) => b.outstanding - a.outstanding);
    arr.sort((a, b) => {
      let cmp: number;
      if (sortKey === 'clientCode') cmp = a.clientCode.localeCompare(b.clientCode);
      else if (sortKey === 'clientName') cmp = a.clientName.localeCompare(b.clientName);
      else cmp = a[sortKey] - b[sortKey];
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const handleImport = async (file: File) => {
    setImporting(true);
    setImportResult(null);
    try {
      const res = await importOpeningBalances(file);
      setImportResult(res.data);
      loadAll();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Import failed');
    } finally { setImporting(false); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadOpeningBalanceTemplate();
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'OpeningBalance_Import_Template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Could not download template'); }
  };

  const handleAddOB = async (e: React.FormEvent) => {
    e.preventDefault();
    if (obSaving) return;
    setObSaving(true);
    try {
      await createOpeningBalance({
        invoiceNumber: obForm.invoiceNumber,
        clientId: Number(obForm.clientId),
        invoiceDate: obForm.invoiceDate,
        dueDate: obForm.dueDate || null,
        amount: Number(obForm.amount),
        notes: obForm.notes,
      });
      setShowAddOB(false);
      setObForm({ invoiceNumber: '', clientId: '', invoiceDate: '', dueDate: '', amount: '', notes: '' });
      loadAll();
    } catch (err: any) { alert(err.response?.data?.message || 'Error'); }
    finally { setObSaving(false); }
  };

  // Open the invoice PaymentModal for an invoice-based receivable. Fetch the full
  // invoice so the modal has its payments, tdsSection and current status.
  const openInvoicePayment = async (invoiceId: number) => {
    try {
      const res = await getInvoice(invoiceId);
      setPayInv(res.data);
      setPayCanEdit(isAdmin || ['SENT', 'PARTIALLY_PAID', 'OVERDUE'].includes(res.data.status));
    } catch (err: any) { alert(err.response?.data?.message || 'Could not load invoice'); }
  };

  const handleDeleteOB = async (id: number) => {
    if (!window.confirm('Delete this opening balance entry?')) return;
    try { await deleteOpeningBalance(id); loadAll(); }
    catch (err: any) { alert(err.response?.data?.message || 'Error'); }
  };

  // Excel export of the currently-filtered receivables — respects search /
  // source / client / bucket filters. Two sheets: a task-level line-item
  // detail (what the outstanding amount is actually for — task, category,
  // partner, manager; opening balances predate task-linked billing, so those
  // columns are blank for that source) and a client-wise summary matching
  // the on-screen pivot.
  const handleExportExcel = () => {
    if (filtered.length === 0) return;
    const detailRows = filtered.map(r => {
      const task = r.source === 'invoice' ? (r.raw?.task as TaskInfo | undefined) : undefined;
      return {
        'Client Code': r.clientCode,
        'Client': r.clientName,
        'Source': r.source === 'invoice' ? 'Invoice' : 'Opening Balance',
        'Document #': r.invoiceNumber,
        'Task ID': task?.taskId ?? '',
        'Task Name': task?.taskName ?? '',
        'Category': task?.category?.name ?? '',
        'Partner': task?.partner?.staffName ?? '',
        'Manager': task?.manager?.staffName ?? '',
        'Document Date': fmtD(r.invoiceDate),
        'Due Date': r.dueDate ? fmtD(r.dueDate) : '',
        'Ageing Bucket': r.bucket || (r.daysOverdue > 0 ? '' : 'Current'),
        'Days Overdue': r.daysOverdue,
        'Outstanding (₹)': r.outstanding,
      };
    });
    const clientRows = pivot.map(g => ({
      'Client Code': g.clientCode,
      'Client': g.clientName,
      '# Items': g.count,
      'Outstanding (₹)': g.outstanding,
      '0–30 Days (₹)': g.b0to30,
      '31–60 Days (₹)': g.b31to60,
      '61–90 Days (₹)': g.b61to90,
      '90+ Days (₹)': g.b90plus,
      'Max Days Overdue': g.maxDays,
    }));

    const wb = XLSX.utils.book_new();
    const wsDetail = XLSX.utils.json_to_sheet(detailRows);
    wsDetail['!cols'] = [
      { wch: 11 }, { wch: 26 }, { wch: 10 }, { wch: 16 }, { wch: 10 }, { wch: 28 },
      { wch: 20 }, { wch: 16 }, { wch: 16 }, { wch: 13 }, { wch: 13 }, { wch: 13 }, { wch: 13 }, { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(wb, wsDetail, 'Task-Level Detail');
    const wsClient = XLSX.utils.json_to_sheet(clientRows);
    wsClient['!cols'] = [{ wch: 11 }, { wch: 26 }, { wch: 9 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, wsClient, 'By Client');
    XLSX.writeFile(wb, `receivables-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  return (
    <div>
      {/* Sub-tabs: Outstanding (ageing pivot) | Receipts (payment register) |
          Write-offs (short & excess / bad debt register) — all three live
          here since chasing, collecting, and writing off are one workflow. */}
      <div className="flex gap-0 border-b border-gray-200 mb-4">
        {([
          { id: 'outstanding' as const, label: '📊 Outstanding' },
          { id: 'receipts' as const, label: '🧾 Receipts' },
          { id: 'writeoffs' as const, label: '✂️ Write-offs' },
        ]).map(t => (
          <button key={t.id} onClick={() => setRecvSubTab(t.id)}
            className={`px-5 py-2 text-sm font-medium border-b-2 transition-colors ${recvSubTab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {recvSubTab === 'receipts' ? <ReceiptsTab />
       : recvSubTab === 'writeoffs' ? <WriteOffsRegisterTab /> : <>
      {/* Summary + clickable ageing buckets in one row */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-4">
        <SummaryCard label="Grand Total" value={fmt(totals.grand)} color="text-red-700" sub="Invoices + Opening Bal." />
        <SummaryCard label="From Invoices" value={fmt(totals.invoices)} color="text-red-600" sub={`${rows.filter(r => r.source === 'invoice').length} entries`} />
        <SummaryCard label="Opening Balance" value={fmt(totals.opening)} color="text-amber-700" sub={`${totals.obCount} entries`} />
        {([
          { key: '0-30',  label: '0–30 Days',  value: totals.b0to30,  border: 'border-green-400',  text: 'text-green-700',  ring: 'ring-green-400' },
          { key: '31-60', label: '31–60 Days', value: totals.b31to60, border: 'border-yellow-400', text: 'text-yellow-700', ring: 'ring-yellow-400' },
          { key: '61-90', label: '61–90 Days', value: totals.b61to90, border: 'border-orange-400', text: 'text-orange-700', ring: 'ring-orange-400' },
          { key: '90+',   label: '90+ Days',   value: totals.b90plus, border: 'border-red-500',    text: 'text-red-700',    ring: 'ring-red-500' },
        ] as const).map(b => {
          const active = bucketFilter === b.key;
          return (
            <button key={b.key} type="button"
              onClick={() => setBucketFilter(active ? '' : b.key)}
              className={`card p-4 border-l-4 ${b.border} text-left transition-shadow hover:shadow-md ${active ? `ring-2 ${b.ring}` : ''}`}>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                {b.label}{active && <span className="text-[10px] normal-case text-gray-400">(filtered ✕)</span>}
              </p>
              <p className={`text-lg font-bold ${b.text}`}>{fmt(b.value)}</p>
            </button>
          );
        })}
      </div>

      {/* Filters + opening-balance management */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input className="input-field text-sm max-w-[240px]" placeholder="Search invoice #, client name or code…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <select className="input-field text-sm max-w-[160px]" value={sourceFilter} onChange={e => setSourceFilter(e.target.value as any)}>
          <option value="all">All Sources</option>
          <option value="invoice">Invoices</option>
          <option value="opening">Opening Balance</option>
        </select>
        <select className="input-field text-sm max-w-[200px]" value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
          <option value="">All Clients</option>
          {clientsList.map((c: any) => <option key={c.id} value={c.id}>{c.clientCode} – {c.clientName}</option>)}
        </select>
        <span className="text-sm text-gray-400">{filtered.length} of {rows.length}</span>
        <div className="flex gap-2 ml-auto">
          <button onClick={handleExportExcel} disabled={filtered.length === 0} className="btn-secondary text-sm disabled:opacity-50"
            title="Export the receivables shown below — task-level detail plus a client-wise summary">📊 Export Excel</button>
          {canManageOB && (
            <>
            <button onClick={handleDownloadTemplate} className="btn-secondary text-sm">📥 Template</button>
            <button onClick={() => setShowImport(true)} className="btn-secondary text-sm">📤 Import Excel</button>
            <button onClick={() => setShowAddOB(true)} className="btn-primary text-sm">+ Add Opening Bal.</button>
            </>
          )}
        </div>
      </div>

      {/* Client-wise pivot: click a row to expand its invoice-wise pendencies */}
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="table-header w-8"></th>
              <SortHeader label="Client Code" k="clientCode" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortHeader label="Client" k="clientName" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortHeader label="# Invoices" k="count" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-right" />
              <SortHeader label="Outstanding" k="outstanding" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-right" />
              <SortHeader label="0–30 Days" k="b0to30" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-right" />
              <SortHeader label="31–60 Days" k="b31to60" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-right" />
              <SortHeader label="61–90 Days" k="b61to90" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-right" />
              <SortHeader label="90+ Days" k="b90plus" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-right" />
              <SortHeader label="Max Days Overdue" k="maxDays" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-right" />
              {canManageOB && <th className="table-header text-center">Remind</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={canManageOB ? 11 : 10} className="table-cell text-center text-gray-400 py-10">Loading…</td></tr>
            ) : pivot.length === 0 ? (
              <tr><td colSpan={canManageOB ? 11 : 10} className="table-cell text-center text-gray-400 py-10">No outstanding receivables.</td></tr>
            ) : pivot.map(g => {
              const expanded = expandedClients.has(g.key);
              return (
                <React.Fragment key={g.key}>
                  <tr className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpand(g.key)}>
                    <td className="table-cell text-gray-400">{expanded ? '▾' : '▸'}</td>
                    <td className="table-cell font-mono text-xs text-blue-600">{g.clientCode || '—'}</td>
                    <td className="table-cell"><span className="truncate max-w-[200px] inline-block align-bottom" title={g.clientName}>{g.clientName}</span></td>
                    <td className="table-cell text-right text-gray-600">{g.count}</td>
                    <td className="table-cell text-right font-semibold text-red-600">{fmt(g.outstanding)}</td>
                    <td className="table-cell text-right text-green-700">{g.b0to30 ? fmt(g.b0to30) : '—'}</td>
                    <td className="table-cell text-right text-yellow-700">{g.b31to60 ? fmt(g.b31to60) : '—'}</td>
                    <td className="table-cell text-right text-orange-700">{g.b61to90 ? fmt(g.b61to90) : '—'}</td>
                    <td className="table-cell text-right text-red-700">{g.b90plus ? fmt(g.b90plus) : '—'}</td>
                    <td className="table-cell text-right">{g.maxDays > 0 ? <span className="text-red-600 font-medium">{g.maxDays}d</span> : <span className="text-green-600">—</span>}</td>
                    {canManageOB && (
                      <td className="table-cell text-center">
                        {g.clientId != null && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setReminderTarget({
                              clientId: g.clientId!, clientCode: g.clientCode, clientName: g.clientName,
                              rows: g.rows.map((r) => ({
                                source: r.source, id: r.id, invoiceNumber: r.invoiceNumber,
                                outstanding: r.outstanding, invoiceDate: r.invoiceDate,
                                dueDate: r.dueDate, daysOverdue: r.daysOverdue,
                              })),
                            }); }}
                            title={`Send one consolidated payment reminder covering all ${g.count} outstanding item${g.count === 1 ? '' : 's'}`}
                            aria-label="Send payment reminder"
                            className="p-1 rounded hover:bg-amber-50 text-amber-600">📧</button>
                        )}
                      </td>
                    )}
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={canManageOB ? 11 : 10} className="p-0 bg-gray-50/60">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-100">
                              <th className="table-header pl-10">Source</th>
                              <th className="table-header">Invoice #</th>
                              <th className="table-header text-right">Outstanding</th>
                              <th className="table-header">Invoice Date</th>
                              <th className="table-header">Due Date</th>
                              <th className="table-header">Ageing</th>
                              <th className="table-header text-right">Days Overdue</th>
                              <th className="table-header">Last Follow-up</th>
                              {canManageOB && <th className="table-header text-center">Actions</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {g.rows.map(r => {
                              const bucketClass = r.bucket === '' || r.bucket.toLowerCase() === 'current' || r.bucket.startsWith('0')
                                ? 'bg-green-100 text-green-700'
                                : r.bucket.startsWith('31') ? 'bg-yellow-100 text-yellow-700'
                                : r.bucket.startsWith('61') ? 'bg-orange-100 text-orange-700'
                                : 'bg-red-100 text-red-700';
                              return (
                                <tr key={r.key} className="border-b border-gray-100 hover:bg-white">
                                  <td className="table-cell pl-10">
                                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${r.source === 'invoice' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                                      {r.source === 'invoice' ? 'Invoice' : 'Opening'}
                                    </span>
                                  </td>
                                  <td className="table-cell font-mono">
                                    {r.source === 'invoice' ? (
                                      <button className="text-blue-700 hover:underline" title="Preview invoice"
                                        onClick={(e) => { e.stopPropagation(); openInvoicePreview(r.id); }}>{r.invoiceNumber}</button>
                                    ) : (
                                      <span className="text-blue-700">{r.invoiceNumber}</span>
                                    )}
                                  </td>
                                  <td className="table-cell text-right font-semibold text-red-600">{fmt(r.outstanding)}</td>
                                  <td className="table-cell text-gray-600">{fmtD(r.invoiceDate)}</td>
                                  <td className="table-cell text-gray-600">{r.dueDate ? fmtD(r.dueDate) : '—'}</td>
                                  <td className="table-cell"><span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${bucketClass}`}>{r.bucket || '—'}</span></td>
                                  <td className="table-cell text-right">{r.daysOverdue > 0 ? <span className="text-red-600 font-medium">{r.daysOverdue}d</span> : <span className="text-green-600">—</span>}</td>
                                  <td className="table-cell max-w-[260px]">
                                    {(() => {
                                      const f = (r.source === 'invoice' ? latestFollow.invoice : latestFollow.opening)[r.id];
                                      if (!f) return <span className="text-gray-300">—</span>;
                                      return (
                                        <button className="text-left text-xs w-full" title={`${f.remark}${f.count > 1 ? ` (${f.count} remarks — click for history)` : ''}`}
                                          onClick={(e) => { e.stopPropagation(); setFollowTarget({ source: r.source, id: r.id, label: r.invoiceNumber, clientName: r.clientName }); }}>
                                          <span className="font-medium text-gray-700">{fmtD(f.remarkDate)}</span>
                                          <span className="text-gray-600 block truncate">{f.remark}</span>
                                          {f.nextFollowUpDate && <span className="text-amber-700 block">Next: {fmtD(f.nextFollowUpDate)}</span>}
                                        </button>
                                      );
                                    })()}
                                  </td>
                                  {canManageOB && (
                                    <td className="table-cell">
                                      <div className="flex items-center justify-center gap-2">
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setFollowTarget({ source: r.source, id: r.id, label: r.invoiceNumber, clientName: r.clientName }); }}
                                          title="Follow-up remarks" aria-label="Follow-up remarks"
                                          className="p-1 rounded hover:bg-blue-50 text-blue-600">💬</button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); r.source === 'invoice' ? openInvoicePayment(r.id) : setObPayTarget(r.raw); }}
                                          title={r.hasPayments ? 'View / add receipts' : 'Record payment'}
                                          aria-label="Receipts"
                                          className={`p-1 rounded ${r.hasPayments ? 'hover:bg-blue-50 text-blue-600' : 'hover:bg-green-50 text-green-600'}`}>
                                          {r.hasPayments ? '🧾' : '💰'}
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setWriteOffTarget({
                                            targetType: r.source === 'invoice' ? 'invoice' : 'openingBalance',
                                            targetId: r.id, targetLabel: r.invoiceNumber, outstanding: r.outstanding,
                                            invoiceDate: r.source === 'invoice' ? r.invoiceDate : undefined,
                                          }); }}
                                          title="Write off (Short & Excess / Bad Debt)" aria-label="Write off"
                                          className="p-1 rounded hover:bg-orange-50 text-orange-600">✂️</button>
                                        {(isAdmin || isPartner) && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (r.source === 'invoice') {
                                                onCreateCNFromInvoice?.(r.raw);
                                              } else {
                                                onCreateCNFromOb?.({ id: r.id, invoiceNumber: r.invoiceNumber, invoiceDate: r.invoiceDate, clientId: r.clientId, billingEntityId: r.raw?.billingEntityId, amount: Number(r.raw?.amount ?? 0) });
                                              }
                                            }}
                                            title="Create Credit Note" aria-label="Create Credit Note"
                                            className="p-1 rounded hover:bg-purple-50 text-purple-600">📝</button>
                                        )}
                                        {r.source === 'opening' && r.raw?.pdfFile && (
                                          <button onClick={(e) => { e.stopPropagation(); window.open(uploadsUrl(r.raw.pdfFile), '_blank'); }}
                                            title="View PDF" aria-label="View PDF"
                                            className="p-1 rounded hover:bg-blue-50 text-blue-600">📄</button>
                                        )}
                                        {r.source === 'opening' && isAdmin && (
                                          <button onClick={(e) => { e.stopPropagation(); handleDeleteOB(r.id); }} title="Delete entry" aria-label="Delete"
                                            className="p-1 rounded hover:bg-red-50 text-red-500">🗑</button>
                                        )}
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {followTarget && (
        <FollowUpModal target={followTarget} onClose={() => setFollowTarget(null)} onChanged={loadFollowUps} />
      )}

      {/* Import modal */}
      {showImport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Import Opening Balances</h3>
            <p className="text-xs text-gray-500 mb-3">
              Upload an Excel file. Required columns: Invoice Number, Client Code, Invoice Date, Amount.
              Optional: Due Date, Billing Entity, Notes. Existing (Client + Invoice #) pairs will be updated.
            </p>
            <input type="file" accept=".xlsx,.xls" onChange={e => {
              const file = e.target.files?.[0];
              if (file) handleImport(file);
            }} className="block w-full text-sm" disabled={importing} />
            {importing && <p className="text-xs text-blue-600 mt-2">Importing…</p>}
            {importResult && (
              <div className="mt-3 text-xs">
                <p className="text-green-700">✓ {importResult.inserted} inserted/updated of {importResult.total}</p>
                {importResult.skipped > 0 && <p className="text-red-600">✗ {importResult.skipped} skipped</p>}
                {importResult.errors?.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto bg-red-50 p-2 rounded border border-red-200">
                    {importResult.errors.map((e: any, i: number) => (
                      <div key={i} className="text-red-700">Row {e.row}: {e.reason}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-2 justify-end mt-4">
              <button className="btn-secondary text-sm" onClick={() => { setShowImport(false); setImportResult(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Opening Balance modal */}
      {showAddOB && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Add Opening Balance</h3>
            <form onSubmit={handleAddOB} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Invoice Number *</label>
                  <input className="input-field" required value={obForm.invoiceNumber}
                    onChange={e => setObForm({ ...obForm, invoiceNumber: e.target.value })} />
                </div>
                <div>
                  <label className="label">Client *</label>
                  <select className="input-field" required value={obForm.clientId}
                    onChange={e => setObForm({ ...obForm, clientId: e.target.value })}>
                    <option value="">— Select —</option>
                    {clientsList.map((c: any) => <option key={c.id} value={c.id}>{c.clientCode} – {c.clientName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Invoice Date *</label>
                  <input type="date" className="input-field" required value={obForm.invoiceDate}
                    onChange={e => setObForm({ ...obForm, invoiceDate: e.target.value })} />
                </div>
                <div>
                  <label className="label">Due Date</label>
                  <input type="date" className="input-field" value={obForm.dueDate}
                    onChange={e => setObForm({ ...obForm, dueDate: e.target.value })} />
                </div>
                <div>
                  <label className="label">Amount *</label>
                  <input type="number" step="0.01" className="input-field" required value={obForm.amount}
                    onChange={e => setObForm({ ...obForm, amount: e.target.value })} />
                </div>
                <div>
                  <label className="label">Notes</label>
                  <input className="input-field" value={obForm.notes}
                    onChange={e => setObForm({ ...obForm, notes: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" className="btn-secondary" disabled={obSaving} onClick={() => setShowAddOB(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={obSaving}>{obSaving ? 'Saving…' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {payInv && (
        <PaymentModal invoice={payInv} canEdit={payCanEdit}
          onClose={() => setPayInv(null)}
          onSaved={() => loadAll()} />
      )}
      {obPayTarget && (
        <OpeningBalancePaymentModal ob={obPayTarget} canEdit={canManageOB}
          onClose={() => setObPayTarget(null)}
          onSaved={() => loadAll()} />
      )}
      {previewInv && <GSTPreviewModal invoice={previewInv} onClose={() => setPreviewInv(null)} />}
      {reminderTarget && <SendClientReminderModal target={reminderTarget} onClose={() => setReminderTarget(null)} />}
      {writeOffTarget && (
        <WriteOffModal {...writeOffTarget} onClose={() => setWriteOffTarget(null)} onSaved={() => loadAll()} />
      )}
      </>}
    </div>
  );
};

// ── TDS Tracker Tab ───────────────────────────────────────────────────────────

const TDS_SECTIONS = ['194A','194C','194H','194I','194J','194LA','192','206C','Other'];

const TdsTrackerTab: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
  const [certs, setCerts] = useState<TdsCertificate[]>([]);
  const [recon, setRecon] = useState<TdsReconRow[]>([]);
  const [clients, setClients] = useState<ClientInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'certs' | 'recon'>('certs');
  const [fyFilter, setFyFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<TdsCertificate | null>(null);

  const [fClientId, setFClientId] = useState('');
  const [fCertNo, setFCertNo] = useState('');
  const [fSection, setFSection] = useState('194J');
  const [fFY, setFFY] = useState('');
  const [fPeriod, setFPeriod] = useState('');
  const [fAmount, setFAmount] = useState('');
  const [fRecDate, setFRecDate] = useState('');
  const [fNotes, setFNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const currentFY = (): string => {
    const now = new Date();
    const y = now.getFullYear();
    return now.getMonth() >= 3 ? `${y}-${String(y+1).slice(-2)}` : `${y-1}-${String(y).slice(-2)}`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, rRes, clRes] = await Promise.all([
        getTdsCertificates(fyFilter ? { fiscalYear: fyFilter } : {}),
        getTdsReconciliation(fyFilter ? { fiscalYear: fyFilter } : {}),
        getClients(),
      ]);
      setCerts(cRes.data);
      setRecon(rRes.data);
      setClients(clRes.data);
    } catch { } finally { setLoading(false); }
  }, [fyFilter]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setFClientId(''); setFCertNo(''); setFSection('194J');
    setFFY(currentFY()); setFPeriod(''); setFAmount(''); setFRecDate(''); setFNotes('');
    setErr(''); setShowForm(true);
  };
  const openEdit = (c: TdsCertificate) => {
    setEditing(c);
    setFClientId(String(c.clientId)); setFCertNo(c.certificateNo);
    setFSection(c.section); setFFY(c.fiscalYear); setFPeriod(c.period ?? '');
    setFAmount(String(c.amountAsPer26AS)); setFRecDate(c.receivedDate ? c.receivedDate.slice(0,10) : '');
    setFNotes(c.notes ?? ''); setErr(''); setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setErr('');
    const data = { clientId: Number(fClientId), certificateNo: fCertNo, section: fSection, fiscalYear: fFY, period: fPeriod || undefined, amountAsPer26AS: parseFloat(fAmount), receivedDate: fRecDate || undefined, notes: fNotes || undefined };
    try {
      if (editing) await updateTdsCertificate(editing.id, data);
      else await createTdsCertificate(data);
      setShowForm(false); load();
    } catch (e: any) { setErr(e?.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this certificate?')) return;
    try { await deleteTdsCertificate(id); load(); } catch (e: any) { alert(e?.response?.data?.message || 'Failed.'); }
  };

  const filteredCerts = certs.filter(c =>
    (!clientFilter || String(c.clientId) === clientFilter) &&
    (!fyFilter || c.fiscalYear === fyFilter)
  );

  const fyOptions = Array.from(new Set(certs.map(c => c.fiscalYear))).sort().reverse();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex border-b border-transparent gap-1">
          {(['certs','recon'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-1.5 text-sm rounded-t font-medium ${tab === t ? 'bg-white border border-b-white text-blue-600 border-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
              {t === 'certs' ? '🗂 Certificates' : '🔄 Reconciliation'}
            </button>
          ))}
        </div>
        <select className="input-field w-36" value={fyFilter} onChange={e => setFyFilter(e.target.value)}>
          <option value="">All FYs</option>
          {fyOptions.map(fy => <option key={fy} value={fy}>{fy}</option>)}
        </select>
        {tab === 'certs' && (
          <select className="input-field w-44" value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={String(c.id)}>{c.clientName}</option>)}
          </select>
        )}
        {isAdmin && tab === 'certs' && (
          <button onClick={openAdd} className="btn-primary text-sm ml-auto">+ Add Certificate</button>
        )}
      </div>

      {tab === 'certs' && (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="table-header">Client</th>
                <th className="table-header">Certificate No</th>
                <th className="table-header">Section</th>
                <th className="table-header">FY</th>
                <th className="table-header">Quarter</th>
                <th className="table-header text-right">Amount as per 26AS</th>
                <th className="table-header">Received</th>
                <th className="table-header">Notes</th>
                {isAdmin && <th className="table-header">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="table-cell text-center text-gray-400 py-10">Loading…</td></tr>
              ) : filteredCerts.length === 0 ? (
                <tr><td colSpan={9} className="table-cell text-center text-gray-400 py-10">No certificates found.</td></tr>
              ) : filteredCerts.map(c => (
                <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="table-cell font-medium">{c.client?.clientName ?? '—'}</td>
                  <td className="table-cell font-mono text-xs">{c.certificateNo}</td>
                  <td className="table-cell"><span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded text-xs font-medium">{c.section}</span></td>
                  <td className="table-cell text-gray-600">{c.fiscalYear}</td>
                  <td className="table-cell text-gray-500">{c.period ?? '—'}</td>
                  <td className="table-cell text-right font-semibold text-orange-700">{fmt(c.amountAsPer26AS)}</td>
                  <td className="table-cell text-gray-500">{c.receivedDate ? fmtD(c.receivedDate) : '—'}</td>
                  <td className="table-cell text-gray-400 text-xs max-w-[120px] truncate">{c.notes ?? '—'}</td>
                  {isAdmin && (
                    <td className="table-cell">
                      <div className="flex gap-1">
                        <button onClick={() => openEdit(c)} className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">Edit</button>
                        <button onClick={() => handleDelete(c.id)} className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">Del</button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'recon' && (
        <div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-sm text-blue-800">
            Compares TDS booked in invoices vs TDS certificates received from clients. Positive variance = more TDS on cert than invoiced (good). Negative variance = shortfall.
          </div>
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="table-header">Client</th>
                  <th className="table-header">Section</th>
                  <th className="table-header text-right">Invoice TDS</th>
                  <th className="table-header text-right">Certificate (26AS)</th>
                  <th className="table-header text-right">Variance</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="table-cell text-center text-gray-400 py-10">Loading…</td></tr>
                ) : recon.length === 0 ? (
                  <tr><td colSpan={5} className="table-cell text-center text-gray-400 py-10">No TDS data found.</td></tr>
                ) : recon.map((r, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="table-cell font-medium">{r.clientName}</td>
                    <td className="table-cell"><span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded text-xs font-medium">{r.section}</span></td>
                    <td className="table-cell text-right">{fmt(r.invoiceTotal)}</td>
                    <td className="table-cell text-right">{fmt(r.certTotal)}</td>
                    <td className="table-cell text-right">
                      <span className={r.variance >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                        {r.variance >= 0 ? '+' : ''}{fmt(r.variance)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">{editing ? 'Edit' : 'Add'} TDS Certificate</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>
            <form onSubmit={handleSave} className="px-6 py-4 space-y-3">
              {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">{err}</div>}
              <div><label className="label">Client *</label>
                <select className="input-field" value={fClientId} onChange={e => setFClientId(e.target.value)} required>
                  <option value="">Select client…</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.clientName}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Certificate No *</label>
                  <input className="input-field" value={fCertNo} onChange={e => setFCertNo(e.target.value)} required />
                </div>
                <div><label className="label">TDS Section *</label>
                  <select className="input-field" value={fSection} onChange={e => setFSection(e.target.value)}>
                    {TDS_SECTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Fiscal Year *</label>
                  <input className="input-field" value={fFY} onChange={e => setFFY(e.target.value)} placeholder="e.g. 2025-26" required />
                </div>
                <div><label className="label">Quarter</label>
                  <select className="input-field" value={fPeriod} onChange={e => setFPeriod(e.target.value)}>
                    <option value="">—</option>
                    {['Q1','Q2','Q3','Q4'].map(q => <option key={q} value={q}>{q}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Amount as per 26AS (₹) *</label>
                  <input type="number" step="0.01" min="0" className="input-field" value={fAmount} onChange={e => setFAmount(e.target.value)} required />
                </div>
                <div><label className="label">Date Received</label>
                  <input type="date" className="input-field" value={fRecDate} onChange={e => setFRecDate(e.target.value)} />
                </div>
              </div>
              <div><label className="label">Notes</label>
                <input className="input-field" value={fNotes} onChange={e => setFNotes(e.target.value)} />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Credit Notes Tab ──────────────────────────────────────────────────────────

type CreditNoteReason = 'SALES_RETURN' | 'PRICE_ADJUSTMENT' | 'POST_SUPPLY_DISCOUNT' | 'DEFICIENCY_IN_SERVICE' | 'INVOICE_ERROR' | 'OTHER';
type CreditNoteStatus = 'DRAFT' | 'ISSUED' | 'CANCELLED';

interface CreditNote {
  id: number;
  creditNoteNumber: string;
  creditNoteDate: string;
  originalInvoiceNumber: string;
  originalInvoiceDate: string;
  client: ClientInfo;
  billingEntity?: BillingEntityFull;
  clientGstin?: ClientGstin;
  invoice?: { id: number; invoiceNumber: string };
  openingBalance?: { id: number; invoiceNumber: string };
  amount: number;
  taxType: TaxType;
  cgstRate?: number; sgstRate?: number; igstRate?: number;
  cgstAmount?: number; sgstAmount?: number; igstAmount?: number;
  totalAmount?: number;
  hsnSacCode?: string;
  signatory?: Signatory | null;
  reason: CreditNoteReason;
  notes?: string;
  status: CreditNoteStatus;
  lineItems: LineItem[];
  createdAt: string;
  updatedAt: string;
}

const CN_REASON_LABEL: Record<CreditNoteReason, string> = {
  SALES_RETURN: 'Sales Return',
  PRICE_ADJUSTMENT: 'Price Adjustment',
  POST_SUPPLY_DISCOUNT: 'Post-Supply Discount',
  DEFICIENCY_IN_SERVICE: 'Deficiency in Service',
  INVOICE_ERROR: 'Invoice Error',
  OTHER: 'Other',
};

const CN_STATUS_CLASS: Record<CreditNoteStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-600',
  ISSUED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-500 line-through',
};
const CN_STATUS_LABEL: Record<CreditNoteStatus, string> = {
  DRAFT: 'Draft', ISSUED: 'Issued', CANCELLED: 'Cancelled',
};

interface CreditNotesTabProps {
  clients: Array<{ id: number; clientCode: string; clientName: string }>;
  billingEntities: BillingEntityFull[];
  isAdmin: boolean;
  /** Issue / edit / cancel are open to partners; only delete stays Admin-only. */
  isPartner?: boolean;
  defaultInvoice?: Invoice | null;
  defaultOb?: { id: number; invoiceNumber: string; invoiceDate: string; clientId: number; billingEntityId?: number | null; amount: number } | null;
  onDefaultConsumed?: () => void;
  docType: InvoiceDocType;
  onDocTypeChange: (v: InvoiceDocType) => void;
}

const CreditNotesTab: React.FC<CreditNotesTabProps> = ({ clients, billingEntities, isAdmin, isPartner, defaultInvoice, defaultOb, onDefaultConsumed, docType, onDocTypeChange }) => {
  const [notes, setNotes] = useState<CreditNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CreditNote | null>(null);
  const [detail, setDetail] = useState<CreditNote | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [cnDateFrom, setCnDateFrom] = useState('');
  const [cnDateTo, setCnDateTo] = useState('');

  // Form state
  const [fDate, setFDate] = useState(today());
  const [fOrigInvNum, setFOrigInvNum] = useState('');
  const [fOrigInvDate, setFOrigInvDate] = useState('');
  const [fClientId, setFClientId] = useState('');
  const [fBillingEntityId, setFBillingEntityId] = useState('');
  const [fTaxType, setFTaxType] = useState<TaxType>('NONE');
  const [fCgstRate, setFCgstRate] = useState('9');
  const [fSgstRate, setFSgstRate] = useState('9');
  const [fIgstRate, setFIgstRate] = useState('18');
  const [fHsnSac, setFHsnSac] = useState('');
  const [fReason, setFReason] = useState<CreditNoteReason>('INVOICE_ERROR');
  const [fNotes, setFNotes] = useState('');
  const [fStatus, setFStatus] = useState<CreditNoteStatus>('DRAFT');
  const [fLineItems, setFLineItems] = useState<LineItem[]>([{ slNo: 1, description: '', quantity: 1, rate: 0, amount: 0 }]);
  const [fSignatoryId, setFSignatoryId] = useState('');
  const [fInvoiceId, setFInvoiceId] = useState<number | null>(null); // FK link to an in-app invoice (settles it)
  const [fObId, setFObId] = useState<number | null>(null); // FK link to an opening balance (settles it; no task)
  const [saving, setSaving] = useState(false);

  // Source documents for the "pick original" dropdown (invoices + opening balances).
  const [allInvoices, setAllInvoices] = useState<any[]>([]);
  const [allObs, setAllObs] = useState<any[]>([]);
  useEffect(() => {
    getInvoices().then(r => setAllInvoices((r.data || []).filter((i: any) => i.status !== 'DRAFT' && i.status !== 'CANCELLED'))).catch(() => {});
    getOpeningBalances().then(r => setAllObs((r.data || []).filter((o: any) => o.status !== 'PAID' && o.status !== 'WRITTEN_OFF'))).catch(() => {});
  }, []);

  // Documents for the currently-selected client, value-keyed as inv:<id> / ob:<id>.
  const docOptions = useMemo(() => {
    const cid = fClientId ? Number(fClientId) : null;
    const invs = allInvoices
      .filter((i: any) => !cid || (i.client?.id ?? i.clientId) === cid)
      .map((i: any) => ({ key: `inv:${i.id}`, label: `${i.invoiceNumber} · Invoice · ₹${Number(i.totalAmount ?? i.amount).toLocaleString('en-IN')}`, raw: i, kind: 'inv' as const }));
    const obs = allObs
      .filter((o: any) => !cid || (o.clientId ?? o.client?.id) === cid)
      .map((o: any) => ({ key: `ob:${o.id}`, label: `${o.invoiceNumber} · Opening Bal · ₹${Number(o.amount).toLocaleString('en-IN')}`, raw: o, kind: 'ob' as const }));
    return [...invs, ...obs];
  }, [allInvoices, allObs, fClientId]);

  const selectedDocKey = fInvoiceId ? `inv:${fInvoiceId}` : fObId ? `ob:${fObId}` : '';

  // Auto-fill the form from the chosen original document.
  const pickDocument = (key: string) => {
    if (!key) { setFInvoiceId(null); setFObId(null); return; }
    const opt = docOptions.find(d => d.key === key);
    if (!opt) return;
    const d = opt.raw;
    setFOrigInvNum(d.invoiceNumber || '');
    setFOrigInvDate((d.invoiceDate || '').split('T')[0]);
    if (opt.kind === 'inv') {
      setFInvoiceId(d.id); setFObId(null);
      setFClientId(String(d.client?.id ?? d.clientId ?? fClientId));
      setFBillingEntityId(d.billingEntity ? String(d.billingEntity.id) : (d.billingEntityId ? String(d.billingEntityId) : ''));
      setFTaxType(d.taxType ?? 'NONE');
      setFCgstRate(String(d.cgstRate ?? 9)); setFSgstRate(String(d.sgstRate ?? 9)); setFIgstRate(String(d.igstRate ?? 18));
      setFHsnSac(d.hsnSacCode ?? '');
      setFLineItems(d.lineItems?.length
        ? d.lineItems.map((li: any, i: number) => ({ ...li, id: undefined, slNo: i + 1 }))
        : [{ slNo: 1, description: '', quantity: 1, rate: 0, amount: 0 }]);
    } else {
      // Opening balance — no tax breakdown / line items; seed a single line at the OB value.
      setFObId(d.id); setFInvoiceId(null);
      setFClientId(String(d.clientId ?? d.client?.id ?? fClientId));
      setFBillingEntityId(d.billingEntityId ? String(d.billingEntityId) : '');
      setFTaxType('NONE');
      const outstanding = Math.max(0, Number(d.amount) - Number(d.amountPaid ?? 0) - Number(d.creditedAmount ?? 0) - Number(d.writtenOffAmount ?? 0));
      setFLineItems([{ slNo: 1, description: `Against opening balance ${d.invoiceNumber}`, quantity: 1, rate: outstanding, amount: outstanding }]);
    }
  };

  const cnSignatories = useMemo(
    () => billingEntities.find(b => String(b.id) === fBillingEntityId)?.signatories ?? [],
    [billingEntities, fBillingEntityId]
  );

  // Auto-select default signatory when billing entity changes in CN form —
  // but keep the current selection if it still belongs to this entity (e.g.
  // openEdit just set it from the saved CN); only reassign when it doesn't.
  useEffect(() => {
    if (fSignatoryId && cnSignatories.some(s => String(s.id) === fSignatoryId)) return;
    const def = cnSignatories.find(s => s.isDefault);
    setFSignatoryId(def ? String(def.id) : (cnSignatories.length === 1 ? String(cnSignatories[0].id) : ''));
    // eslint-disable-next-line
  }, [fBillingEntityId, cnSignatories]);

  const fetch = useCallback(() => {
    setLoading(true);
    const params: any = {};
    if (filterStatus) params.status = filterStatus;
    if (filterClient) params.clientId = filterClient;
    getCreditNotes(params).then(r => { setNotes(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [filterStatus, filterClient]);

  useEffect(() => { fetch(); }, [fetch]);

  useEffect(() => {
    if (!defaultInvoice) return;
    setEditing(null);
    setFDate(today());
    setFInvoiceId(defaultInvoice.id); setFObId(null);
    setFOrigInvNum(defaultInvoice.invoiceNumber);
    setFOrigInvDate(defaultInvoice.invoiceDate.split('T')[0]);
    setFClientId(String(defaultInvoice.client?.id ?? ''));
    setFBillingEntityId(defaultInvoice.billingEntity ? String(defaultInvoice.billingEntity.id) : '');
    setFTaxType(defaultInvoice.taxType);
    setFCgstRate(String(defaultInvoice.cgstRate ?? 9));
    setFSgstRate(String(defaultInvoice.sgstRate ?? 9));
    setFIgstRate(String(defaultInvoice.igstRate ?? 18));
    setFHsnSac(defaultInvoice.hsnSacCode ?? '');
    setFReason('INVOICE_ERROR');
    setFNotes('');
    setFStatus('DRAFT');
    setFLineItems(defaultInvoice.lineItems?.length
      ? defaultInvoice.lineItems.map((li, i) => ({ ...li, id: undefined, slNo: i + 1 }))
      : [{ slNo: 1, description: '', quantity: 1, rate: 0, amount: 0 }]);
    setShowForm(true);
    onDefaultConsumed?.();
  }, [defaultInvoice]); // open CN form pre-filled when triggered from invoice row

  useEffect(() => {
    if (!defaultOb) return;
    setEditing(null);
    setFDate(today());
    setFInvoiceId(null); setFObId(defaultOb.id);
    setFOrigInvNum(defaultOb.invoiceNumber);
    setFOrigInvDate(defaultOb.invoiceDate.split('T')[0]);
    setFClientId(String(defaultOb.clientId));
    setFBillingEntityId(defaultOb.billingEntityId ? String(defaultOb.billingEntityId) : '');
    setFTaxType('NONE'); setFCgstRate('9'); setFSgstRate('9'); setFIgstRate('18');
    setFHsnSac(''); setFReason('INVOICE_ERROR'); setFNotes(''); setFStatus('DRAFT');
    setFLineItems([{ slNo: 1, description: '', quantity: 1, rate: 0, amount: 0 }]);
    setShowForm(true);
    onDefaultConsumed?.();
  }, [defaultOb]);

  const lineItemTotal = useMemo(() =>
    fLineItems.reduce((s, li) => s + (Number(li.amount) || 0), 0),
    [fLineItems]);

  const taxPreview = useMemo(() => {
    const amt = lineItemTotal || 0;
    if (fTaxType === 'CGST_SGST') {
      const c = parseFloat(fCgstRate) || 0;
      const s = parseFloat(fSgstRate) || 0;
      return { cgst: +(amt * c / 100).toFixed(2), sgst: +(amt * s / 100).toFixed(2), igst: 0, total: +(amt + amt * c / 100 + amt * s / 100).toFixed(2) };
    }
    if (fTaxType === 'IGST') {
      const i = parseFloat(fIgstRate) || 0;
      return { cgst: 0, sgst: 0, igst: +(amt * i / 100).toFixed(2), total: +(amt + amt * i / 100).toFixed(2) };
    }
    return { cgst: 0, sgst: 0, igst: 0, total: amt };
  }, [fTaxType, fCgstRate, fSgstRate, fIgstRate, lineItemTotal]);

  const openAdd = () => {
    setEditing(null);
    setFDate(today()); setFInvoiceId(null); setFObId(null); setFOrigInvNum(''); setFOrigInvDate('');
    setFClientId(''); setFBillingEntityId(''); setFSignatoryId('');
    setFTaxType('NONE'); setFCgstRate('9'); setFSgstRate('9'); setFIgstRate('18');
    setFHsnSac(''); setFReason('INVOICE_ERROR'); setFNotes(''); setFStatus('DRAFT');
    setFLineItems([{ slNo: 1, description: '', quantity: 1, rate: 0, amount: 0 }]);
    setShowForm(true);
  };

  const openEdit = (cn: CreditNote) => {
    setEditing(cn);
    setFDate(cn.creditNoteDate.split('T')[0]);
    setFInvoiceId(cn.invoice?.id ?? null);
    setFObId((cn as any).openingBalance?.id ?? null);
    setFOrigInvNum(cn.originalInvoiceNumber);
    setFOrigInvDate(cn.originalInvoiceDate.split('T')[0]);
    setFClientId(String(cn.client.id));
    setFBillingEntityId(cn.billingEntity ? String(cn.billingEntity.id) : '');
    setFSignatoryId(cn.signatory ? String(cn.signatory.id) : '');
    setFTaxType(cn.taxType);
    setFCgstRate(cn.cgstRate ? String(cn.cgstRate) : '9');
    setFSgstRate(cn.sgstRate ? String(cn.sgstRate) : '9');
    setFIgstRate(cn.igstRate ? String(cn.igstRate) : '18');
    setFHsnSac(cn.hsnSacCode || '');
    setFReason(cn.reason);
    setFNotes(cn.notes || '');
    setFStatus(cn.status);
    setFLineItems(cn.lineItems.length ? cn.lineItems : [{ slNo: 1, description: '', quantity: 1, rate: 0, amount: 0 }]);
    setShowForm(true);
  };

  const updateLineItem = (idx: number, field: keyof LineItem, value: any) => {
    setFLineItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'quantity' || field === 'rate') {
        const q = field === 'quantity' ? parseFloat(value) : parseFloat(String(next[idx].quantity));
        const r = field === 'rate' ? parseFloat(value) : parseFloat(String(next[idx].rate));
        next[idx].amount = parseFloat(((q || 0) * (r || 0)).toFixed(2));
      }
      return next;
    });
  };

  const addLineItem = () => setFLineItems(prev => [...prev, { slNo: prev.length + 1, description: '', quantity: 1, rate: 0, amount: 0 }]);
  const removeLineItem = (idx: number) => setFLineItems(prev => prev.filter((_, i) => i !== idx).map((li, i) => ({ ...li, slNo: i + 1 })));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      creditNoteDate: fDate,
      invoiceId: fInvoiceId,
      openingBalanceId: fObId,
      originalInvoiceNumber: fOrigInvNum,
      originalInvoiceDate: fOrigInvDate,
      clientId: parseInt(fClientId),
      billingEntityId: fBillingEntityId ? parseInt(fBillingEntityId) : null,
      amount: lineItemTotal,
      taxType: fTaxType,
      cgstRate: fTaxType === 'CGST_SGST' ? parseFloat(fCgstRate) : null,
      sgstRate: fTaxType === 'CGST_SGST' ? parseFloat(fSgstRate) : null,
      igstRate: fTaxType === 'IGST' ? parseFloat(fIgstRate) : null,
      hsnSacCode: fHsnSac || null,
      reason: fReason,
      notes: fNotes || null,
      status: fStatus,
      signatoryId: fSignatoryId ? Number(fSignatoryId) : undefined,
      lineItems: fLineItems.filter(li => li.description),
    };
    try {
      if (editing) await updateCreditNote(editing.id, payload);
      else await createCreditNote(payload);
      setShowForm(false);
      fetch();
    } catch (err: any) {
      alert(err?.response?.data?.error || err?.response?.data?.message || 'Failed to save credit note');
    } finally { setSaving(false); }
  };

  const handleCancel = async (cn: CreditNote) => {
    if (!window.confirm(`Cancel credit note ${cn.creditNoteNumber}?`)) return;
    try { await cancelCreditNote(cn.id); fetch(); }
    catch (err: any) { alert(err?.response?.data?.error || err?.response?.data?.message || 'Failed to cancel credit note'); }
  };

  const handleIssue = async (cn: CreditNote) => {
    if (!window.confirm(`Mark ${cn.creditNoteNumber} as Issued? This will reduce the outstanding on the linked invoice.`)) return;
    try { await updateCreditNote(cn.id, { status: 'ISSUED' }); fetch(); }
    catch (err: any) { alert(err?.response?.data?.error || err?.response?.data?.message || 'Failed to issue credit note'); }
  };

  const handleDelete = async (cn: CreditNote) => {
    if (!window.confirm(`Delete draft credit note ${cn.creditNoteNumber}?`)) return;
    try { await deleteCreditNote(cn.id); fetch(); }
    catch (err: any) { alert(err?.response?.data?.error || err?.response?.data?.message || 'Failed to delete credit note'); }
  };

  const visible = notes.filter(n => {
    if (!filterStatus && n.status === 'CANCELLED') return false;
    if (filterStatus && n.status !== filterStatus) return false;
    if (filterClient && n.client.id !== parseInt(filterClient)) return false;
    if ((cnDateFrom || cnDateTo) && !inDateRange(n.creditNoteDate, cnDateFrom, cnDateTo)) return false;
    return true;
  });

  // Summary stats
  const totalCNAmount = useMemo(() => visible.reduce((s, cn) => s + Number(cn.totalAmount ?? cn.amount), 0), [visible]);
  const issuedCount = useMemo(() => visible.filter(cn => cn.status === 'ISSUED').length, [visible]);
  const draftCount = useMemo(() => visible.filter(cn => cn.status === 'DRAFT').length, [visible]);

  const handleExportCSV = () => {
    const headers = ['CN#', 'Client', 'GSTIN', 'Sale Type', 'Against Invoice', 'Date', 'Status', 'Reason', 'Taxable', 'Tax', 'Total'];
    const csvRows = visible.map(cn => {
      const taxAmt = Number(cn.cgstAmount ?? 0) + Number(cn.sgstAmount ?? 0) + Number(cn.igstAmount ?? 0);
      return [cn.creditNoteNumber, cn.client.clientName, cn.clientGstin?.gstin ?? '', saleTypeLabel(cn.clientGstin?.gstType, cn.taxType), cn.originalInvoiceNumber, fmtD(cn.creditNoteDate), cn.status, CN_REASON_LABEL[cn.reason], cn.amount, taxAmt, cn.totalAmount ?? cn.amount];
    });
    const csv = [headers, ...csvRows].map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'credit-notes.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <SummaryCard label="Total Credits" value={`−${fmt(totalCNAmount)}`} color="text-red-600" />
        <SummaryCard label="Issued" value={String(issuedCount)} color="text-green-700" sub={issuedCount === 1 ? '1 credit note' : `${issuedCount} credit notes`} />
        <SummaryCard label="Draft" value={String(draftCount)} color={draftCount > 0 ? 'text-amber-600' : 'text-gray-800'} sub={draftCount > 0 ? 'Pending issue' : 'None pending'} />
      </div>

      {/* Filters + actions */}
      <div className="flex flex-wrap gap-3 items-center">
        <DocTypeSelect value={docType} onChange={onDocTypeChange} />
        <select className="input-field w-44" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="ISSUED">Issued</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        <select className="input-field w-56" value={filterClient} onChange={e => setFilterClient(e.target.value)}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.clientName}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={handleExportCSV} className="btn-secondary text-sm">↓ Export CSV</button>
          <RegisterDownload label="CN Register" fileSlug="credit-note-register"
            title="Detailed credit note register (CSV) for the accountant — pick any date range"
            fetchRegister={async ({ from, to, includeCancelled }) => {
              const params: any = { from, to };
              if (!includeCancelled) params.excludeCancelled = '1';
              return new Blob([(await exportCreditNoteRegister(params)).data], { type: 'text/csv' });
            }} />
          <button onClick={openAdd} className="btn-primary text-sm">+ New Credit Note</button>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-x-auto">
        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="text-center py-16 text-gray-400">No credit notes found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">CN#</th>
                <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">Client</th>
                <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">Against Invoice</th>
                <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Taxable</th>
                <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Tax</th>
                <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Total</th>
                <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">
                  <DateRangeFilter
                    label="Date"
                    fromValue={cnDateFrom}
                    toValue={cnDateTo}
                    onChange={(f, t) => { setCnDateFrom(f); setCnDateTo(t); }}
                  />
                </th>
                <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">Status</th>
                <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">TDS</th>
                <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Paid</th>
                <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Outstanding</th>
                <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(cn => {
                const taxAmt = Number(cn.cgstAmount ?? 0) + Number(cn.sgstAmount ?? 0) + Number(cn.igstAmount ?? 0);
                const total = Number(cn.totalAmount ?? cn.amount);
                return (
                  <tr key={cn.id} className={`border-b border-gray-50 hover:bg-gray-50 ${cn.status === 'CANCELLED' ? 'line-through text-gray-400' : ''}`}>
                    <td className="px-2 py-2 font-mono font-medium text-purple-700 border-b border-gray-100 whitespace-nowrap">
                      <button onClick={() => setDetail(cn)} className="hover:underline">{cn.creditNoteNumber}</button>
                    </td>
                    <td className="px-2 py-2 text-gray-700 border-b border-gray-100 max-w-[140px] truncate" title={cn.client.clientName}>{cn.client.clientName}</td>
                    <td className="px-2 py-2 border-b border-gray-100 max-w-[180px]">
                      <div className="truncate text-xs text-gray-500" title={cn.originalInvoiceNumber}>{cn.originalInvoiceNumber}</div>
                      <div className="text-xs text-gray-400">{CN_REASON_LABEL[cn.reason]}</div>
                    </td>
                    <td className="px-2 py-2 text-right border-b border-gray-100 whitespace-nowrap text-red-600 font-medium">−{fmt(Number(cn.amount))}</td>
                    <td className="px-2 py-2 text-right border-b border-gray-100 whitespace-nowrap">
                      {taxAmt > 0 ? <span className="text-red-600">−{fmt(taxAmt)}</span> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-2 py-2 text-right font-semibold border-b border-gray-100 whitespace-nowrap text-red-600">−{fmt(total)}</td>
                    <td className="px-2 py-2 text-gray-600 border-b border-gray-100 whitespace-nowrap">{fmtD(cn.creditNoteDate)}</td>
                    <td className="px-2 py-2 border-b border-gray-100">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${CN_STATUS_CLASS[cn.status]}`}>{CN_STATUS_LABEL[cn.status]}</span>
                    </td>
                    <td className="px-2 py-2 text-right border-b border-gray-100"><span className="text-gray-300">—</span></td>
                    <td className="px-2 py-2 text-right border-b border-gray-100"><span className="text-gray-300">—</span></td>
                    <td className="px-2 py-2 text-right border-b border-gray-100"><span className="text-gray-300">—</span></td>
                    <td className="px-2 py-2 border-b border-gray-100 whitespace-nowrap">
                      <div className="flex items-center gap-0.5">
                        {cn.status === 'DRAFT' && (isAdmin || isPartner) && (
                          <button onClick={() => handleIssue(cn)} title="Mark as Issued" className="p-1 rounded hover:bg-green-50 text-green-600">✅</button>
                        )}
                        {cn.status === 'DRAFT' && (isAdmin || isPartner) && (
                          <button onClick={() => openEdit(cn)} title="Edit" className="p-1 rounded hover:bg-indigo-50 text-indigo-600">✏</button>
                        )}
                        {cn.status !== 'CANCELLED' && (isAdmin || isPartner) && (
                          <button onClick={() => handleCancel(cn)} title="Cancel" className="p-1 rounded hover:bg-orange-50 text-orange-600">🚫</button>
                        )}
                        {cn.status === 'DRAFT' && isAdmin && (
                          <button onClick={() => handleDelete(cn)} title="Delete" className="p-1 rounded hover:bg-red-50 text-red-600">🗑</button>
                        )}
                        <button onClick={() => setDetail(cn)} title="View details" className="p-1 rounded hover:bg-gray-100 text-gray-600">🔍</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="text-lg font-semibold">{detail.creditNoteNumber}</h2>
                <p className="text-xs text-gray-400">{fmtD(detail.creditNoteDate)}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { generateCreditNotePDF(detail).catch(() => alert('PDF failed.')); }}
                  className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3 py-1.5 rounded transition-colors"
                >📥 PDF</button>
                <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
              </div>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="text-gray-500">Client</span><p className="font-medium">{detail.client.clientName}</p></div>
                <div><span className="text-gray-500">Status</span><p><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CN_STATUS_CLASS[detail.status]}`}>{detail.status}</span></p></div>
                <div><span className="text-gray-500">Original Invoice #</span><p className="font-medium">{detail.originalInvoiceNumber}</p></div>
                <div><span className="text-gray-500">Original Invoice Date</span><p>{fmtD(detail.originalInvoiceDate)}</p></div>
                {detail.billingEntity && <div><span className="text-gray-500">Billing Entity</span><p>{detail.billingEntity.name}</p></div>}
                <div><span className="text-gray-500">Reason</span><p>{CN_REASON_LABEL[detail.reason]}</p></div>
              </div>
              {detail.notes && <div className="bg-gray-50 rounded-lg p-3"><span className="text-gray-500">Notes: </span>{detail.notes}</div>}

              {/* Line Items */}
              {detail.lineItems.length > 0 && (
                <div>
                  <p className="font-medium mb-2 text-gray-700">Line Items</p>
                  <table className="w-full text-xs border border-gray-200 rounded">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1.5 text-left">#</th>
                        <th className="px-2 py-1.5 text-left">Description</th>
                        <th className="px-2 py-1.5 text-right">Qty</th>
                        <th className="px-2 py-1.5 text-right">Rate</th>
                        <th className="px-2 py-1.5 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.lineItems.map(li => (
                        <tr key={li.id} className="border-t border-gray-100">
                          <td className="px-2 py-1.5">{li.slNo}</td>
                          <td className="px-2 py-1.5">{li.description}{li.hsnSac && <span className="text-gray-400 ml-1">({li.hsnSac})</span>}</td>
                          <td className="px-2 py-1.5 text-right">{li.quantity}</td>
                          <td className="px-2 py-1.5 text-right">{fmt(li.rate)}</td>
                          <td className="px-2 py-1.5 text-right">{fmt(li.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tax Summary */}
              <div className="bg-blue-50 rounded-lg p-4 space-y-1">
                <div className="flex justify-between"><span>Taxable Amount</span><span className="font-medium">{fmt(detail.amount)}</span></div>
                {detail.taxType === 'CGST_SGST' && <>
                  <div className="flex justify-between text-gray-600"><span>CGST @ {detail.cgstRate}%</span><span>{fmt(detail.cgstAmount ?? 0)}</span></div>
                  <div className="flex justify-between text-gray-600"><span>SGST @ {detail.sgstRate}%</span><span>{fmt(detail.sgstAmount ?? 0)}</span></div>
                </>}
                {detail.taxType === 'IGST' && (
                  <div className="flex justify-between text-gray-600"><span>IGST @ {detail.igstRate}%</span><span>{fmt(detail.igstAmount ?? 0)}</span></div>
                )}
                <div className="flex justify-between font-bold text-blue-800 border-t border-blue-200 pt-1">
                  <span>Total Credit Amount</span><span>{fmt(detail.totalAmount ?? detail.amount)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[95vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">{editing ? 'Edit Credit Note' : 'New Credit Note'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Credit Note Date *</label>
                  <input type="date" className="input-field" value={fDate} onChange={e => setFDate(e.target.value)} required />
                </div>
                <div>
                  <label className="label">Status</label>
                  <select className="input-field" value={fStatus} onChange={e => setFStatus(e.target.value as CreditNoteStatus)}>
                    <option value="DRAFT">Draft</option>
                    <option value="ISSUED">Issued</option>
                  </select>
                </div>
              </div>

              {/* Pick client first, then the original invoice / opening balance — the
                  rest of the form (dates, tax, line items) auto-fills from it. */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Client *</label>
                  <select className="input-field" value={fClientId}
                    onChange={e => { setFClientId(e.target.value); setFInvoiceId(null); setFObId(null); }} required>
                    <option value="">Select client…</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.clientName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Original Document {fClientId ? '' : <span className="text-gray-400 font-normal">(select client first)</span>}</label>
                  <select className="input-field" value={selectedDocKey} onChange={e => pickDocument(e.target.value)} disabled={!fClientId}>
                    <option value="">— Select invoice / opening balance —</option>
                    {docOptions.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Original Invoice Number *</label>
                  <input className="input-field" placeholder="e.g. INV-001" value={fOrigInvNum} onChange={e => setFOrigInvNum(e.target.value)} required />
                </div>
                <div>
                  <label className="label">Original Invoice Date *</label>
                  <input type="date" className="input-field" value={fOrigInvDate} onChange={e => setFOrigInvDate(e.target.value)} required />
                </div>
              </div>

              <div>
                <label className="label">Billing Entity</label>
                <select className="input-field" value={fBillingEntityId} onChange={e => setFBillingEntityId(e.target.value)}>
                  <option value="">None</option>
                  {billingEntities.map(be => <option key={be.id} value={be.id}>{be.name}</option>)}
                </select>
                {fObId && <p className="text-xs text-amber-600 mt-1">Against an opening balance — this credit will not affect any task billing.</p>}
                {fInvoiceId && <p className="text-xs text-blue-600 mt-1">Against an invoice — issuing this will reduce the linked task's billed amount.</p>}
              </div>

              {/* Signatory row */}
              {cnSignatories.length > 0 && (
                <div>
                  <label className="label">Signatory</label>
                  <select className="input-field" value={fSignatoryId} onChange={e => setFSignatoryId(e.target.value)}>
                    <option value="">— None / Default —</option>
                    {cnSignatories.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name}{s.designation ? ` (${s.designation})` : ''}{s.isDefault ? ' ★' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Reason *</label>
                  <select className="input-field" value={fReason} onChange={e => setFReason(e.target.value as CreditNoteReason)} required>
                    {Object.entries(CN_REASON_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">HSN / SAC Code</label>
                  <input className="input-field" placeholder="e.g. 998212" value={fHsnSac} onChange={e => setFHsnSac(e.target.value)} />
                </div>
              </div>

              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label mb-0">Line Items</label>
                  <button type="button" onClick={addLineItem} className="text-blue-600 text-xs hover:underline">+ Add Row</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border border-gray-200 rounded">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1.5 text-left w-8">#</th>
                        <th className="px-2 py-1.5 text-left">Description *</th>
                        <th className="px-2 py-1.5 text-left w-20">HSN/SAC</th>
                        <th className="px-2 py-1.5 text-right w-16">Qty</th>
                        <th className="px-2 py-1.5 text-right w-24">Rate</th>
                        <th className="px-2 py-1.5 text-right w-24">Amount</th>
                        <th className="px-2 py-1.5 w-6"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {fLineItems.map((li, idx) => (
                        <tr key={idx} className="border-t border-gray-100">
                          <td className="px-2 py-1">{li.slNo}</td>
                          <td className="px-1 py-1"><textarea className="input-field text-xs py-1 resize-y leading-snug" rows={2} value={li.description} onChange={e => updateLineItem(idx, 'description', e.target.value)} /></td>
                          <td className="px-1 py-1"><input className="input-field text-xs py-1" value={li.hsnSac || ''} onChange={e => updateLineItem(idx, 'hsnSac', e.target.value)} /></td>
                          <td className="px-1 py-1"><input type="number" step="0.001" className="input-field text-xs py-1 text-right" value={li.quantity} onChange={e => updateLineItem(idx, 'quantity', e.target.value)} /></td>
                          <td className="px-1 py-1"><input type="number" step="0.01" className="input-field text-xs py-1 text-right" value={li.rate} onChange={e => updateLineItem(idx, 'rate', e.target.value)} /></td>
                          <td className="px-2 py-1 text-right font-medium">{fmt(li.amount)}</td>
                          <td className="px-1 py-1"><button type="button" onClick={() => removeLineItem(idx)} className="text-red-400 hover:text-red-600">×</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Tax */}
              <div>
                <label className="label">Tax Type</label>
                <div className="flex gap-4">
                  {(['NONE', 'CGST_SGST', 'IGST'] as TaxType[]).map(t => (
                    <label key={t} className="flex items-center gap-1 cursor-pointer">
                      <input type="radio" name="taxType" value={t} checked={fTaxType === t} onChange={() => setFTaxType(t)} />
                      <span className="text-sm">{t === 'NONE' ? 'No Tax' : t === 'CGST_SGST' ? 'CGST + SGST' : 'IGST'}</span>
                    </label>
                  ))}
                </div>
                {fTaxType === 'CGST_SGST' && (
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div><label className="label">CGST Rate %</label><input type="number" step="0.01" className="input-field" value={fCgstRate} onChange={e => setFCgstRate(e.target.value)} /></div>
                    <div><label className="label">SGST Rate %</label><input type="number" step="0.01" className="input-field" value={fSgstRate} onChange={e => setFSgstRate(e.target.value)} /></div>
                  </div>
                )}
                {fTaxType === 'IGST' && (
                  <div className="mt-2 max-w-xs"><label className="label">IGST Rate %</label><input type="number" step="0.01" className="input-field" value={fIgstRate} onChange={e => setFIgstRate(e.target.value)} /></div>
                )}
              </div>

              {/* Tax preview */}
              <div className="bg-blue-50 rounded-lg p-4 text-sm space-y-1">
                <div className="flex justify-between"><span>Taxable Amount</span><span className="font-medium">{fmt(lineItemTotal)}</span></div>
                {fTaxType === 'CGST_SGST' && <>
                  <div className="flex justify-between text-gray-600"><span>CGST @ {fCgstRate}%</span><span>{fmt(taxPreview.cgst)}</span></div>
                  <div className="flex justify-between text-gray-600"><span>SGST @ {fSgstRate}%</span><span>{fmt(taxPreview.sgst)}</span></div>
                </>}
                {fTaxType === 'IGST' && <div className="flex justify-between text-gray-600"><span>IGST @ {fIgstRate}%</span><span>{fmt(taxPreview.igst)}</span></div>}
                <div className="flex justify-between font-bold text-blue-800 border-t border-blue-200 pt-1"><span>Total Credit Amount</span><span>{fmt(taxPreview.total)}</span></div>
              </div>

              <div>
                <label className="label">Notes</label>
                <textarea className="input-field" rows={2} value={fNotes} onChange={e => setFNotes(e.target.value)} />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ── All Documents View (Invoices + Credit Notes combined) ─────────────────────

const AllDocumentsView: React.FC<{ docType: InvoiceDocType; onDocTypeChange: (v: InvoiceDocType) => void }> = ({ docType, onDocTypeChange }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [printTarget, setPrintTarget] = useState<Invoice | null>(null);
  const [sendEmailTarget, setSendEmailTarget] = useState<Invoice | null>(null);
  const [cnDetail, setCnDetail] = useState<CreditNote | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, cnRes] = await Promise.all([getInvoices(), getCreditNotes()]);
      setInvoices(invRes.data);
      setCreditNotes(cnRes.data);
    } catch { } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  type Row = { key: string; isInv: boolean; inv: Invoice | undefined; cn: CreditNote | undefined };

  const allRows: Row[] = useMemo(() => [
    ...invoices.filter(inv => inv.status !== 'CANCELLED').map(inv => ({ key: `inv-${inv.id}`, isInv: true, inv, cn: undefined })),
    ...creditNotes.filter(cn => cn.status !== 'CANCELLED').map(cn => ({ key: `cn-${cn.id}`, isInv: false, inv: undefined, cn })),
  ].sort((a, b) => {
    const da = new Date(a.isInv ? a.inv!.invoiceDate : a.cn!.creditNoteDate).getTime();
    const db = new Date(b.isInv ? b.inv!.invoiceDate : b.cn!.creditNoteDate).getTime();
    return db - da;
  }), [invoices, creditNotes]);

  const rows = useMemo(() => allRows.filter(r => {
    if (search) {
      const q = search.toLowerCase();
      if (r.isInv) {
        const inv = r.inv!;
        return inv.invoiceNumber.toLowerCase().includes(q) || (inv.client?.clientName ?? '').toLowerCase().includes(q);
      } else {
        const cn = r.cn!;
        return cn.creditNoteNumber.toLowerCase().includes(q) || cn.client.clientName.toLowerCase().includes(q) || cn.originalInvoiceNumber.toLowerCase().includes(q);
      }
    }
    return true;
  }), [allRows, search]);

  // Summary stats
  const totalInvoiced = useMemo(() => invoices.filter(inv => inv.status !== 'CANCELLED').reduce((s, inv) => s + Number(inv.totalAmount ?? inv.amount), 0), [invoices]);
  const totalCredits = useMemo(() => creditNotes.filter(cn => cn.status !== 'CANCELLED').reduce((s, cn) => s + Number(cn.totalAmount ?? cn.amount), 0), [creditNotes]);
  const totalCollected = useMemo(() => invoices.filter(inv => inv.status !== 'CANCELLED').reduce((s, inv) => s + Number(inv.paymentAmount ?? 0), 0), [invoices]);
  const totalOutstanding = useMemo(() => invoices
    .filter(inv => ['SENT', 'OVERDUE', 'PARTIALLY_PAID'].includes(inv.status))
    .reduce((s, inv) => s + outstandingOf(inv), 0), [invoices]);

  const handleExportCSV = () => {
    const headers = ['#', 'Type', 'Client', 'GSTIN', 'Sale Type', 'Task / Against', 'Date', 'Status', 'Taxable', 'Tax', 'Total', 'TDS', 'Paid', 'Outstanding'];
    const csvRows = rows.map(r => {
      if (r.isInv) {
        const inv = r.inv!;
        const taxAmt = Number(inv.totalAmount ?? inv.amount) - Number(inv.amount);
        const outstanding = ['SENT', 'OVERDUE', 'PARTIALLY_PAID'].includes(inv.status) ? outstandingOf(inv) : 0;
        return [inv.invoiceNumber, 'INV', inv.client?.clientName ?? '', inv.clientGstin?.gstin ?? '', saleTypeLabel(inv.clientGstin?.gstType, inv.taxType), inv.task?.taskName ?? '', fmtD(inv.invoiceDate), inv.status,
          inv.amount, taxAmt, inv.totalAmount ?? inv.amount, inv.tdsAmount ?? 0, inv.paymentAmount ?? 0, outstanding];
      } else {
        const cn = r.cn!;
        const taxAmt = Number(cn.cgstAmount ?? 0) + Number(cn.sgstAmount ?? 0) + Number(cn.igstAmount ?? 0);
        return [cn.creditNoteNumber, 'CN', cn.client.clientName, cn.clientGstin?.gstin ?? '', saleTypeLabel(cn.clientGstin?.gstType, cn.taxType), cn.originalInvoiceNumber, fmtD(cn.creditNoteDate), cn.status,
          cn.amount, taxAmt, cn.totalAmount ?? cn.amount, '', '', ''];
      }
    });
    const csv = [headers, ...csvRows].map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'all-documents.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard label="Total Invoiced" value={fmt(totalInvoiced)} />
        <SummaryCard label="Total Credits (CN)" value={`−${fmt(totalCredits)}`} color="text-red-600" />
        <SummaryCard label="Collected" value={fmt(totalCollected)} color="text-green-700" />
        <SummaryCard label="Outstanding" value={fmt(totalOutstanding)} color="text-orange-600" />
      </div>

      {/* Filters + actions */}
      <div className="flex flex-wrap items-center gap-3">
        <input className="input-field max-w-[220px]" placeholder="Search #, client…" value={search} onChange={e => setSearch(e.target.value)} />
        <DocTypeSelect value={docType} onChange={onDocTypeChange} />
        <button onClick={handleExportCSV} className="btn-secondary text-sm ml-auto">↓ Export CSV</button>
      </div>

      <div className="card overflow-x-auto p-0">
        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-gray-400">No documents found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">#</th>
                <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">Client</th>
                <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">Task / Against</th>
                <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Taxable</th>
                <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Tax</th>
                <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Total</th>
                <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Date</th>
                <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">Status</th>
                <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">TDS</th>
                <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Paid</th>
                <th className="bg-gray-50 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Outstanding</th>
                <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Type</th>
                <th className="bg-gray-50 px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                if (r.isInv) {
                  const inv = r.inv!;
                  const taxAmt = Number(inv.totalAmount ?? inv.amount) - Number(inv.amount);
                  // Same derived-multi-task logic as the Invoices tab — trust
                  // the actual line items rather than the stored isMultiTask flag.
                  const liArr = ((inv as any).lineItems ?? []) as any[];
                  const liTaskIds = Array.from(new Set(
                    liArr.filter(li => !li.isOpe && li.taskId).map(li => li.taskId as number)
                  ));
                  const showAsMulti = liTaskIds.length > 1;
                  return (
                    <tr key={r.key} className={`border-b border-gray-50 hover:bg-gray-50 ${inv.status === 'CANCELLED' ? 'line-through text-gray-400' : ''}`}>
                      <td className="px-2 py-2 font-mono font-medium text-blue-700 border-b border-gray-100 whitespace-nowrap">{inv.invoiceNumber}</td>
                      <td className="px-2 py-2 text-gray-700 border-b border-gray-100 max-w-[140px] truncate" title={inv.serviceClient ? `${inv.client?.clientName || ''} — for ${inv.serviceClient.clientName}` : inv.client?.clientName}>
                        {inv.client?.clientName ?? '—'}
                        {inv.serviceClient && <div className="text-[10px] text-indigo-600 truncate">for {inv.serviceClient.clientName}</div>}
                      </td>
                      <td className="px-2 py-2 text-gray-700 border-b border-gray-100 max-w-[160px]">
                        {showAsMulti ? (
                          <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded">Multi-task</span>
                        ) : inv.task ? (
                          <div className="truncate" title={`${inv.task.taskId} — ${inv.task.taskName}`}>
                            <span className="text-xs text-gray-400">{inv.task.taskId}</span> <span className="text-xs">{inv.task.taskName}</span>
                          </div>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-2 py-2 text-right border-b border-gray-100 whitespace-nowrap">{fmt(Number(inv.amount))}</td>
                      <td className="px-2 py-2 text-right border-b border-gray-100 whitespace-nowrap">
                        {taxAmt > 0 ? <span>{fmt(taxAmt)}</span> : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold border-b border-gray-100 whitespace-nowrap">{fmt(Number(inv.totalAmount ?? inv.amount))}</td>
                      <td className="px-2 py-2 text-gray-600 border-b border-gray-100 whitespace-nowrap">{fmtD(inv.invoiceDate)}</td>
                      <td className="px-2 py-2 border-b border-gray-100"><StatusBadge status={inv.status} /></td>
                      <td className="px-2 py-2 text-right border-b border-gray-100 whitespace-nowrap">
                        {(inv.tdsAmount ?? 0) > 0 ? <span className="text-orange-600 font-medium">{fmt(inv.tdsAmount!)}</span> : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-2 py-2 text-right border-b border-gray-100 whitespace-nowrap">
                        {(inv.paymentAmount ?? 0) > 0 ? <span className="text-green-600 font-medium">{fmt(inv.paymentAmount!)}</span> : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-2 py-2 text-right border-b border-gray-100 whitespace-nowrap">
                        {['SENT', 'OVERDUE', 'PARTIALLY_PAID'].includes(inv.status) ? (() => {
                          const o = outstandingOf(inv);
                          return <span className={o > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>{fmt(o)}</span>;
                        })() : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-2 py-2 border-b border-gray-100">
                        <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">INV</span>
                      </td>
                      <td className="px-2 py-2 border-b border-gray-100 whitespace-nowrap">
                        <div className="flex items-center gap-0.5">
                          <button onClick={() => setPrintTarget(inv)} title="Preview / Print" className="p-1 rounded hover:bg-gray-100 text-gray-600">🖨</button>
                          <button onClick={() => { generateInvoicePDF(inv, inv.template ?? 1).catch(() => alert('PDF failed.')); }} title="Download PDF" className="p-1 rounded hover:bg-green-50 text-green-600">📥</button>
                          <button onClick={() => setSendEmailTarget(inv)} title="Send to client by email" className="p-1 rounded hover:bg-indigo-50 text-indigo-600">📧</button>
                        </div>
                      </td>
                    </tr>
                  );
                } else {
                  const cn = r.cn!;
                  const taxAmt = Number(cn.cgstAmount ?? 0) + Number(cn.sgstAmount ?? 0) + Number(cn.igstAmount ?? 0);
                  const total = Number(cn.totalAmount ?? cn.amount);
                  return (
                    <tr key={r.key} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-2 py-2 font-mono font-medium text-purple-700 border-b border-gray-100 whitespace-nowrap">
                        <button onClick={() => setCnDetail(cn)} className="hover:underline">{cn.creditNoteNumber}</button>
                      </td>
                      <td className="px-2 py-2 text-gray-700 border-b border-gray-100 max-w-[140px] truncate" title={cn.client.clientName}>{cn.client.clientName}</td>
                      <td className="px-2 py-2 border-b border-gray-100 max-w-[160px]">
                        <div className="truncate text-xs text-gray-500" title={cn.originalInvoiceNumber}>{cn.originalInvoiceNumber}</div>
                        <div className="text-xs text-gray-400">{CN_REASON_LABEL[cn.reason]}</div>
                      </td>
                      <td className="px-2 py-2 text-right border-b border-gray-100 whitespace-nowrap text-red-600 font-medium">−{fmt(Number(cn.amount))}</td>
                      <td className="px-2 py-2 text-right border-b border-gray-100 whitespace-nowrap">
                        {taxAmt > 0 ? <span className="text-red-600">−{fmt(taxAmt)}</span> : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold border-b border-gray-100 whitespace-nowrap text-red-600">−{fmt(total)}</td>
                      <td className="px-2 py-2 text-gray-600 border-b border-gray-100 whitespace-nowrap">{fmtD(cn.creditNoteDate)}</td>
                      <td className="px-2 py-2 border-b border-gray-100">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${CN_STATUS_CLASS[cn.status]}`}>{CN_STATUS_LABEL[cn.status]}</span>
                      </td>
                      <td className="px-2 py-2 text-right border-b border-gray-100"><span className="text-gray-300">—</span></td>
                      <td className="px-2 py-2 text-right border-b border-gray-100"><span className="text-gray-300">—</span></td>
                      <td className="px-2 py-2 text-right border-b border-gray-100"><span className="text-gray-300">—</span></td>
                      <td className="px-2 py-2 border-b border-gray-100">
                        <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700">CN</span>
                      </td>
                      <td className="px-2 py-2 border-b border-gray-100 whitespace-nowrap">
                        <div className="flex items-center gap-0.5">
                          <button onClick={() => setCnDetail(cn)} title="View details" className="p-1 rounded hover:bg-gray-100 text-gray-600">🔍</button>
                          <button onClick={() => { generateCreditNotePDF(cn).catch(() => alert('PDF failed.')); }} title="Download PDF" className="p-1 rounded hover:bg-green-50 text-green-600">📥</button>
                        </div>
                      </td>
                    </tr>
                  );
                }
              })}
            </tbody>
          </table>
        )}
      </div>

      {printTarget && <GSTPreviewModal invoice={printTarget} onClose={() => setPrintTarget(null)} />}
      {sendEmailTarget && <SendInvoiceEmailModal invoice={sendEmailTarget} onClose={() => setSendEmailTarget(null)} onSent={() => { setSendEmailTarget(null); load(); }} />}

      {cnDetail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="text-lg font-semibold">{cnDetail.creditNoteNumber}</h2>
                <p className="text-xs text-gray-400">{fmtD(cnDetail.creditNoteDate)}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { generateCreditNotePDF(cnDetail).catch(() => alert('PDF failed.')); }} className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3 py-1.5 rounded transition-colors">📥 PDF</button>
                <button onClick={() => setCnDetail(null)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
              </div>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="text-gray-500">Client</span><p className="font-medium">{cnDetail.client.clientName}</p></div>
                <div><span className="text-gray-500">Status</span><p><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CN_STATUS_CLASS[cnDetail.status]}`}>{cnDetail.status}</span></p></div>
                <div><span className="text-gray-500">Original Invoice #</span><p className="font-medium">{cnDetail.originalInvoiceNumber}</p></div>
                <div><span className="text-gray-500">Original Invoice Date</span><p>{fmtD(cnDetail.originalInvoiceDate)}</p></div>
                {cnDetail.billingEntity && <div><span className="text-gray-500">Billing Entity</span><p>{cnDetail.billingEntity.name}</p></div>}
                <div><span className="text-gray-500">Reason</span><p>{CN_REASON_LABEL[cnDetail.reason]}</p></div>
              </div>
              {cnDetail.notes && <div className="bg-gray-50 rounded-lg p-3"><span className="text-gray-500">Notes: </span>{cnDetail.notes}</div>}
              {cnDetail.lineItems.length > 0 && (
                <div>
                  <p className="font-medium mb-2 text-gray-700">Line Items</p>
                  <table className="w-full text-xs border border-gray-200 rounded">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1.5 text-left">#</th>
                        <th className="px-2 py-1.5 text-left">Description</th>
                        <th className="px-2 py-1.5 text-right">Qty</th>
                        <th className="px-2 py-1.5 text-right">Rate</th>
                        <th className="px-2 py-1.5 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cnDetail.lineItems.map(li => (
                        <tr key={li.id} className="border-t border-gray-100">
                          <td className="px-2 py-1.5">{li.slNo}</td>
                          <td className="px-2 py-1.5">{li.description}{li.hsnSac && <span className="text-gray-400 ml-1">({li.hsnSac})</span>}</td>
                          <td className="px-2 py-1.5 text-right">{li.quantity}</td>
                          <td className="px-2 py-1.5 text-right">{fmt(li.rate)}</td>
                          <td className="px-2 py-1.5 text-right">{fmt(li.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="bg-blue-50 rounded-lg p-4 space-y-1">
                <div className="flex justify-between"><span>Taxable Amount</span><span className="font-medium">{fmt(cnDetail.amount)}</span></div>
                {cnDetail.taxType === 'CGST_SGST' && <>
                  <div className="flex justify-between text-gray-600"><span>CGST @ {cnDetail.cgstRate}%</span><span>{fmt(cnDetail.cgstAmount ?? 0)}</span></div>
                  <div className="flex justify-between text-gray-600"><span>SGST @ {cnDetail.sgstRate}%</span><span>{fmt(cnDetail.sgstAmount ?? 0)}</span></div>
                </>}
                {cnDetail.taxType === 'IGST' && (
                  <div className="flex justify-between text-gray-600"><span>IGST @ {cnDetail.igstRate}%</span><span>{fmt(cnDetail.igstAmount ?? 0)}</span></div>
                )}
                <div className="flex justify-between font-bold text-blue-800 border-t border-blue-200 pt-1">
                  <span>Total Credit Amount</span><span>{fmt(cnDetail.totalAmount ?? cnDetail.amount)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const Invoices: React.FC = () => {
  const { isAdmin, isHR, isPartner } = useAuth();
  const location = useLocation();
  // Everything except the Invoices tab shows firm-wide money, so it is limited
  // to billing managers. A plain staff member gets the Invoices tab alone, and
  // the server scopes that to invoices they raised themselves — see
  // accessScope.invoiceRoleWhere. The server enforces all of this
  // independently; hiding here just keeps the UI honest.
  const canSeeBilling = useCanSeeBilling();
  type InvoiceTabId = 'invoices' | 'retainers' | 'reconciliation' | 'receivables' | 'tds' | 'profit-centres';
  // Honour ?tab= URL param (e.g. /invoices?tab=reconciliation from the old
  // sidebar redirect) so deep links keep working after the move.
  const initialTab: InvoiceTabId = (() => {
    const t = new URLSearchParams(location.search).get('tab');
    const known = (['invoices', 'retainers', 'reconciliation', 'receivables', 'tds', 'profit-centres'] as const).includes(t as any);
    if (!known) return 'invoices';
    // A deep link to a billing tab must not strand a staff member on a tab
    // that will only ever render 403s.
    return canSeeBilling ? (t as InvoiceTabId) : 'invoices';
  })();
  const [activeTab, setActiveTab] = useState<InvoiceTabId>(initialTab);
  const [docType, setDocType] = useState<InvoiceDocType>('invoices');
  const [cnDefaultInvoice, setCnDefaultInvoice] = useState<Invoice | null>(null);
  const [cnDefaultOb, setCnDefaultOb] = useState<{ id: number; invoiceNumber: string; invoiceDate: string; clientId: number; billingEntityId?: number | null; amount: number } | null>(null);
  const [tasks, setTasks] = useState<TaskInfo[]>([]);
  const [billingEntities, setBillingEntities] = useState<BillingEntityFull[]>([]);
  const [allClients, setAllClients] = useState<Array<{ id: number; clientCode: string; clientName: string }>>([]);
  const [loading, setLoading] = useState(true);

  // Check if opened from task "Create Invoice" button
  const params = new URLSearchParams(location.search);
  const defaultTaskId = params.get('taskId') ? Number(params.get('taskId')) : undefined;
  const defaultCertificateId = params.get('certificateId') ? Number(params.get('certificateId')) : undefined;
  // Multi-certificate bootstrap from Certificates page: ?certificateIds=1,2,3
  const defaultCertificateIds = (params.get('certificateIds') || '')
    .split(',').map(s => s.trim()).filter(Boolean).map(Number).filter(n => !isNaN(n));
  // Litigation bootstrap from the Litigations panel: ?litigationId=X
  // (parent-level "final settlement" invoice) or ?litigationStepId=Y
  // (1:1 step-level invoice).
  const defaultLitigationId     = params.get('litigationId')     ? Number(params.get('litigationId'))     : undefined;
  const defaultLitigationStepId = params.get('litigationStepId') ? Number(params.get('litigationStepId')) : undefined;

  useEffect(() => {
    Promise.all([getTasks(), getBillingEntities(), getClients()]).then(([tRes, beRes, cRes]) => {
      setTasks(tRes.data.filter((t: TaskInfo) => t.status === 'OPEN' || t.status === 'CLOSED'));
      setBillingEntities(beRes.data);
      setAllClients(cRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleCreateCNFromInvoice = useCallback((inv: Invoice) => {
    setCnDefaultInvoice(inv);
    setCnDefaultOb(null);
    setDocType('credit-notes');
    setActiveTab('invoices');
  }, []);

  const handleCreateCNFromOb = useCallback((ob: { id: number; invoiceNumber: string; invoiceDate: string; clientId: number; billingEntityId?: number | null; amount: number }) => {
    setCnDefaultOb(ob);
    setCnDefaultInvoice(null);
    setDocType('credit-notes');
    setActiveTab('invoices');
  }, []);

  const tabs = [
    { id: 'invoices' as const, label: '🧾 Invoices' },
    ...(canSeeBilling ? [
      { id: 'retainers' as const, label: '🔁 Retainers' },
      // Reconciliation is the firm's raw bank feed — nothing on it can be
      // narrowed to a partner's profit centres, so it stays Admin/HR (the
      // server enforces the same via requireHR on /reconciliation).
      ...(isAdmin || isHR ? [{ id: 'reconciliation' as const, label: '🏦 Reconciliation' }] : []),
      { id: 'receivables' as const, label: '📊 Receivables' },
      { id: 'tds' as const, label: '🏷 TDS Tracker' },
      { id: 'profit-centres' as const, label: '🏦 Profit Centres' },
    ] : []),
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Invoicing</h1>
      </div>

      <div className="flex border-b border-gray-200">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'profit-centres' ? (
        <ProfitCentreAccess />
      ) : activeTab === 'tds' ? (
        <TdsTrackerTab isAdmin={isAdmin || isHR || isPartner} />
      ) : activeTab === 'receivables' ? (
        <ReceivablesTab onCreateCNFromInvoice={handleCreateCNFromInvoice} onCreateCNFromOb={handleCreateCNFromOb} />
      ) : activeTab === 'reconciliation' ? (
        <Reconciliation />
      ) : activeTab === 'retainers' ? (
        <Retainers />
      ) : (
        <>
          {/* Document type (All / Invoices / Credit Notes) is picked from the
              dropdown each view renders in its filter row. */}
          {docType === 'all' && canSeeBilling && <AllDocumentsView docType={docType} onDocTypeChange={setDocType} />}
          {docType === 'credit-notes' && canSeeBilling && <CreditNotesTab clients={allClients} billingEntities={billingEntities} isAdmin={isAdmin} isPartner={isPartner} defaultInvoice={cnDefaultInvoice} defaultOb={cnDefaultOb} onDefaultConsumed={() => { setCnDefaultInvoice(null); setCnDefaultOb(null); }} docType={docType} onDocTypeChange={setDocType} />}
          {(docType === 'invoices' || !canSeeBilling) && (loading
            ? <div className="text-center py-20 text-gray-400">Loading…</div>
            : <InvoicesTab tasks={tasks} billingEntities={billingEntities} isAdmin={isAdmin} isHR={isHR} isPartner={isPartner} defaultTaskId={defaultTaskId} defaultCertificateId={defaultCertificateId} defaultCertificateIds={defaultCertificateIds.length >= 1 ? defaultCertificateIds : undefined} defaultLitigationId={defaultLitigationId} defaultLitigationStepId={defaultLitigationStepId} onCreateCN={handleCreateCNFromInvoice} docType={docType} onDocTypeChange={setDocType} />
          )}
        </>
      )}
    </div>
  );
};

export default Invoices;
